import * as THREE from "three/webgpu"
import { disposeWebGpuBackend } from "./webgpu-backend-disposal"
import { invalidFrameEnvelope } from "./frame-validation"
import { ParticleVisibilityQueries, type ParticleVisibilitySample } from "./particle-visibility"
import type { SpriteCardInput } from "./sprite-card"
import { ParticleMaterialGraphs, bindParticleTexture } from "./particle-material-graphs"
import { SourceParticleDepth } from "./particle-depth"
import { CombatDecals,type CombatDecalInput } from "./combat-decals"
import { ProjectedMarkMaterials } from "./projected-mark-materials"
import { createWorldClipGroup, prepareWorldViewPipelines } from "./world-pipeline-preparation"
export type { SpriteCardInput } from "./sprite-card"
import * as TSL from "three/tsl"
import {
  ExposureController,
  SOURCE_LDR,
  SOURCE_PC_INTEGER_HDR,
  sourceShaderGammaToLinear,
  validateRenderConfiguration,
  type CanvasAlphaMode,
  type CanvasFormat,
  type ExposureConfiguration,
  type ExposureSnapshot,
  type OutputColorSpace,
  type RenderConfiguration,
  type ToneOperator,
} from "./color-output"
import { applyParticleDepthState, sourceMaterialUsesFog, worldMaterialSide } from "./material-state"
import { borrowWorldLightmapTextures, createWorldLightmapTextures, replaceWorldLightmapData } from "./world-lightmap-textures"
import { retainedSceneSource } from "./scene-resource-handoff"
import { projectedDecalDepthBias, projectedDecalReceiverIsValid } from "./decal-occlusion"
import { OwnedResourceGeneration } from "./resource-generation"
import { SharedTextureResidency } from "./texture-residency"
import { particleTextureAlias } from "./particle-texture-alias"
import { sourceTextureSamples } from "./texture-samples"
import { sourceTextureLayout,authoredMipUpload } from "./source-texture-layout"
import { FramePacingController, type FramePacingRecord } from "./frame-pacing"
import { browserFrameProfiler, installNodeBuilderInstrumentation, observeStaticPropUse, RendererFrameInstrumentation, type RendererFrameProfile } from "./frame-instrumentation"
import { RenderOwnerProbe, RENDER_OWNER_PLAN } from "./render-owner-probe"
export { browserFrameProfiler, type BrowserFrameProfiler, type RendererFrameProfile, type RendererMemoryProfile, type RendererPassProfile } from "./frame-instrumentation"
import { fillParticleBatchRanges, type MutableParticleBatchRange } from "./particle-batches"
import { particlePipelineKey, particlePipelineVariant, particlePreparationSides } from "./particle-pipeline"
import { createParticleQuadWriter, writeParticleQuadIndices } from "./particle-geometry"
import { createParticleAttributeUpdates, resetParticleAttributeUpdates, writeParticleAppearance, writeParticleCenters } from "./particle-attributes"
import { installOrderedWebGpuBundles, type OrderedBundleBackend } from "./ordered-webgpu-bundles"
import { PersistentWorldDraws } from "./persistent-world-draws"
import { applyMapModelTransform, applyMapModelRenderBounds } from "./map-model-transform"
import { WebGpuFramePresentation, type FramePresentationBackend } from "./webgpu-frame-presentation"
import { WebGpuUploadBatch, type UploadBatchBackend } from "./webgpu-upload-batch"
import { requestCoreWebGpuDevice } from "./webgpu-core-device"
import { prepareReachablePipelineVisibility } from "./reachable-pipeline-visibility"
import { LegacyVisuals,legacyFog, type LegacyVisualFrame, type PixelVisibilityFeedback,type LegacyVisualProgram,type LegacyVisualFrameSet,type LegacyViewKind } from "./legacy-visuals"
export type { LegacyVisualFrame, PixelVisibilityFeedback,LegacyVisualProgram,LegacyVisualFrameSet,LegacyViewKind } from "./legacy-visuals"
import { withBoundedPipelineCompilation } from "./bounded-pipeline-compilation"
import { installWebGpuBufferNames, type BufferNamingBackend } from "./webgpu-buffer-names"
import { RetainedLeafVisibility, RetainedVisibilityError, RetainedWorldVisibility } from "./retained-visibility"
import { RetainedStaticSceneGroup } from "./static-scene-group"
import { RetainedModelCache } from "./retained-model-cache"
import { installRenderObjectLifetime } from "./render-object-lifetime"
import { installGeometryAttributeLifetime } from "./geometry-attribute-lifetime"
import { installTextureBindingLifetime } from "./texture-binding-lifetime"
import { disposeDynamicModel } from "./dynamic-model-disposal"
import { ModelLightingGraphs, bindModelLighting, bindModelTexture, bindStaticPropFade, modelEnvironmentShape, transferModelBindings } from "./model-lighting-graphs"
import { modelMaterialGraph, swizzleModelTexture } from "./model-material-graphs"
import { sourceFragmentColor } from "./source-fragment-color"
import { createStaticPropBatch, MAX_STATIC_PROPS_PER_BATCH, type StaticPropBatch } from "./static-prop-batches"
import { createStaticPropFadeVariant, selectStaticPropFadePass, type StaticPropFadeBinding, distanceFadeOpacity, quantizeStaticPropOpacity, screenFadeOpacity } from "./static-prop-fade"
import { executeViewModelDepthPhase } from "./viewmodel-depth-phase"
import { selectDiagnosticModelBase } from "./diagnostic-model"
import { sourceModelPanelPresentation, withSourceModelPanelTargetViewport } from "./model-panel"
import { bindSourceModelMesh, createSourceModelSkeleton, updateSourceModelSkeleton } from "./source-model-skinning"
import { modelNormalEvidence, visibleModelIntersection } from "./model-normal-evidence"
import { resizeSampledRenderTargets } from "./render-target-resize"
import { modelIntersectsViewFrustum } from "./model-visibility"
import { sourceHorizontal4By3FovToVertical, sourceViewportDepthRange } from "./source-camera"
import {
  validateDynamicLights,
  validateShadows,
  type DynamicLightInput,
  type ShadowInput,
} from "./frame-foundations"
import {
  prepareModelDrawInputs,
  validateModelEyeStates,
  validateModelLightingInput,
  type ModelDrawRequirement,
  type ModelEyeState,
  type ModelLightingInput,
} from "./model-lighting"
import { buildSourceSkyGeometry } from "./source-environment"
import { createSourceLightmappedEnvironmentNode, type SourceLightmappedEnvironmentNode } from "./source-lightmapped"
import { SourceExposureSampler } from "./source-exposure"
import {
  createSourceModelLightingUniforms,
  createSourceModelEyeUniforms,
  sourceStaticVertexLightingNode,
  sourceModelWorldNormal,
  updateSourceModelLightingUniforms,
  updateSourceModelEyeUniforms,
  type SourceModelEyeUniforms,
  type SourceModelLightingUniforms,
  type SourceModelPhongState,
} from "./source-model-lighting"
import {
  createSourceViewFogUniforms,
  createSourceWaterFogUniforms,
  createSourceWaterMaterial,
  sourceViewFogNode,
  type SourceWaterFogUniforms,
  type SourceWaterShaderState,
} from "./source-water"
import { sourceWaterTangentAttributes } from "./source-water-geometry"
import { createSourceRefractMaterial } from "./source-refract"
import { SourceHudMaterials, type HudMaterialFrame } from "./source-hud-materials"
export type { HudMaterial, HudTexture, HudMaterialDraw, HudMaterialFrame } from "./source-hud-materials"
import { SourceCloakFramebuffer, SourceModelCloak, sourceCloakTransparentSort, type SourceCloakState, type SourceCloakBinding } from "./source-cloak"
import { prepareWaterPipelineVisibility } from "./water-pipeline-visibility"
import { installRenderService } from "./render-service"
import {
  buildRuntimeLightmap,
  parseRuntimeMap,
  parseRuntimeMapVerified,
  type ProfileRequirement,
  type RuntimeBatch,
  type RuntimeLightmap,
  type RuntimeMap,
  type RuntimeMaterial,
} from "./runtime-map"

export {
  ExposureController,
  SOURCE_EXPOSURE,
  SOURCE_LDR,
  SOURCE_PC_INTEGER_HDR,
  applyColorOutput,
  buildLuminanceHistogram,
  exposureTarget,
  histogramBoundary,
  linearToSrgb,
  sourceShaderGammaToLinear,
  validateRenderConfiguration,
  type CanvasAlphaMode,
  type CanvasFormat,
  type ExposureConfiguration,
  type ExposureSnapshot,
  type OutputColorSpace,
  type RenderConfiguration,
  type ToneOperator,
} from "./color-output"
export {
  LightingInputError,
  SOURCE_BUMP_BASIS,
  directionalWeights,
  evaluateDirectionalLightmap,
  prepareWorldLights,
  sampleAmbientCube,
  type AmbientCubeRequest,
  type DirectionalLightingInput,
  type PreparedWorldLight,
  type WorldLightRequest,
} from "./lighting"
export { sourceHorizontal4By3FovToVertical, sourceViewportDepthRange } from "./source-camera"
export {
  ModelLightingError,
  ambientCubeLuminance,
  dilateIrisUv,
  evaluateAmbientCube,
  evaluateLocalLight,
  evaluateModelLighting,
  intersectEyeSphere,
  projectEyeCoordinate,
  prepareModelDrawInputs,
  validateModelEyeStates,
  validateModelLightingInput,
  type ModelEyeState,
  type ModelDrawRequirement,
  type ModelLightingInput,
  type ModelLocalLight,
  type SourceVector3,
  type SourceVector4,
} from "./model-lighting"
export {
  SourceEnvironmentError,
  buildSourceSkyGeometry,
  decodeRgbsBilinear,
  evaluateCheapWater,
  evaluateExpensiveWater,
  evaluateFogColor,
  linearFogFactor,
  sampleAuthoredCubemap,
  selectNearestCubemap,
  sourceCubemapCoordinate,
  sourceSkyFaceVisible,
  waterBlurOffsets,
  type AuthoredCubemap,
  type CheapWaterRequest,
  type CubemapSample,
  type ExpensiveWaterRequest,
  type FogState,
  type SkyFace,
  type SkyFaceGeometry,
} from "./source-environment"
export {
  SourceWaterError,
  createSourceWaterFogUniforms,
  createSourceWaterMaterial,
  evaluateSourceWaterPixel,
  sourceWaterFogAlpha,
  sourceWaterProjectiveCoordinates,
  type SourceWaterFogAlphaRequest,
  type SourceWaterFogUniforms,
  type SourceWaterGpuInput,
  type SourceWaterGpuMaterial,
  type SourceWaterPixel,
  type SourceWaterPixelRequest,
  type SourceWaterPixelResult,
  type SourceWaterSampler,
  type SourceWaterShaderState,
} from "./source-water"
export {
  sourceWaterTangentAttributes,
  type SourceWaterGeometryInput,
  type SourceWaterTangentAttributes,
} from "./source-water-geometry"
export {
  FRAME_PHASES,
  FrameContractError,
  SourceViewStack,
  executeFrameGraph,
  interpolatePresentation,
  orderTransparentItems,
  validateDynamicLights,
  validateShadows,
  validateViewState,
  visibleTriangleIndices,
  type ClipPlane,
  type DepthStencilState,
  type DynamicLightInput,
  type FrameGraphResult,
  type FramePhase,
  type InterpolationField,
  type InterpolationPolicy,
  type InterpolationRequest,
  type ShadowInput,
  type TransparentItem,
  type TransparentOperation,
  type ViewState,
} from "./frame-foundations"
export { FramePacingController, FramePacingError, type FramePacingRecord } from "./frame-pacing"
export { selectDiagnosticModelBase, type DiagnosticModelBaseDisposition } from "./diagnostic-model"
export { AtomicResourceError, AtomicResourceSlot, type ReplaceableResource } from "./atomic-resources"
export {
  DebugCaptureError,
  compareDebugPlanes,
  createDebugCapture,
  validateAlignedCaptureManifest,
  type AlignedCaptureManifest,
  type DebugCapture,
  type DebugPlane,
  type DebugPlaneInput,
  type DebugPlaneKind,
  type PlaneComparison,
  type PlaneComparisonResult,
} from "./debug-capture"
export {
  RuntimeMapError,
  buildRuntimeLightmap,
  parseRuntimeMap,
  parseRuntimeMapVerified,
  validateRuntimeMapHashes,
  type HdrProfile,
  type LightingMember,
  type ProfileRequirement,
  type Rgb,
  type RuntimeAmbientIndex,
  type RuntimeAmbientSample,
  type RuntimeInput,
  type RuntimeLightmap,
  type RuntimeMap,
  type RuntimeProfileMaterial,
  type RuntimeWorldLight,
  type SurfaceLighting,
  type SurfaceLightingKind,
} from "./runtime-map"

// Native requests can expand into player, carried-item and wearable poses. This
// bounded rendered-variant cache is distinct from the 128-request wire bound.
const MAX_PREPARED_MODEL_VARIANTS = 192
const MAX_EFFECTS = 4_096
const MAX_DIMENSION = 8_192
const HASH = /^[0-9a-f]{64}$/
const SOURCE_MODEL_BIND_GEOMETRY = new WeakMap<THREE.BufferGeometry, Readonly<{ geometry: THREE.BufferGeometry; palette: Uint16Array }>>()
const SOURCE_MODEL_BASE_COLOR = new WeakMap<THREE.BufferGeometry, any>()
const SOURCE_MODEL_BASE_TEXTURE = new WeakMap<object, Readonly<{ texture: THREE.Texture; input: AuthoredTextureInput }>>()
const swizzle = (sample: any, input: AuthoredTextureInput) => swizzleModelTexture(sample, input.sourceFormat)

type Canvas = HTMLCanvasElement | OffscreenCanvas

export type Camera = Readonly<{
  position: readonly [number, number, number]
  yawDegrees: number
  pitchDegrees: number
  verticalFovDegrees: number
  near: number
  far: number
}>

export type Effect = DynamicLightInput

export type ModelItem = Readonly<{
  identity: number
  model: string
  position: readonly [number, number, number]
  angles?: readonly [number, number, number]
  orientation?: readonly [number, number, number, number]
  scale: number
  skin?: number
  body?: number
  viewModel?: boolean
  pose?: Readonly<{
    cloak?: SourceCloakBinding | null
    viewmodel?: null | Readonly<{ frontFace: "clockwise" | "counter-clockwise"; cullFace: "back"; reflected: boolean; drawDisposition: "draw" | "suppressed-success" | "suppressed" }>
    boneMatrices: Float32Array
    flex?: readonly Readonly<{ primitive: number; indices: Uint32Array; positions: Float32Array; normals: Float32Array }>[]
    primitives: readonly Readonly<{
      primitive: number
      material: number
      vertexCount: number
      translucent:boolean
      visible?:boolean
    }>[]
  }>
  renderBounds?: readonly [readonly [number, number, number], readonly [number, number, number]]
  viewModelProjection?: Readonly<{
    kind: "viewmodel"
    horizontalFov4By3: number
    near: number
    depthRange: readonly [number, number]
    drawsAfterWorld: boolean
    opaqueBeforeTranslucent: boolean
    optionalViewSpaceYReflection: boolean
  }>
  modelLighting?: ModelLightingInput
  eyeStates?: readonly ModelEyeState[]
  currentFramebufferAvailable?: boolean
  gameProxyValuesAvailable?: boolean
}>

export type ParticleItem = Readonly<{
  sky: boolean
  identity: number
  primitive: "sprite" | "trail" | "rope"
  visibility?: Readonly<{ identity: bigint; vertices: Float32Array; clipFraction: number }>
  mesh?: Readonly<{ positions: Float32Array; uv: Float32Array; colors: Uint8Array; indices: Uint32Array }>
  material: string
  position: readonly [number, number, number]
  previousPosition: readonly [number, number, number]
  trailEndPosition:readonly[number,number,number]
  radius: number
  rollRadians: number
  yawRadians:number
  color: number
  opacity: number
  trailLength: number
  trailWidth:number
  trailLengthScale:number
  ageSeconds: number
  trailMinLength: number
  trailMaxLength: number
  trailFadeInSeconds: number
  orientationType: number
  materialShader:"sprite-card"|"mesh-sprite"
  textureColorSpace:"srgb-texture-linear-tint"
  blendSource:"zero"|"one"|"source-alpha"|"one-minus-source-alpha"
  blendDestination:"zero"|"one"|"source-alpha"|"one-minus-source-alpha"
  stableTieIdentity:bigint
  primarySheet: Readonly<{
    current: readonly (readonly [number, number, number, number])[]
    next: readonly (readonly [number, number, number, number])[]
    blend: number
  }> | null
  secondarySheet:Readonly<{current:readonly(readonly[number,number,number,number])[];next:readonly(readonly[number,number,number,number])[];blend:number}>|null
}>

export type FrameCaptureRequest = Readonly<{ format: "image/png"; beforeHud?: boolean }>
export type WaterFramePass = Readonly<{
  kind: "reflection" | "refraction" | "main" | "intersection"
  origin: readonly [number, number, number]
  angles: readonly [number, number, number]
  renderAboveWater: boolean
  renderUnderWater: boolean
  renderWaterSurface: boolean
  drawEntities: boolean
  drawSky2d: boolean
  clip: null | Readonly<{ height: number; keep: "above" | "below" }>
  forcedVisibilityLeaf: number | null
  fog: Readonly<{ kind: "world" }> | Readonly<{ kind: "water"; volume: number; heightFog: boolean }>
  surfaces: Uint32Array
}>
export type WaterFramePlan = Readonly<{
  visibleWater: null | Readonly<{
    volume: number
    visibleLeaf: number
    eyeLeaf: number
    eyeInVolume: boolean
    surfaceZ: number
    distanceToWater: number | null
    material: string
    translucent: boolean
    evaluated: null | Readonly<{ normalFrame: number; normalTransform: Float32Array; cheapStart: number; cheapEnd: number }>
    overlay: null | Readonly<{ identity: string; normalFrame: number; normalTransform: Float32Array }>
  }>
  render: Readonly<{ cheap: boolean; reflect: boolean; refract: boolean; reflectEntities: boolean; drawSurface: boolean; opaque: boolean }>
  nearPlaneIntersects: boolean
  passes: readonly WaterFramePass[]
}>
export type FogInput=Readonly<{enabled:boolean;radial:boolean;primary:readonly[number,number,number,number];start:number;end:number;maximumDensity:number;farZ:number|null}>
export type EvaluatedWorldMaterialInput = Readonly<{
  identity: string
  mapMaterial: number
  textures: readonly Readonly<{ role: number; frame: number | null; transform: Float32Array | null }>[]
}>
export type ScreenOverlayFrame = Readonly<{ identity: string; normalFrame: number; normalTransform: Float32Array; refractTint: readonly [number, number, number] }>
export type VisibilityFrame=Readonly<{worldIdentity:string;cacheIdentity:string;outsideWorld:boolean;sky:0|1|2;eyeLeaf:number|null;leaves:readonly number[];areas:readonly number[];surfaces:Uint32Array;water:WaterFramePlan;worldMaterials:readonly EvaluatedWorldMaterialInput[];screenOverlay?:ScreenOverlayFrame|null}>

export type Frame = Readonly<{
  /** Accepted Source client-render clock; absent for non-game fixture renders. */
  clientFrame?: number
  clientFrameSeconds?: number
  hudMaterials?: HudMaterialFrame
  camera: Camera
  effects: readonly Effect[]
  shadows?: readonly ShadowInput[]
  particles?: readonly ParticleItem[]
  legacyVisuals?:LegacyVisualFrameSet
  combatDecals?: readonly CombatDecalInput[]
  maximumCombatDecals?: number
  models?: readonly ModelItem[]
  modelVisibility?: ReadonlyMap<number, boolean>
  lightStyles?: readonly Readonly<{ style: number; scalar: number }>[]
  exposureHistogram?: Uint32Array
  deltaSeconds?: number
  capture?: FrameCaptureRequest
  visibility?:VisibilityFrame
  fog?:FogInput
  sky3d?:Readonly<{camera:Camera;visibility:VisibilityFrame;fog:FogInput}>
  brushModels?:Readonly<{sourceIdentity:bigint;registryIdentity:bigint;tick:bigint;entityRevision:bigint;collisionRevision:bigint;models:readonly Readonly<{sourceIndex:number;model:number;worldPosition:readonly[number,number,number];worldAngles:readonly[number,number,number];renderMode:number;color:readonly[number,number,number,number];renderFx:number;effects:number;draw:boolean;mover:unknown}>[]}>
  studioModels?: readonly Readonly<{sourceIndex:number;worldPosition:readonly[number,number,number];worldAngles:readonly[number,number,number];draw:boolean}>[]
  collisionWorldIdentity?: string
}>

export type DirectionalTextureInput = Readonly<{
  material: string
  kind: "normal" | "ssbump"
  logicalPath: string
  sha256: string
  width: number
  height: number
  authored: AuthoredTextureInput
  uvTransform: readonly [number, number, number, number, number, number]
}>
export type EnvironmentTextureInput = Readonly<{
  material: string
  logicalPath: string
  width: number
  height: number
  sha256: string
  rgba: Uint8Array
}>
export type EnvironmentFragmentInput = Readonly<{
  model: number
  face: number
  positions: Float32Array
  normals: Float32Array
  uv: Float32Array
  indices: Uint32Array
  lightmapUv: Float32Array
  visibility: Readonly<
    | { kind: "world"; leaves: readonly number[]; clusters: readonly number[]; areas: readonly number[] }
    | { kind: "brush-model"; entity: bigint; model: number }
  >
}>
type EffectiveInput<T> = Readonly<{ value: T; origin: "authored" | "shader-initializer" | "type-initializer" }>
export type WaterMaterialInput = Readonly<{
  identity: string
  mapMaterial: number | null
  shader: "dx90" | "dx9-hdr"
  opacity: "opaque" | "translucent"
  textures: readonly Readonly<{
    role: number
    disposition: "source" | "environment" | "render-target"
    colorRead: "srgb" | "linear" | "format-dependent"
    parameter: string
    reference: string
    logicalPath: string | null
  }>[]
  bottomMaterial: string | null
  underwaterOverlay: string | null
  baseFrame: EffectiveInput<number>
  normalFrame: EffectiveInput<number>
  environmentFrame: EffectiveInput<number>
  normalTransform: Readonly<{
    parameter: string
    matrix: Float32Array
    origin: EffectiveInput<number>["origin"]
    proxyMutated: boolean
  }>
  scale: EffectiveInput<readonly [number, number]>
  time: EffectiveInput<number>
  waterDepth: EffectiveInput<number>
  aboveWater: EffectiveInput<boolean>
  reflectAmount: EffectiveInput<number>
  refractAmount: EffectiveInput<number>
  reflectTint: EffectiveInput<readonly [number, number, number]>
  refractTint: EffectiveInput<readonly [number, number, number]>
  reflectionBlendFactor: EffectiveInput<number>
  fog: Readonly<{
    enabled: EffectiveInput<boolean> | null
    color: EffectiveInput<readonly [number, number, number]>
    start: EffectiveInput<number>
    end: EffectiveInput<number>
  }>
  cheapStart: EffectiveInput<number>
  cheapEnd: EffectiveInput<number>
  forceCheap: EffectiveInput<boolean>
  forceExpensive: EffectiveInput<boolean>
  reflectEntities: EffectiveInput<boolean>
  blurRefraction: EffectiveInput<boolean>
  noLowEndLightmap: EffectiveInput<boolean>
  scroll: readonly [
    EffectiveInput<readonly [number, number, number]>,
    EffectiveInput<readonly [number, number, number]>,
  ]
  fresnel: Readonly<{ cheapEnabled: boolean; expensiveConstant: readonly [number, number, number, number] }>
  requiredInputs: readonly number[]
}>
export type RefractMaterialInput = Readonly<{
  identity: string
  normal: Readonly<{ role: number; disposition: "source"; colorRead: "linear"; parameter: string; reference: string; logicalPath: string }>
  blurAmount: 0 | 1
  ignoreDepth: boolean
  refractAmount: number
  refractTint: readonly [number, number, number]
  normalFrame: number
  normalTransform: Float32Array
}>
export type WorldMaterialInput = Readonly<{
  identity: string
  mapMaterial: number
  shader: "lightmapped-generic" | "world-vertex-transition"
  textures: readonly Readonly<{
    role: number
    disposition: "source" | "environment" | "render-target"
    colorRead: "srgb" | "linear" | "format-dependent"
    parameter: string
    reference: string
    logicalPath: string | null
    initialFrame: number | null
    frameProxyMutated: boolean
    transform: Float32Array | null
    transformProxyMutated: boolean
  }>[]
  proxies: readonly Readonly<{ name: string; disposition: "handled" | "malformed" | "unsupported" }>[]
  environmentMap: null | Readonly<{ tint: readonly [number, number, number]; contrast: number; saturation: number }>
  fresnelReflection: number
}>
export type EnvironmentInput = Readonly<{
  profile: "ldr" | "hdr"
  identity: string
  clusters: number
  nodes: number
  leaves: number
  skySurfaces: number
  cubemaps: number
  waterSurfaces: number
  waterVolumes: number
  marks: number
  markFragments: number
  controllers: number
  markRecords: readonly Readonly<{
    status: number
    kind: number
    enabled: boolean
    dynamic: boolean
    material: string
    fragments: readonly EnvironmentFragmentInput[]
    sourceIndex: number
    receiver: null | Readonly<{ entity: bigint | null; model: number; parentEntity: number | null; localOrigin: readonly [number, number, number]; origin: readonly [number, number, number]; angles: readonly [number, number, number] }>
    targetFaces: readonly number[]
    renderOrder: number
    normalOffset: number
    polygonOffset: "none" | "decal"
  }>[]
  textures: readonly EnvironmentTextureInput[]
  collisionWorldIdentity: string
  receiverSnapshotRevision: bigint
  placementRevision: bigint
  authoredTextures: ReadonlyMap<string, AuthoredTextureInput>
  waterSurfaceFacts: readonly Readonly<{ face:number; model:number; material:number; selected:boolean; plane:readonly[number,number,number,number]; bindings:Readonly<{environment:boolean;reflection:boolean;refraction:boolean}>}>[]
  waterVolumeFacts: readonly Readonly<{index:number;surfaceZ:number;minimumZ:number;bounds:readonly[readonly[number,number,number],readonly[number,number,number]];surfaceMaterial:number;bottomMaterial:unknown;leaves:readonly number[];clusters:readonly number[];areas:readonly number[];contents:number;plane:readonly[number,number,number,number];surfaceTranslucent:boolean;bottomTranslucent:boolean|null;surfaceBindings:Readonly<{environment:boolean;reflection:boolean;refraction:boolean}>;bottomBindings:Readonly<{environment:boolean;reflection:boolean;refraction:boolean}>|null}>[]
  waterMaterials: ReadonlyMap<string, WaterMaterialInput>
  refractMaterials: ReadonlyMap<string, RefractMaterialInput>
  worldMaterials: ReadonlyMap<string, WorldMaterialInput>
  leafMinimumDistanceToWater: Uint16Array
  sky:null|Readonly<{name:string;faces:readonly Readonly<{face:number;material:string;encoding:"srgb"|"linear"|"hdr-rgbs";selectedTextures:readonly Readonly<{logicalPath:string;sha256:string}>[];textureTransform:readonly number[];color:readonly [number,number,number]}>[]}>
  cubemapFacts:readonly Readonly<{index:number;origin:readonly[number,number,number];logicalPath:string;sha256:string;width:number;height:number}>[]
}>

export type MaterialStateInput = Readonly<{
  lighting: number
  blendEnabled: boolean
  blendSource: number
  blendDestination: number
  alphaTest: boolean
  cull: number
  depthTest: boolean
  depthWrite: boolean
  depthFunction: number
  polygonOffset: number
  fog: number
  wireframe: boolean
  noDraw: boolean
  wrapS: number
  wrapT: number
  wrapU: number
  minFilter: number
  magFilter: number
  mipmapped: boolean
  noLod: boolean
  allMips: boolean
  samplingAvailable: boolean
  alphaTestReference: number
  alphaModulation: number
  alphaOwnership: Readonly<{
    baseTextureAvailable: boolean
    opacity: boolean
    alphaTest: boolean
    selfIlluminationMask: boolean
    environmentMask: boolean
    phongMask: boolean
    tintMask: boolean
    vertexAlpha: boolean
    materialAlphaModulation: boolean
  }>
  fragmentDiscard: Readonly<{
    kind: "none" | "alpha"
    source: "base-texture-or-one" | "shader-output"
    pass: "greater" | "greater-or-equal"
    reference: number
  }>
}>
export type AdditiveSpriteInput = Readonly<{ srgb: boolean; vertexColor: boolean; color: readonly [number, number, number]; hdrColorScale: number }>
export type ModelMaterialInput = Readonly<{
  identity: string
  cloakProxy: number
  shader: "unlit-generic" | "unlit-two-texture" | "modulate" | "vertex-lit-generic" | "eye-refract" | "eyes" | "refract"
  vertexRequirements: number
  bindings: readonly Readonly<{
    kind: "material" | "model"
    role: number
    colorRead: "srgb" | "linear" | "format-dependent"
    logicalPath: string
  }>[]
  environmentMap: unknown
  opacity: "opaque" | "translucent"
  framebuffer: "none" | "potential" | "current"
  requiredInputs: readonly string[]
  state: Readonly<{ kind: "vertex-lit-generic" | "eye-refract" | "eyes"; cloak?: SourceCloakState }>
    | Readonly<{ kind: "unlit-generic"; colorModulation: readonly [number, number, number] }>
    | Readonly<{ kind: "modulate" }>
    | Readonly<{ kind: "unlit-two-texture"; secondFrameRate: number | null; secondScrollRate: number | null; secondScrollAngle: number | null }>
    | Readonly<{ kind: "refract"; normalFrame: number; normalTransform: readonly number[]; refractAmount: number; refractTint: readonly [number, number, number]; blurAmount: 0 | 1; ignoreDepth: boolean }>
}>
export type AuthoredTextureInput = Readonly<{
  logicalPath: string
  sourceSha256: string
  width: number
  height: number
  depth: number
  mipCount: number
  frameCount: number
  faces: readonly number[]
  scalarEncoding: "u8" | "f16"
  sourceFormat: number | null
  sampling: Readonly<{
    wrapS: number
    wrapT: number
    wrapU: number
    minFilter: number
    magFilter: number
    anisotropyLevel: number
    mipmapped: boolean
    noLod: boolean
    allMips: boolean
  }>
  planes: readonly Readonly<{
    mip: number
    frame: number
    face: number
    slice: number
    width: number
    height: number
    rgba: Uint8Array
    decodedRgba?:Uint8Array
  }>[]
}>
export type StaticPropInput=Readonly<{
  count:number;source:Uint32Array;dictionaryModel:Uint32Array;presentationModel:Uint32Array;transform:Float32Array;skin:Int32Array;body:Uint32Array;lod:Uint32Array;fades:Float32Array;flags:Uint32Array;solidity:Uint8Array;ownership:Uint8Array;lightingKind:Uint8Array;lightingOrigin:Float32Array;leafOffsets:Uint32Array;leaves:Uint16Array;areas:Uint16Array;vhvObjects:Uint32Array;runtimeAmbient:Float32Array;runtimeLightOffsets:Uint32Array;runtimeLights:readonly Readonly<{source:number;kind:number;style:number;ratio:number;direction:readonly[number,number,number];intensity:readonly[number,number,number];origin:readonly[number,number,number];normal:readonly[number,number,number];stopDot:number;stopDot2:number;exponent:number;radius:number;attenuation:readonly[number,number,number]}>[];models:readonly string[];vhv:readonly Readonly<{occurrence:number;model:number;profile:0|1;vertexCount:number;meshes:readonly Readonly<{primitive:number;lod:number;vertexCount:number;colors:Uint8Array}>[]}>[]
}>

export type MapLoadRequest = Readonly<{
  /** Verified immutable presentation-resource closure, when supplied by the owner. */
  resourceIdentity?: string
  payload: Uint8Array
  payloadSha256: string
  lightStyles?: readonly Readonly<{ style: number; scalar: number }>[]
  directionalTextures?: readonly DirectionalTextureInput[]
  environment?: EnvironmentInput
  materialStates?: ReadonlyMap<string, MaterialStateInput>
  particleTextures?: readonly (AuthoredTextureInput & Readonly<{ material: string; materialPath: string; spriteCard?: SpriteCardInput | null; additiveSprite?: AdditiveSpriteInput | null }>)[]
  legacyVisualTextures?: readonly (AuthoredTextureInput & Readonly<{ material: string;program:LegacyVisualProgram;normal:AuthoredTextureInput|null }>)[]
  modelOccurrences?: readonly Readonly<{ entity: number; model: string; matrix: Float32Array }>[]
  modelFacing?: ReadonlyMap<string, Readonly<{ frontFace: "clockwise" | "counter-clockwise"; cullFace: "back" }>>
  modelMaterials?: ReadonlyMap<string, ModelMaterialInput>
  authoredTextures?: ReadonlyMap<string, AuthoredTextureInput>
  brushModels?:readonly Readonly<{index:number;surfaceRange:readonly[number,number];vertexCount:number;triangleCount:number;materials:readonly number[]}>[]
  modelDrawInputs?: readonly Readonly<{ entity: number; lighting: ModelLightingInput; eyes: readonly ModelEyeState[] }>[]
  staticProps?:StaticPropInput
  diagnostic?: boolean
  signal?: AbortSignal
}>

export type SceneDiagnostic = Readonly<{
  code: "MissingMaterial" | "MissingTextureMips" | "MissingModelLighting" | "MissingModelEyeState" | "MissingDirectionalInput" | "MissingProfileInput" | "UnsupportedProfileInput"
  identity: string
  detail: string
}>

export type SceneResult = Readonly<{
  payloadSha256: string
  lightingProfile: "ldr" | "hdr"
  deviceGeneration: number
  sceneGeneration: number
  drawableSurfaces: number
  drawBatches: number
  lightingSampleCount: number
  directionalFaces: number
  worldLights: number
  ambientSamples: number
  requirements: readonly ProfileRequirement[]
  diagnostics: readonly SceneDiagnostic[]
  resources: Readonly<{ geometries: number; materials: number; textures: number }>
  environment?: MapLoadRequest["environment"]
  environmentDrawables: number
  staticProps:Readonly<{total:number;main:number;sky3d:number;runtimeLit:number;batches:number;batched:number;hierarchyNodes:number}>
  runtimeStaticProps:readonly Readonly<{source:number;origin:readonly[number,number,number];lightingOrigin:readonly[number,number,number];radius:number}>[]
  displacements: readonly Readonly<{
    source: number
    face: number
    material: string
    positions: Float32Array
    normals: Float32Array
    indices: Uint32Array
    bounds: readonly [readonly [number, number, number], readonly [number, number, number]]
    submittedTriangles: number
    cull: "back" | "none"
    depthTest: boolean
    depthWrite: boolean
    blend: boolean
    lighting: Readonly<{ kind: string; styles: readonly number[]; sampleStart: number; samplesPerLayer: number; layers: number }>
  }>[]
}>

export type ModelPanelPass = Readonly<{
  identity: string
  model: string
  skin: number
  kind: "entity" | "studio"
  fov: number
  origin: readonly [number, number, number]
  angles: readonly [number, number, number]
  bounds: Readonly<{ x: number; y: number; width: number; height: number }>
  background: "opaque" | "transparent" | "clear-transparent"
  presentationTimeSeconds?: number
  pose?: NonNullable<ModelItem["pose"]>
  modelLighting?: ModelLightingInput
  eyeStates?: readonly ModelEyeState[]
  mergedModels?: readonly Readonly<{ model: string; skin: number; pose: NonNullable<ModelItem["pose"]>; modelLighting?: ModelLightingInput; eyeStates?: readonly ModelEyeState[] }>[]
  particles?: readonly ParticleItem[]
}>

export type ModelPanelFrameResult = Readonly<{
  panels: readonly Readonly<{ identity: string; model: string; skin: number; primitives: number }>[]
  milliseconds: number
}>

export type FrameCapture = Readonly<{
  format: "image/png"
  sha256: string
  bytes: Uint8Array
}>

export type WaterPassTiming = Readonly<{
  kind: WaterFramePass["kind"]
  visibilityMilliseconds: number
  sceneMilliseconds: number
  clippingMilliseconds: number
  renderMilliseconds: number
  totalMilliseconds: number
}>

export type FrameResult = Readonly<{
  beforeHudCapture?: FrameCapture
  deviceGeneration: number
  sceneGeneration: number
  submission: null
  exposure: ExposureSnapshot
  visibleProjectedMarks: number
  waterPasses: readonly ("reflection"|"refraction"|"main"|"intersection")[]
  waterPassTimings: readonly WaterPassTiming[]
  waterStateRestored: boolean
  sky3dPass?:Readonly<{phases:readonly["sky3d","depth-reset","main","restore"];skySurfaces:number;skyProps:number;mainProps:number;visibleSkyPropSources:readonly number[];fog:Readonly<{start:number;end:number;primary:readonly[number,number,number,number]}>;stateRestored:boolean}>
  visibleMainStaticPropSources:readonly number[]
  runtimeStaticPropScreen:readonly Readonly<{source:number;x:number;y:number;width:number;height:number}>[]
  timings: Readonly<{
    particleItems: number
    particleBatches: number
    cloakFramebufferCopies: number
    dynamicItemsMilliseconds: number
    worldMilliseconds: number
    viewModelMilliseconds: number
    totalMilliseconds: number
  }>
  viewModelPass?: Readonly<{
    depthRange: readonly [number, number]
    worldDepthCleared: boolean
    viewportRestored: boolean
  }>
  pacing: readonly FramePacingRecord[]
  capture?: FrameCapture
}>

export type ResizeResult = Readonly<{
  width: number
  height: number
  suspended: boolean
  deviceGeneration: number
  attachmentGeneration: number
}>

export type RendererLifecycle = "Initializing" | "Ready" | "Recovering" | "Failed" | "Disposed"

export type RendererCreateRequest = Readonly<{
  serviceAudio?: () => void
  canvas: Canvas
  configuration: RenderConfiguration
  powerPreference?: "low-power" | "high-performance"
  sampleCount?: 1 | 4
  textureQuality?: Readonly<{ mipOffset: number; trilinear: boolean; anisotropy: number }>
}>

export type FramePacingCallback = (timestampMilliseconds: number) => Frame | undefined | Promise<Frame | undefined>

export type GeometryEvidence = Readonly<{
  sceneGeneration: number
  samples: readonly Readonly<{
    x: number
    y: number
    disposition: "main-world" | "static-prop" | "dynamic-prop" | "background"
    family: "world" | "displacement" | "water" | "static-prop" | "dynamic-prop" | null
    depth: number | null
    primitive: number | null
    object: number | null
    material: string | null
    lightmap: readonly [number, number, number] | null
  }>[]
}>

export type WaterTargetEvidence = Readonly<{
  x: number
  y: number
  reflection: readonly [number, number, number, number]
  refraction: readonly [number, number, number, number]
}>

export type ModelGeometryEvidence = Readonly<{
  sceneGeneration: number
  samples: readonly Readonly<{
    x: number
    y: number
    identity: number
    material: string
    modelDepth: number
    worldDepth: number | null
    worldPosition: readonly number[]
    worldNormal: readonly number[]
  }>[]
}>

export type MaterialDepthEvidence=Readonly<{id:number;name:string;identity?:string;depthTest:boolean;depthWrite:boolean;depthFunc:number;transparent:boolean;renderOrder:number}>
export type ParticleBatchEvidence=Readonly<{key:string;visible:boolean;sky:boolean;count:number;positions:number[];material:string;depthTest:boolean;depthWrite:boolean;depthFunc:number}>

export interface Renderer {
  takeParticleVisibilitySamples(): readonly ParticleVisibilitySample[]
  captureParticleVisibilityEvidence(): ReturnType<ParticleVisibilityQueries["evidence"]>
  readonly configuration: RenderConfiguration
  readonly sampleCount: 1 | 4
  readonly capabilities: Readonly<{ sampleCounts: readonly [1, 4]; presentationSynchronization: false }>
  readonly textureQuality: RendererCreateRequest["textureQuality"]
  readonly lifecycle: RendererLifecycle
  readonly deviceGeneration: number
  readonly sceneGeneration: number
  loadMap(request: MapLoadRequest): Promise<SceneResult>
  admitModels(request: ModelAdmissionRequest): Promise<void>
  prepareVisiblePipelines(camera: Camera, leaves: readonly number[]): Promise<void>
  prepareModelPipelines(models: readonly Readonly<{ item: ModelItem; pass: "panel" | "view" | "world"; unposedPanel?: boolean }>[], camera: Camera, fog?: FogInput): Promise<void>
  prepareParticlePipelines(camera: Camera, fog?: FogInput): Promise<void>
  requestParticleDepthEvidence(): void
  readParticleDepthEvidence(): ReturnType<SourceParticleDepth["readEvidence"]>
  render(frame: Frame): Promise<FrameResult>
  renderModelPanels(panels: readonly ModelPanelPass[]): Promise<ModelPanelFrameResult>
  modelVisible(model: string, skin: number, position: readonly [number, number, number], angles: readonly [number, number, number], camera: Camera, views: readonly WaterFramePass[]): boolean
  completeFrameProfile(): RendererFrameProfile | undefined
  captureGeometryEvidence(camera: Camera, ownership?: "main" | "sky3d"): GeometryEvidence
  captureViewModelEvidence(camera: Camera): ModelGeometryEvidence
  captureWorldModelEvidence(camera: Camera): ModelGeometryEvidence
  captureParticleBatchEvidence(): ParticleBatchEvidence[]
  captureMaterialDepthEvidence(): MaterialDepthEvidence[]
  captureWaterTargetEvidence(x: number, y: number): Promise<WaterTargetEvidence>
  resize(cssWidth: number, cssHeight: number, devicePixelRatio: number): ResizeResult
  startFramePacing(callback: FramePacingCallback): void
  stopFramePacing(): void
  dispose(): Promise<void>
}

export class RenderingError extends Error {
  constructor(
    readonly code:
      | "MalformedInput"
      | "MissingInput"
      | "IdentityMismatch"
      | "UnsupportedEnvironment"
      | "UnsupportedFeature"
      | "BoundExceeded"
      | "InvalidState"
      | "GpuValidationFailed"
      | "DeviceLost"
      | "CaptureFailed",
    message: string,
  ) {
    super(message)
    this.name = "RenderingError"
  }
}

type WaterMeshResource = Readonly<{
  mesh: THREE.Mesh
  materialIdentity: string
}>
type WaterMaterialResource = Readonly<{
  material: THREE.MeshBasicNodeMaterial
  cheapMaterial: THREE.MeshBasicNodeMaterial | null
  normalFrames: AuthoredTextureFrames
  normalConsumer: string
  normalNode: ReturnType<typeof TSL.texture>
  cheapNormalNode: ReturnType<typeof TSL.texture> | null
  fog: THREE.Fog | null
  state: WaterMaterialInput
}>
type RefractMaterialResource = Readonly<{
  setTint(tint: readonly [number, number, number]): void
  material: THREE.MeshBasicNodeMaterial
  normalFrames: AuthoredTextureFrames
  normalConsumer: string
  normalNode: ReturnType<typeof TSL.texture>
  state: RefractMaterialInput
}>
type WorldMaterialResource = Readonly<{
  mapMaterial: number
  normalFrames: AuthoredTextureFrames
  normalConsumer: string
  normalNode: ReturnType<typeof TSL.texture>
  state: WorldMaterialInput
}>
type WorldBatchResource = Readonly<{
  mesh: THREE.Mesh
  faces: Uint32Array
  sourceIndices: Uint32Array
  index: THREE.BufferAttribute
  transparent: boolean
  bundled: boolean
}>
type ProjectedMarkResource = Readonly<{
  mesh: THREE.Mesh
  face: number
  sourceIndex: number
  visibility: EnvironmentFragmentInput["visibility"]
}>
type StaticPropResource = Readonly<{
  object: THREE.Group
  source: number
  ownership: 0 | 1
  leaves: Uint16Array
  origin: readonly [number, number, number]
  lightingOrigin: readonly [number, number, number] | null
  flags: number
  fadeMinimum: number
  fadeMaximum: number
  forcedFadeScale: number
  radius: number
  bounds: readonly [number, number, number, number, number, number]
  fadeUniform: ReturnType<typeof TSL.uniform>
  fadeBindings: readonly StaticPropFadeBinding[]
  batches: Readonly<{ batch: StaticPropBatch; index: number }>[]
}>

type ModelPanelMaterialAnimation = Readonly<{
  texture: THREE.Texture
  frames: AuthoredTextureFrames
  consumer: string
  frameRate: number | null
  scrollRate: number | null
  scrollAngle: number | null
  scrollX: ReturnType<typeof TSL.uniform>
  scrollY: ReturnType<typeof TSL.uniform>
}> & { frame: number }

type ModelLightingScene = Readonly<{
  materials: ReadonlyMap<string, ModelMaterialInput> | undefined
  states: ReadonlyMap<string, MaterialStateInput>
  waterFog?: SourceWaterFogUniforms
  textures: ReadonlyMap<string, ModelLightingTextures>
  cubemap: (identity: string) => THREE.CubeTexture | undefined
  exposure: ReturnType<typeof TSL.uniform>
  graphs: ModelLightingGraphs
}>

type ModelLightingTextures = Readonly<{
  normal?: () => THREE.Texture | undefined
  warp?: THREE.Texture
  exponent?: THREE.Texture
  iris?: THREE.Texture
  ambientOcclusion?: THREE.Texture
  environment?: THREE.CubeTexture
}>

export type ModelAdmissionRequest = Readonly<{
  models: RuntimeMap["models"]
  modelMaterials: ReadonlyMap<string, ModelMaterialInput>
  authoredTextures: ReadonlyMap<string, AuthoredTextureInput>
  materialStates: ReadonlyMap<string, MaterialStateInput>
  modelFacing: NonNullable<MapLoadRequest["modelFacing"]>
  particleTextures?: MapLoadRequest["particleTextures"]
}>

type ModelAssets = Pick<SceneResources, "modelTemplates" | "modelPipelineKeys" | "modelLightingTextures" | "modelPanelLightingGraphs" | "modelPanelMaterialAnimations" | "materialStates" | "disposables" | "textureResidency" | "waterFogUniforms" | "modelCubemap" | "particleTextures" | "particleBatchMaterials" | "particleDepth" | "particlePipelineMeshes"> & { loadRequest: Pick<MapLoadRequest, "modelMaterials" | "modelFacing" | "authoredTextures"> }

type ModelTemplateContext = Readonly<{
  request: Pick<MapLoadRequest, "modelMaterials" | "modelFacing">
  materialStates: ReadonlyMap<string, MaterialStateInput>
  disposables: OwnedResourceGeneration
  texture: (identity: string, role: number, kind?: "material" | "model") => Readonly<{ texture: THREE.Texture; input: AuthoredTextureInput }> | undefined
  cubemap: (identity: string) => THREE.CubeTexture | undefined
  frames: (input: AuthoredTextureInput, colorSpace: string) => AuthoredTextureFrames
  modelLightingTextures: Map<string, ModelLightingTextures>
  modelPanelMaterialAnimations: Map<string, ModelPanelMaterialAnimation[]>
  modelTemplates: Map<string, THREE.Group>
  modelBaseSamples: Map<string, any>
  waterFogUniforms: SourceWaterFogUniforms
}>

function buildModelTemplates(models: RuntimeMap["models"], context: ModelTemplateContext): void {
  const { request, materialStates, disposables, modelTemplates, modelLightingTextures, modelPanelMaterialAnimations, modelBaseSamples, waterFogUniforms } = context
  const createModelTexture = context.texture, createModelCubemap = context.cubemap
  for (const model of models) {
    const template = new THREE.Group()
    for (const [primitiveIndex, primitive] of model.primitives.entries()) {
      const geometry = new THREE.BufferGeometry()
      geometry.setAttribute("position", new THREE.BufferAttribute(primitive.positions, 3))
      geometry.setAttribute("normal", new THREE.BufferAttribute(primitive.normals, 3))
      geometry.setAttribute("uv", new THREE.BufferAttribute(primitive.uv, 2))
      geometry.setIndex(new THREE.BufferAttribute(primitive.indices, 1))
      geometry.computeBoundingSphere(); disposables.add(geometry)
      const bindGeometry = new THREE.BufferGeometry()
      bindGeometry.setAttribute("position", new THREE.BufferAttribute(primitive.bindPositions, 3))
      bindGeometry.setAttribute("normal", new THREE.BufferAttribute(primitive.bindNormals, 3))
      bindGeometry.setAttribute("tangent", new THREE.BufferAttribute(primitive.bindTangents, 4))
      bindGeometry.setAttribute("skinIndex", new THREE.BufferAttribute(primitive.boneIndices, 4))
      bindGeometry.setAttribute("skinWeight", new THREE.BufferAttribute(primitive.boneWeights, 4))
      bindGeometry.setAttribute("uv", geometry.getAttribute("uv")); bindGeometry.setIndex(geometry.getIndex())
      bindGeometry.boundingSphere = geometry.boundingSphere; disposables.add(bindGeometry)
      SOURCE_MODEL_BIND_GEOMETRY.set(geometry, Object.freeze({ geometry: bindGeometry, palette: primitive.bonePalette }))
      const resolved = model.materials[primitive.material]!, materialState = materialStates.get(resolved.logicalPath.toLowerCase())
      if (materialState?.noDraw) continue
      const typed = request.modelMaterials?.get(resolved.logicalPath.toLowerCase())
      const eye = typed?.shader === "eye-refract" || typed?.shader === "eyes"
      const baseTexture = eye ? createModelTexture(resolved.logicalPath, 8, "model") : createModelTexture(resolved.logicalPath, 0)
      if (eye && !baseTexture) throw new RenderingError("MissingInput", `authored Studio eye iris ${resolved.logicalPath} is unavailable`)
      if (typed && typed.shader !== "unlit-generic" && typed.shader !== "unlit-two-texture" && typed.shader !== "refract") {
        const warp = createModelTexture(resolved.logicalPath, 6, "model")?.texture
        const exponent = createModelTexture(resolved.logicalPath, 5, "model")?.texture
        const iris = eye ? baseTexture?.texture : undefined
        const ambientOcclusion = typed.shader === "eye-refract" ? createModelTexture(resolved.logicalPath, 10, "model")?.texture : undefined
        const environment = createModelCubemap(resolved.logicalPath)
        const normal = typed.state.kind === "vertex-lit-generic" && typed.state.cloak?.enabled ? () => createModelTexture(resolved.logicalPath, 7)?.texture : undefined
        if (warp || exponent || iris || ambientOcclusion || environment || normal) modelLightingTextures.set(resolved.logicalPath.toLowerCase(), Object.freeze({ warp, exponent, iris, ambientOcclusion, environment, normal }))
      }
      const refract = typed?.state.kind === "refract" ? typed.state : undefined
      const material = refract ? (() => {
        const normal = createModelTexture(resolved.logicalPath, 8)
        if (!normal || refract.normalFrame >= normal.input.frameCount) throw new RenderingError("MissingInput", `authored model Refract normal ${resolved.logicalPath} is unavailable`)
        const texture = context.frames(normal.input, THREE.NoColorSpace).select(refract.normalFrame, `model-refract:${model.logicalPath}:${resolved.logicalPath}`)
        return createSourceRefractMaterial({ state: refract, normal: texture, normalTransform: refract.normalTransform }).material
      })() : new THREE.MeshBasicNodeMaterial(materialOptions(resolved, materialState))
      material.side = materialState?.cull === 1 ? THREE.DoubleSide : sourceModelSide(request.modelFacing!.get(model.logicalPath.split("#skin=")[0]!.toLowerCase())!)
      const sampled = baseTexture ? swizzle(TSL.texture(baseTexture.texture, TSL.uv()), baseTexture.input) : undefined
      const first = selectDiagnosticModelBase(baseTexture !== undefined) === "authored-texture" ? sampled! : TSL.vec4(TSL.color(debugColor(`diagnostic:${resolved.logicalPath}`)), 1)
      const baseKey = baseTexture && typed?.shader !== "unlit-two-texture" ? `${baseTexture.texture.uuid}:${baseTexture.input.sourceFormat}` : undefined
      let base = baseKey ? modelBaseSamples.get(baseKey) : undefined
      if (!base) { base = first; if (baseKey) modelBaseSamples.set(baseKey, base) }
      if (baseKey && baseTexture) SOURCE_MODEL_BASE_TEXTURE.set(base, baseTexture)
      if (typed?.shader === "unlit-two-texture") {
        const second = createModelTexture(resolved.logicalPath, 6)
        if (!second) throw new RenderingError("MissingInput", `authored second model texture ${resolved.logicalPath} is unavailable`)
        const scrollX = TSL.uniform(0, "float"), scrollY = TSL.uniform(0, "float"), frames = context.frames(second.input, THREE.SRGBColorSpace)
        const consumer = `panel:${model.logicalPath.toLowerCase()}:${resolved.logicalPath.toLowerCase()}`
        frames.select(0, consumer)
        const records = modelPanelMaterialAnimations.get(model.logicalPath.toLowerCase()) ?? []
        records.push({ texture: second.texture, frames, consumer, frameRate: typed.state.secondFrameRate, scrollRate: typed.state.secondScrollRate, scrollAngle: typed.state.secondScrollAngle, scrollX, scrollY, frame: 0 })
        modelPanelMaterialAnimations.set(model.logicalPath.toLowerCase(), records)
        base = TSL.vec4(first.rgb.mul(swizzle(TSL.texture(second.texture, TSL.uv().add(TSL.vec2(scrollX, scrollY))), second.input).rgb), 1)
      }
      const color = refract ? material.colorNode : sourceFragmentColor(base, materialState, waterFogUniforms)
      SOURCE_MODEL_BASE_COLOR.set(geometry, refract ? color : base); SOURCE_MODEL_BASE_COLOR.set(bindGeometry, refract ? color : base)
      material.colorNode = color; material.toneMapped = false; disposables.add(material)
      const mesh = new THREE.Mesh(geometry, material)
      mesh.userData.primitiveMaterial = primitive.material; mesh.userData.sourcePrimitive = primitiveIndex; mesh.userData.materialIdentity = resolved.logicalPath
      template.add(mesh)
    }
    modelTemplates.set(model.logicalPath, template)
  }
}

type SceneResources = {
  map: RuntimeMap
  payload: Uint8Array
  payloadSha256: string
  loadRequest: Omit<MapLoadRequest, "payload" | "signal">
  group: THREE.Group
  modelTemplates: Map<string, THREE.Group>
  modelBaseSamples: Map<string, any>
  modelTemplateResources: Map<string, (THREE.BufferGeometry | THREE.Material)[]>
  modelPipelineKeys: Set<string>
  borrowedResources: (THREE.BufferGeometry | THREE.Material | THREE.Texture)[]
  modelLightingTextures: ReadonlyMap<string, ModelLightingTextures>
  modelLightingGraphs: ModelLightingGraphs
  modelPanelLightingGraphs: ModelLightingGraphs
  modelPanelMaterialAnimations: ReadonlyMap<string, readonly ModelPanelMaterialAnimation[]>
  modelOccurrenceInstances:Map<number,THREE.Group>
  modelOccurrenceLighting: readonly SourceModelLightingUniforms[]
  brushModelTemplates:Map<number,THREE.Group>
  particleTextures: Map<string, THREE.Texture>
  particleBatchMaterials: Map<string, THREE.MeshBasicNodeMaterial>
  particleDepth: SourceParticleDepth
  legacyVisuals: readonly LegacyVisuals[] | null
  particlePipelineMeshes: THREE.Group
  materialStates: ReadonlyMap<string, MaterialStateInput>
  disposables: OwnedResourceGeneration
  textureResidency: SharedTextureResidency<THREE.Texture>
  lightmapTextures: readonly [THREE.DataTexture, THREE.DataTexture?, THREE.DataTexture?, THREE.DataTexture?]
  exposureUniform: ReturnType<typeof TSL.uniform>
  waterFogUniforms: SourceWaterFogUniforms
  diagnostics: readonly SceneDiagnostic[]
  worldBundle: THREE.BundleGroup
  skyWorldBundle: THREE.BundleGroup
  mainAnimatedWorld: THREE.Group
  skyAnimatedWorld: THREE.Group
  mainTransparentWorld: THREE.Group
  skyTransparentWorld: THREE.Group
  mainModelOccurrences: THREE.Group
  projectedMarkGroup: THREE.Group
  worldBatches: readonly WorldBatchResource[]
  skyWorldBatches: readonly WorldBatchResource[]
  worldVisibility: RetainedWorldVisibility
  skyWorldVisibility: RetainedWorldVisibility
  worldDraws: PersistentWorldDraws
  skyWorldDraws: PersistentWorldDraws
  modelLookup: ReadonlyMap<string, RuntimeMap["models"][number]>
  projectedMarksByFace: ReadonlyMap<number, readonly ProjectedMarkResource[]>
  brushProjectedMarks: readonly ProjectedMarkResource[]
  leafVisibility: RetainedLeafVisibility
  runtimeStaticPropInstances: readonly StaticPropResource[]
  projectedMarks: readonly ProjectedMarkResource[]
  combatDecals: CombatDecals
  waterMeshes: readonly WaterMeshResource[]
  waterMaterials: ReadonlyMap<string,WaterMaterialResource>
  refractMaterials: ReadonlyMap<string, RefractMaterialResource>
  worldMaterials: ReadonlyMap<string,WorldMaterialResource>
  cubemapTextures: ReadonlyMap<number,THREE.CubeTexture>
  modelCubemap: (identity: string) => THREE.CubeTexture | undefined
  skyGroup: THREE.Group | null
  mainStaticProps:THREE.Group
  skyStaticProps:THREE.Group
  staticPropInstances: readonly StaticPropResource[]
  staticPropBatches: readonly Readonly<{ ownership: 0 | 1; batch: StaticPropBatch }>[]
  reflectionTarget: THREE.RenderTarget | null
  refractionTarget: THREE.RenderTarget | null
  result: SceneResult
  disposed: boolean
}

type AuthoredTextureFrames = Readonly<{
  frameCount: number
  select(frame: number, consumer: string): THREE.Texture
}>

type Backend = THREE.WebGPURenderer & {
  backend: {
    isWebGPUBackend?: boolean
    device?: Readonly<{
      limits: Readonly<{ maxTextureDimension2D: number }>
      queue: Readonly<{ onSubmittedWorkDone(): Promise<unknown> }>
    }>
    context?: Readonly<{
      unconfigure(): void
      getConfiguration?(): null | Readonly<{ format: string; alphaMode?: string; colorSpace?: string }>
    }>
  }
  onDeviceLost: (info: unknown) => void
}

type WebGpuNavigator = Navigator & Readonly<{ gpu?: Readonly<{ getPreferredCanvasFormat(): string }> }>

function finite(values: readonly number[]): boolean {
  return values.every(Number.isFinite)
}

function debugColor(identity: string): number {
  let hash = 0x811c9dc5
  for (const byte of new TextEncoder().encode(identity)) {
    hash ^= byte
    hash = Math.imul(hash, 0x01000193)
  }
  return ((hash >>> 8) & 0x7f7f7f) | 0x404040
}

async function digest(bytes: Uint8Array): Promise<string> {
  const value = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))
  return Array.from(value, (byte) => byte.toString(16).padStart(2, "0")).join("")
}

function sourceTransform(object: THREE.Object3D, position: readonly number[], angles: readonly number[]): void {
  object.position.set(position[0]!, position[1]!, position[2]!)
  object.rotation.set(
    THREE.MathUtils.degToRad(angles[2]!),
    THREE.MathUtils.degToRad(angles[0]!),
    THREE.MathUtils.degToRad(angles[1]!),
    "ZYX",
  )
}
function runtimeStaticLightingNode(
  map: RuntimeMap,
  input: StaticPropInput,
  index: number,
  halfLambert: boolean,
  warp?: THREE.Texture,
):any{
  const normal=sourceModelWorldNormal.normalize(),cube=Array.from({length:6},(_,side)=>TSL.vec3(...input.runtimeAmbient.subarray(index*18+side*3,index*18+side*3+3) as unknown as [number,number,number]))
  let lighting=normal.x.lessThan(0).select(cube[1],cube[0]).mul(normal.x.mul(normal.x)).add(normal.y.lessThan(0).select(cube[3],cube[2]).mul(normal.y.mul(normal.y))).add(normal.z.lessThan(0).select(cube[5],cube[4]).mul(normal.z.mul(normal.z)))
  for(let at=input.runtimeLightOffsets[index]!;at<input.runtimeLightOffsets[index+1]!;at++){
    const light=input.runtimeLights[at]!
    if(map.lighting.profile==="hdr"){
      const source=map.lighting.descriptor.worldLights[light.source]
      if(!source||source.kind!==light.kind||source.style!==light.style||source.origin.some((value,axis)=>value!==light.origin[axis]))throw new RenderingError("IdentityMismatch","runtime static-prop world-light identity differs")
    }
    const delta=TSL.vec3(...light.origin).sub(TSL.positionWorld),distance=delta.length(),direction=delta.normalize()
    let attenuation:any
    if(light.kind===3)attenuation=TSL.float(1)
    else if(light.kind===0)attenuation=distance.mul(distance).max(1).reciprocal().mul(direction.dot(TSL.vec3(...light.normal)).negate().max(0))
    else if(light.kind===4)attenuation=TSL.float(light.attenuation[1]).sub(distance).max(0)
    else attenuation=TSL.float(light.attenuation[0]).add(distance.mul(light.attenuation[1])).add(distance.mul(distance).mul(light.attenuation[2])).reciprocal()
    if(light.kind===2){const cone=direction.dot(TSL.vec3(...light.normal)).negate(),spread=Math.max(Number.EPSILON,light.stopDot-light.stopDot2),factor=cone.sub(light.stopDot2).div(spread).clamp(0,1);attenuation=attenuation.mul(light.exponent===0||light.exponent===1?factor:factor.pow(light.exponent))}
    const lightDirection=light.kind===3?TSL.vec3(...light.normal).negate():direction
    let diffuse:any=halfLambert?normal.dot(lightDirection).mul(0.5).add(0.5).clamp(0,1):normal.dot(lightDirection).clamp(0,1)
    if(warp)diffuse=TSL.texture(warp,TSL.vec2(diffuse,0.5)).rgb.mul(2)
    else if(halfLambert)diffuse=diffuse.pow(2)
    lighting=lighting.add(TSL.vec3(...light.intensity).mul(attenuation).mul(diffuse))
  }
  return lighting
}
function modelKey(model: string, skin: number, body = 0) {
  return `${model}${body === 0 ? "" : `#body=${body}`}${skin === 0 ? "" : `#skin=${skin}`}`
}

function modelOccurrenceMatrices(
  map: RuntimeMap,
  input: MapLoadRequest["modelOccurrences"],
): ReadonlyMap<number, NonNullable<MapLoadRequest["modelOccurrences"]>[number]> {
  const supplied = input ?? []
  if (supplied.length !== map.modelOccurrences.length) {
    throw new RenderingError("MissingInput", "exact model occurrence matrices are incomplete")
  }
  const expected = new Map(map.modelOccurrences.map((occurrence) => [occurrence.entity, occurrence]))
  const matrices = new Map<number, NonNullable<MapLoadRequest["modelOccurrences"]>[number]>()
  for (const occurrence of supplied) {
    const target = expected.get(occurrence.entity)
    if (
      !target ||
      matrices.has(occurrence.entity) ||
      occurrence.model !== map.models[target.model]?.logicalPath.split("#")[0] ||
      occurrence.matrix.length !== 12 ||
      ![...occurrence.matrix].every(Number.isFinite)
    ) {
      throw new RenderingError("IdentityMismatch", "model occurrence matrix identity differs")
    }
    matrices.set(occurrence.entity, occurrence)
  }
  return matrices
}

function disposeScene(scene: Pick<SceneResources,"group"|"disposables"|"modelTemplates"|"modelOccurrenceInstances"|"disposed"> & Partial<Pick<SceneResources,"textureResidency"|"brushModelTemplates"|"waterMaterials"|"refractMaterials"|"worldMaterials"|"cubemapTextures"|"particleBatchMaterials">>): void {
  if (scene.disposed) return
  scene.disposed = true
  scene.group.clear()
  scene.disposables.dispose()
  scene.textureResidency?.clear()
  scene.modelTemplates.clear()
  scene.modelOccurrenceInstances.clear()
  scene.brushModelTemplates?.clear()
  ;(scene.waterMaterials as Map<string, WaterMaterialResource> | undefined)?.clear()
  ;(scene.refractMaterials as Map<string, RefractMaterialResource> | undefined)?.clear()
  ;(scene.worldMaterials as Map<string, WorldMaterialResource> | undefined)?.clear()
  ;(scene.cubemapTextures as Map<number, THREE.CubeTexture> | undefined)?.clear()
  scene.particleBatchMaterials?.clear()
}

function textureFromRgba(
  input: { rgba: Uint8Array; width: number; height: number },
  colorSpace: string,
  state?: Pick<MaterialStateInput, "wrapS" | "wrapT" | "minFilter" | "magFilter">,
): THREE.DataTexture {
  const texture = new THREE.DataTexture(input.rgba, input.width, input.height, THREE.RGBAFormat, THREE.UnsignedByteType)
  texture.colorSpace = colorSpace
  const wrap = (value: number) => value === 0 ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping
  texture.wrapS = wrap(state?.wrapS ?? 0)
  texture.wrapT = wrap(state?.wrapT ?? 0)
  texture.minFilter = (state?.minFilter ?? 2) === 0 ? THREE.NearestFilter : THREE.LinearFilter
  texture.magFilter = (state?.magFilter ?? 1) === 0 ? THREE.NearestFilter : THREE.LinearFilter
  texture.generateMipmaps = false
  texture.flipY = false
  texture.needsUpdate = true
  return texture
}

function textureFromAuthored(input: AuthoredTextureInput, colorSpace: string, frame = 0,
  quality?: RendererCreateRequest["textureQuality"]): THREE.Texture {
  if (input.depth !== 1 || input.frameCount < 1 || !input.faces.includes(0) ||
    frame < 0 || frame >= input.frameCount || input.sampling.wrapS === 2 || input.sampling.wrapT === 2 || input.sampling.wrapU === 2) {
    throw new RenderingError("UnsupportedFeature", `authored 2D texture ${input.logicalPath} requires an unsupported topology or border sampler`)
  }
  const planes = input.planes
    .filter((plane) => plane.frame === frame && plane.face === 0 && plane.slice === 0)
    .sort((left, right) => left.mip - right.mip)
  if (planes.length !== input.mipCount || planes.some((plane, mip) => plane.mip !== mip)) {
    throw new RenderingError("MissingInput", `authored texture ${input.logicalPath} has an incomplete selected mip chain`)
  }
  const selected = planes.slice(input.sampling.noLod ? 0 : Math.min(Math.max(0, quality?.mipOffset ?? 0), planes.length - 1))
  const base = selected[0]!
  const {layout,mipmaps}=authoredMipUpload(input.sourceFormat,input.scalarEncoding,selected)
  const texture = layout.compressed === null
    ? new THREE.DataTexture(mipmaps[0]!.data, base.width, base.height, THREE.RGBAFormat, layout.type)
    : new THREE.CompressedTexture(mipmaps, base.width, base.height, layout.compressed, layout.type)
  texture.mipmaps = mipmaps
  texture.name = `authored:${input.logicalPath}:frame=${frame}/${input.frameCount}:${colorSpace}`
  texture.colorSpace = colorSpace
  const wrap = (value: number) => value === 0 ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping
  texture.wrapS = wrap(input.sampling.wrapS)
  texture.wrapT = wrap(input.sampling.wrapT)
  texture.minFilter = [
    THREE.NearestFilter,
    THREE.LinearFilter,
    THREE.LinearMipmapNearestFilter,
    THREE.LinearMipmapLinearFilter,
    THREE.LinearMipmapLinearFilter,
  ][input.sampling.minFilter] ?? THREE.LinearFilter
  if (quality && input.sampling.minFilter >= 2 && mipmaps.length > 1) {
    texture.minFilter = quality.trilinear || quality.anisotropy > 1 ? THREE.LinearMipmapLinearFilter : THREE.LinearMipmapNearestFilter
  }
  texture.magFilter = input.sampling.magFilter === 0 ? THREE.NearestFilter : THREE.LinearFilter
  texture.anisotropy = quality?.anisotropy ?? input.sampling.anisotropyLevel
  texture.generateMipmaps = false
  texture.flipY = false
  texture.needsUpdate = true
  return texture
}

function textureFromAuthoredCubemap(input: AuthoredTextureInput, colorSpace: string, frame = 0): THREE.CubeTexture {
  if (input.depth !== 1 || frame < 0 || frame >= input.frameCount || [0,1,2,3,4,5].some(face=>!input.faces.includes(face))) {
    throw new RenderingError("UnsupportedFeature", `authored cubemap ${input.logicalPath} requires an unsupported topology`)
  }
  const componentType=input.scalarEncoding==="u8"?THREE.UnsignedByteType:THREE.HalfFloatType
  const data=(plane:AuthoredTextureInput["planes"][number])=>sourceTextureSamples(plane.rgba,input.sourceFormat,input.scalarEncoding)
  const level=(mip:number)=>Object.freeze({images:Object.freeze(Array.from({length:6},(_,face)=>{
    const plane=input.planes.find(value=>value.mip===mip&&value.frame===frame&&value.face===face&&value.slice===0)
    if(!plane)throw new RenderingError("MissingInput",`authored cubemap ${input.logicalPath} has an incomplete mip ${mip}`)
    const texture=new THREE.DataTexture(data(plane),plane.width,plane.height,THREE.RGBAFormat,componentType);texture.colorSpace=colorSpace;texture.flipY=false;texture.generateMipmaps=false;texture.needsUpdate=true;return texture
  }))})
  const base=level(0),texture=new THREE.CubeTexture([...base.images] as any)
  texture.name = `cubemap:${input.logicalPath}:frame=${frame}/${input.frameCount}:${colorSpace}`
  texture.mipmaps=Array.from({length:input.mipCount-1},(_,index)=>level(index+1)) as any
  texture.colorSpace=colorSpace;texture.type=componentType;texture.format=THREE.RGBAFormat;texture.flipY=false;texture.generateMipmaps=false;texture.minFilter=[THREE.NearestFilter,THREE.LinearFilter,THREE.LinearMipmapNearestFilter,THREE.LinearMipmapLinearFilter,THREE.LinearMipmapLinearFilter][input.sampling.minFilter]??THREE.LinearFilter;texture.magFilter=input.sampling.magFilter===0?THREE.NearestFilter:THREE.LinearFilter;texture.anisotropy=input.sampling.anisotropyLevel;texture.needsUpdate=true
  return texture
}

function materialOptions(resolved: RuntimeMaterial, state?: MaterialStateInput): THREE.MeshBasicMaterialParameters {
  const blendFactor = (value: number) => [THREE.ZeroFactor, THREE.OneFactor, THREE.SrcAlphaFactor, THREE.OneMinusSrcAlphaFactor, THREE.DstColorFactor, THREE.SrcColorFactor][value] ?? THREE.OneFactor
  const bias = projectedDecalDepthBias(state?.polygonOffset === 1 ? "decal" : "none")
  const transparent = state?.blendEnabled ?? (resolved.features & 1) !== 0
  return {
    transparent,
    blending: state?.blendEnabled ? THREE.CustomBlending : transparent ? THREE.NormalBlending : THREE.NoBlending,
    blendSrc: state ? blendFactor(state.blendSource) : undefined,
    blendDst: state ? blendFactor(state.blendDestination) : undefined,
    alphaTest: 0,
    side: state?.cull === 1 ? THREE.DoubleSide : worldMaterialSide(resolved.features),
    depthTest: state?.depthTest ?? true,
    depthWrite: state?.depthWrite ?? true,
    depthFunc: state?.depthFunction === 0 ? THREE.LessDepth : THREE.LessEqualDepth,
    polygonOffset: bias.enabled,
    polygonOffsetFactor: bias.slopeScale,
    polygonOffsetUnits: bias.units,
    wireframe: state?.wireframe ?? false,
    fog: sourceMaterialUsesFog(state),
  }
}

function sourceModelSide(facing: Readonly<{ frontFace: "clockwise" | "counter-clockwise"; cullFace: "back" }>): THREE.Side {
  if (facing.cullFace !== "back") throw new RenderingError("MalformedInput", "StudioModel cull face is invalid")
  return facing.frontFace === "clockwise" ? THREE.BackSide : THREE.FrontSide
}

function detailColor(base: any, detail: RuntimeMaterial["detail"], texture?: THREE.Texture): any {
  if (!detail || !texture) return base
  if (detail.blendMode !== 0) throw new RenderingError("UnsupportedFeature", `detail blend mode ${detail.blendMode} is unavailable`)
  const uv = TSL.uv()
  const sample = TSL.texture(texture, TSL.vec2(uv.x.mul(detail.scale[0]), uv.y.mul(detail.scale[1])))
  const tint = TSL.vec3(detail.tint[0], detail.tint[1], detail.tint[2])
  const modulation = TSL.mix(TSL.vec3(1), sample.rgb.mul(tint).mul(2), detail.blendFactor)
  return TSL.vec4(base.rgb.mul(modulation), base.a)
}

function worldBaseColor(resolved: RuntimeMaterial, base: any, second?: THREE.Texture): any {
  if (resolved.shader !== 4) return base
  if (!second) throw new RenderingError("MissingInput", `WorldVertexTransition second texture ${resolved.logicalPath} is unavailable`)
  const blend = TSL.clamp(TSL.attribute("displacementAlpha", "float").div(255), 0, 1)
  return TSL.vec4(TSL.mix(base.rgb, TSL.texture(second, TSL.uv()).rgb, blend), base.a)
}

function worldNodeMaterial(
  resolved: RuntimeMaterial,
  identity: string,
  baseTexture: THREE.Texture | undefined,
  secondTexture: THREE.Texture | undefined,
  detailTexture: THREE.Texture | undefined,
  lightmaps: readonly [THREE.DataTexture, THREE.DataTexture?, THREE.DataTexture?, THREE.DataTexture?],
  directional: THREE.Texture | undefined,
  directionalKind: "normal" | "ssbump" | undefined,
  directionalUvTransform: DirectionalTextureInput["uvTransform"] | undefined,
  exposure: ReturnType<typeof TSL.uniform>,
  waterFogUniforms: SourceWaterFogUniforms,
  environment: SourceLightmappedEnvironmentNode | undefined,
  state?: MaterialStateInput,
): THREE.MeshBasicNodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial(materialOptions(resolved, state))
  const base = detailColor(worldBaseColor(resolved, baseTexture ? TSL.texture(baseTexture, TSL.uv()) : TSL.vec4(TSL.color(debugColor(identity)), 1), secondTexture), resolved.detail, detailTexture)
  const flat = TSL.texture(lightmaps[0], TSL.uv(1)).rgb
  let irradiance = flat
  if (directional && lightmaps[1] && lightmaps[2] && lightmaps[3] && directionalKind && directionalUvTransform) {
    const baseUv = TSL.uv()
    const directionalUv = TSL.vec2(
      baseUv.x
        .mul(directionalUvTransform[0])
        .add(baseUv.y.mul(directionalUvTransform[2]))
        .add(directionalUvTransform[4]),
      baseUv.x
        .mul(directionalUvTransform[1])
        .add(baseUv.y.mul(directionalUvTransform[3]))
        .add(directionalUvTransform[5]),
    )
    const source = environment ? environment.normalNode.rgb : TSL.texture(directional, directionalUv).rgb
    let weights
    if (directionalKind === "ssbump") {
      weights = TSL.max(source, TSL.vec3(0))
    } else {
      const normal = source.mul(2).sub(1)
      const projections = [
        TSL.clamp(TSL.dot(normal, TSL.vec3(0.8164966106414795, 0, 0.5773502588272095)), 0, 1),
        TSL.clamp(TSL.dot(normal, TSL.vec3(-0.40824833512306213, 0.7071067690849304, 0.5773502588272095)), 0, 1),
        TSL.clamp(TSL.dot(normal, TSL.vec3(-0.4082482159137726, -0.7071068286895752, 0.5773502588272095)), 0, 1),
      ]
      const squared = TSL.vec3(
        projections[0]!.mul(projections[0]),
        projections[1]!.mul(projections[1]),
        projections[2]!.mul(projections[2]),
      )
      weights = squared.div(TSL.max(squared.x.add(squared.y).add(squared.z), 1e-12))
    }
    const directionalLight = TSL.texture(lightmaps[1], TSL.uv(1))
      .rgb.mul(weights.x)
      .add(TSL.texture(lightmaps[2], TSL.uv(1)).rgb.mul(weights.y))
      .add(TSL.texture(lightmaps[3], TSL.uv(1)).rgb.mul(weights.z))
    irradiance = TSL.attribute("lightmapKind", "float").greaterThan(1.5).select(directionalLight, flat)
  }
  const diffuse = base.rgb.mul(irradiance)
  const lit = environment ? diffuse.add(environment.specular) : diffuse
  material.colorNode = sourceFragmentColor(
    TSL.vec4(lit.mul(exposure), base.a),
    state,
    waterFogUniforms,
  )
  material.toneMapped = false
  return material
}

function diagnostic(code: SceneDiagnostic["code"], identity: string, detail: string): SceneDiagnostic {
  return Object.freeze({ code, identity, detail })
}

function validateDirectionalInputs(
  inputs: readonly DirectionalTextureInput[],
): Map<string, DirectionalTextureInput> {
  const result = new Map<string, DirectionalTextureInput>()
  for (const input of inputs) {
    const identity = input.material.toLowerCase()
    if (
      !input.material ||
      !input.logicalPath ||
      !HASH.test(input.sha256) ||
      (input.kind !== "normal" && input.kind !== "ssbump") ||
      !Number.isSafeInteger(input.width) ||
      input.width < 1 ||
      input.width > MAX_DIMENSION ||
      !Number.isSafeInteger(input.height) ||
      input.height < 1 ||
      input.height > MAX_DIMENSION ||
      input.authored.logicalPath.toLowerCase() !== input.logicalPath.toLowerCase() ||
      input.authored.sourceSha256 !== input.sha256 ||
      input.authored.width !== input.width || input.authored.height !== input.height ||
      input.uvTransform.length !== 6 ||
      !input.uvTransform.every(Number.isFinite) ||
      result.has(identity)
    ) {
      throw new RenderingError("MalformedInput", "directional texture input is invalid")
    }
    result.set(
      identity,
      Object.freeze({
        ...input,
        uvTransform: Object.freeze([...input.uvTransform]) as DirectionalTextureInput["uvTransform"],
      }),
    )
  }
  return result
}

type DynamicModelLighting = { instance: THREE.Group; meshes?: THREE.Mesh[]; lighting?: SourceModelLightingUniforms; eyes?: Map<number, SourceModelEyeUniforms>; cubemapIdentity?: string | null; environmentShape?: string }
type RetainedDynamicModel = DynamicModelLighting & { model: string; seen: number }
type RetainedViewModel = RetainedDynamicModel & { root: THREE.Group; meshes: THREE.Mesh[] }

class RendererOwner implements Renderer {
  readonly configuration: RenderConfiguration
  readonly sampleCount: 1 | 4
  readonly capabilities = Object.freeze({ sampleCounts: Object.freeze([1, 4] as const), presentationSynchronization: false as const })
  readonly textureQuality: RendererCreateRequest["textureQuality"]
  readonly #canvas: Canvas
  readonly #powerPreference: "low-power" | "high-performance" | undefined
  readonly #serviceAudio: (() => void) | undefined
  readonly #exposure: ExposureController
  #exposureSampler?: SourceExposureSampler
  #lifecycle: RendererLifecycle = "Initializing"
  #deviceGeneration = 0
  #sceneGeneration = 0
  #backend!: Backend
  #instrumentation?: RendererFrameInstrumentation
  #renderOwnerProbe?: RenderOwnerProbe
  #framePresentation?: WebGpuFramePresentation<THREE.RenderTarget>
  #pendingDepthReset = false
  #timestampFrame = 0
  #timestampPending = false
  #scene = new THREE.Scene()
  readonly #viewFogUniforms = createSourceViewFogUniforms()
  #waterClipPlane = new THREE.Plane(new THREE.Vector3(0, 0, 0), 0)
  #waterClipping = createWorldClipGroup(this.#waterClipPlane)
  #world = new THREE.Group()
  #effects = new THREE.Group()
  #particles = new THREE.Group()
  #skyParticles = new THREE.Group()
  #particleVisibility = new ParticleVisibilityQueries()
  #viewModels = new THREE.Group()
  #cloakFramebuffer = new SourceCloakFramebuffer()
  #camera = new THREE.PerspectiveCamera(75, 1, 1, 32_768)
  #viewCamera = new THREE.PerspectiveCamera(41.114, 1, 1, 32_768)
  readonly #underwaterOverlay = new THREE.QuadMesh()
  #hudMaterials?: SourceHudMaterials
  #modelPanelScene = new THREE.Scene()
  #modelPanelExposure = TSL.uniform(1, "float")
  #modelPanelCamera = new THREE.PerspectiveCamera(25, 1, 7, 1000)
  #modelPanelInstances = new Map<string, { model: string; instance: THREE.Group; meshes?: THREE.Mesh[] }>()
  readonly #retainedModelPanels = new RetainedModelCache<{ model: string; instance: THREE.Group; meshes?: THREE.Mesh[] }>(32, retained => this.#disposeDynamicInstance(retained.instance))
  readonly #preparedModelInstances = new RetainedModelCache<{ model: string; instance: THREE.Group; meshes?: THREE.Mesh[] }>(MAX_PREPARED_MODEL_VARIANTS, retained => this.#disposeDynamicInstance(retained.instance))
  #viewModelInstances = new Map<number, RetainedViewModel>()
  // Detached occurrences share a bounded, generation-owned residency budget.
  // Never substitute one actor's mutable materials or skeleton for another's.
  readonly #retainedModels = new RetainedModelCache<RetainedDynamicModel | RetainedViewModel>(32, retained => this.#disposeDynamicInstance("root" in retained ? retained.root : retained.instance))
  #dynamicModelInstances = new Map<number, RetainedDynamicModel>()
  #dynamicBrushInstances = new Map<number, { model: number; appearance: string; instance: THREE.Group; seen: number }>()
  #dynamicRevision = 0
  #particleBatchMeshes: { key: string; capacity: number; mesh: THREE.Mesh }[] = []
  readonly #panelParticlePools = new Map<string, { group: THREE.Group; meshes: { key: string; capacity: number; mesh: THREE.Mesh }[]; ranges: MutableParticleBatchRange[] }>()
  readonly #particleBatchRanges: MutableParticleBatchRange[] = []
  readonly #particleAttributeUpdates = createParticleAttributeUpdates()
  readonly #cameraDirection = new THREE.Vector3()
  readonly #cameraTarget = new THREE.Vector3()
  readonly #projectionPoint = new THREE.Vector3()
  readonly #fadeUp = new THREE.Vector3()
  readonly #fadeFirst = new THREE.Vector3()
  readonly #fadeSecond = new THREE.Vector3()
  readonly #staticPropFrustum = new THREE.Frustum()
  readonly #staticPropProjection = new THREE.Matrix4()
  readonly #staticPropSphere = new THREE.Sphere()
  readonly #modelVisibilityCamera = new THREE.PerspectiveCamera()
  readonly #modelVisibilityFrustums = new Map<string, THREE.Frustum>()
  readonly #modelVisibilityProjection = new THREE.Matrix4()
  readonly #modelVisibilityTransform = new THREE.Group()
  readonly #modelVisibilitySphere = new THREE.Sphere()
  readonly #modelVisibilityMatrix = new THREE.Matrix4()
  #modelVisibilityCameraIdentity = ""
  readonly #fogCache = new WeakMap<FogInput, THREE.Fog>()
  #visibleStaticIndices: [Set<number>, Set<number>] = [new Set(), new Set()]
  #nextVisibleStaticIndices: [Set<number>, Set<number>] = [new Set(), new Set()]
  #visibleStaticSources: [readonly number[], readonly number[]] = [Object.freeze([]), Object.freeze([])]
  #staticVisibilityInputs: [Readonly<{ scene: SceneResources; leaves: readonly number[]; camera: readonly number[]; frustumCull: boolean }> | undefined, Readonly<{ scene: SceneResources; leaves: readonly number[]; camera: readonly number[]; frustumCull: boolean }> | undefined] = [undefined, undefined]
  #visibleWorldMarkFaces = new Set<number>()
  #visibleProjectedMarkCount = 0
  #particleBatchCount=0
  readonly #uploadEvidence?: {
    poseAttributes: number
    unchangedPoseAttributes: number
    poseUploadBytes: number
    particleUploadBytes: number
    particleUploadWrites: number
    particleFullUploadBytes: number
    particleGeometries: number
    retainedParticleBatches: number
  }
  #stagedDynamic?:Readonly<{
    particles:Frame["particles"]
    models:Frame["models"]
    brushModels:Frame["brushModels"]
    studioModels:Frame["studioModels"]
    viewModelDepthRange:readonly[number,number]|undefined
  }>
  #worldVisibilitySurfaces?: Uint32Array
  #worldVisibilityIdentity?: string
  #skyWorldVisibilitySurfaces?: Uint32Array
  #skyWorldVisibilityIdentity?: string
  #restoreOrderedBundles?: () => void
  #restoreNodeBuilderInstrumentation?: () => void
  #renderObjectLifetime?: ReturnType<typeof installRenderObjectLifetime>
  #restoreGeometryAttributeLifetime?: () => void
  #restoreTextureBindingLifetime?: () => void
  #restoreBufferNames?: () => void
  #uploadBatch?: WebGpuUploadBatch
  #active?: SceneResources
  #panelAssets?: ModelAssets
  #renderBusy = false
  #loadOrdinal = 0
  #suspended = false
  #viewportWidth = 0
  #viewportHeight = 0
  #cssWidth = 0
  #cssHeight = 0
  #devicePixelRatio = 1
  #attachmentGeneration = 0
  #pacingHandle?: number
  #pacingCallback?: FramePacingCallback
  readonly #pacing = new FramePacingController({ now: () => performance.now() }, 256)

  constructor(request: RendererCreateRequest) {
    this.configuration = validateRenderConfiguration(request.configuration)
    this.sampleCount = request.sampleCount ?? 4
    if (this.sampleCount !== 1 && this.sampleCount !== 4) throw new RenderingError("UnsupportedFeature", "WebGPU sample count is unsupported")
    this.textureQuality = request.textureQuality
    if (this.textureQuality && (!Number.isSafeInteger(this.textureQuality.mipOffset)
      || ![1, 2, 4, 8, 16].includes(this.textureQuality.anisotropy))) {
      throw new RenderingError("MalformedInput", "WebGPU texture quality is invalid")
    }
    this.#canvas = request.canvas
    this.#powerPreference = request.powerPreference
    this.#serviceAudio = request.serviceAudio
    const profile = (globalThis as typeof globalThis & { __playsrcProfile?: Record<string, unknown> }).__playsrcProfile
    if (profile) {
      this.#uploadEvidence = {
        poseAttributes: 0,
        unchangedPoseAttributes: 0,
        poseUploadBytes: 0,
        particleUploadBytes: 0,
        particleUploadWrites: 0,
        particleFullUploadBytes: 0,
        particleGeometries: 0,
        retainedParticleBatches: 0,
      }
      profile.modelParticleUploads = this.#uploadEvidence
    }
    this.#exposure = new ExposureController(this.configuration.exposure)
    this.#scene.matrixAutoUpdate = false
    ;(this.#scene as THREE.Scene & { fogNode?: unknown }).fogNode = sourceViewFogNode(this.#viewFogUniforms)
    this.#waterClipping.matrixAutoUpdate = false
    this.#world.matrixAutoUpdate = false
    this.#scene.background = null
    this.#waterClipping.add(this.#world, this.#effects, this.#particles)
    this.#waterClipping.add(this.#skyParticles)
    this.#skyParticles.visible = false
    this.#scene.add(this.#waterClipping, this.#camera, this.#viewCamera)
    this.#viewCamera.add(this.#viewModels)
    this.#viewCamera.layers.set(1)
    this.#viewModels.layers.set(1)
    this.#camera.up.set(0, 0, 1)
    this.#viewCamera.up.set(0, 0, 1)
    this.#modelPanelCamera.up.set(0, 0, 1)
    this.#modelVisibilityCamera.up.set(0, 0, 1)
    this.#modelPanelCamera.position.set(0, 0, 0)
    this.#modelPanelCamera.lookAt(1, 0, 0)
    this.#modelPanelScene.background = new THREE.Color(0x000000)
  }

  get lifecycle(): RendererLifecycle {
    return this.#lifecycle
  }
  get deviceGeneration(): number {
    return this.#deviceGeneration
  }
  get sceneGeneration(): number {
    return this.#sceneGeneration
  }

  modelVisible(
    model: string,
    skin: number,
    position: readonly [number, number, number],
    angles: readonly [number, number, number],
    camera: Camera,
    views: readonly WaterFramePass[],
  ): boolean {
    const template = this.#active?.modelTemplates.get(modelKey(model, skin))
    if (!template) throw new RenderingError("MissingInput", `model visibility template ${model}#skin=${skin} is unavailable`)
    const baseIdentity = `${camera.position.join(",")}:${camera.yawDegrees}:${camera.pitchDegrees}:${camera.verticalFovDegrees}:${camera.near}:${camera.far}:${this.#camera.aspect}`
    if (this.#modelVisibilityCameraIdentity !== baseIdentity) {
      this.#modelVisibilityFrustums.clear()
      this.#modelVisibilityCameraIdentity = baseIdentity
    }
    sourceTransform(this.#modelVisibilityTransform, position, angles)
    this.#modelVisibilityTransform.updateMatrixWorld(true)
    const intersects = (position: readonly [number, number, number], yawDegrees: number, pitchDegrees: number): boolean => {
      const identity = `${position.join(",")}:${yawDegrees}:${pitchDegrees}`
      let frustum = this.#modelVisibilityFrustums.get(identity)
      if (frustum) {
        return modelIntersectsViewFrustum(frustum, template, this.#modelVisibilityTransform.matrixWorld, this.#modelVisibilitySphere, this.#modelVisibilityMatrix)
      }
      const view = this.#modelVisibilityCamera
      view.position.set(...position)
      view.fov = camera.verticalFovDegrees
      view.near = camera.near
      view.far = camera.far
      view.aspect = this.#camera.aspect
      view.updateProjectionMatrix()
      const yaw = THREE.MathUtils.degToRad(yawDegrees), pitch = THREE.MathUtils.degToRad(pitchDegrees)
      this.#cameraTarget.set(
        position[0] + Math.cos(pitch) * Math.cos(yaw),
        position[1] + Math.cos(pitch) * Math.sin(yaw),
        position[2] - Math.sin(pitch),
      )
      view.lookAt(this.#cameraTarget)
      view.updateMatrixWorld()
      this.#modelVisibilityProjection.multiplyMatrices(view.projectionMatrix, view.matrixWorldInverse)
      frustum = new THREE.Frustum().setFromProjectionMatrix(this.#modelVisibilityProjection)
      this.#modelVisibilityFrustums.set(identity, frustum)
      return modelIntersectsViewFrustum(frustum, template, this.#modelVisibilityTransform.matrixWorld, this.#modelVisibilitySphere, this.#modelVisibilityMatrix)
    }
    if (intersects(camera.position, camera.yawDegrees, camera.pitchDegrees)) return true
    return views.some((view) => view.kind !== "main" && view.drawEntities && intersects(view.origin, view.angles[1], view.angles[0]))
  }

  completeFrameProfile(): RendererFrameProfile | undefined {
    if (this.#framePresentation?.begun) this.#pass("frame-output", () => this.#framePresentation!.finish())
    this.#particleVisibility.flushReads()
    for (const view of this.#active?.legacyVisuals ?? []) view.finishFrame()
    this.#pendingDepthReset = false
    const result = this.#instrumentation?.complete()
    this.#renderOwnerProbe?.complete()
    const profile = browserFrameProfiler()
    if (profile?.capabilities.timestampQuery && ++this.#timestampFrame % 32 === 0 && !this.#timestampPending) {
      this.#timestampPending = true
      const frame = this.#timestampFrame
      this.#pass("gpu-timestamp-resolve", () => {
        void this.#backend.resolveTimestampsAsync("render").then((milliseconds: unknown) => {
          if (result && typeof milliseconds === "number" && Number.isFinite(milliseconds) && milliseconds > 0) {
            profile.gpuTimestamps?.push({ frame, milliseconds })
          }
        }).catch((error: unknown) => {
          profile.losses.push({ kind: "timestamp", at: performance.now(), message: error instanceof Error ? error.message : String(error) })
        }).finally(() => { this.#timestampPending = false })
      })
    }
    return result
  }

  #pass<T>(identity: string, callback: () => T): T {
    return this.#instrumentation ? this.#instrumentation.pass(identity, callback) : callback()
  }

  #drawScene(identity: string, scene: THREE.Scene, camera: THREE.Camera): void {
    this.#particleVisibility.beginPass(identity, this.#backend, camera)
    try { this.#pass(identity, () => this.#backend.render(scene, camera)) }
    finally { this.#particleVisibility.endPass() }
  }

  #drawPass(identity: string, scene: THREE.Scene, camera: THREE.Camera): void {
    this.#serviceAudio?.()
    if (!this.#pendingDepthReset) {
      this.#drawScene(identity, scene, camera)
      return
    }
    const autoClear = this.#backend.autoClear
    const autoClearColor = this.#backend.autoClearColor
    const autoClearDepth = this.#backend.autoClearDepth
    const autoClearStencil = this.#backend.autoClearStencil
    this.#pendingDepthReset = false
    this.#backend.autoClear = true
    this.#backend.autoClearColor = false
    this.#backend.autoClearDepth = true
    this.#backend.autoClearStencil = false
    try {
      this.#drawScene(identity, scene, camera)
    } finally {
      this.#backend.autoClear = autoClear
      this.#backend.autoClearColor = autoClearColor
      this.#backend.autoClearDepth = autoClearDepth
      this.#backend.autoClearStencil = autoClearStencil
    }
  }

  #resetDepth(identity: string): void {
    this.#pass(identity, () => {
      if (this.#framePresentation?.active) this.#pendingDepthReset = true
      else this.#backend.clearDepth()
    })
  }

  captureGeometryEvidence(camera: Camera, ownership: "main" | "sky3d" = "main"): GeometryEvidence {
    if (!this.#active || this.#lifecycle !== "Ready") throw new RenderingError("InvalidState", "renderer geometry evidence is unavailable")
    this.#setCamera(camera)
    this.#world.updateMatrixWorld(true)
    this.#effects.updateMatrixWorld(true)
    const raycaster = new THREE.Raycaster()
    raycaster.near = camera.near
    raycaster.far = camera.far
    const batches = ownership === "sky3d" ? this.#active.skyWorldBatches : this.#active.worldBatches
    const meshes: THREE.Mesh[] = []
    const worldMeshes = new Map<THREE.Mesh, number>()
    const propMeshes = new Map<THREE.Mesh, Readonly<{ family: "static-prop" | "dynamic-prop"; identity: number }>>()
    for (const [index, batch] of batches.entries()) {
      if (!batch.mesh.visible) continue
      meshes.push(batch.mesh)
      worldMeshes.set(batch.mesh, index)
    }
    const addProp = (object: THREE.Group, family: "static-prop" | "dynamic-prop", identity: number): void => {
      if (!object.visible) return
      object.traverseVisible((child) => {
        if (!(child instanceof THREE.Mesh)) return
        const materials = Array.isArray(child.material) ? child.material : [child.material]
        const state = this.#active!.materialStates.get(String(child.userData.materialIdentity).toLowerCase())
        if (materials.some((material) => material.transparent) || state?.alphaTest) return
        meshes.push(child)
        propMeshes.set(child, Object.freeze({ family, identity }))
      })
    }
    const selectedOwnership = ownership === "sky3d" ? 1 : 0
    const batchedProps = new Map<THREE.Mesh, StaticPropBatch>()
    for (const { ownership: batchOwnership, batch } of this.#active.staticPropBatches) {
      if (batchOwnership !== selectedOwnership || !batch.mesh.visible) continue
      const state = this.#active.materialStates.get(String(batch.mesh.userData.materialIdentity).toLowerCase())
      if (state?.alphaTest) continue
      meshes.push(batch.mesh)
      batchedProps.set(batch.mesh, batch)
    }
    for (const prop of this.#active.staticPropInstances) {
      if (prop.ownership === selectedOwnership) addProp(prop.object, "static-prop", prop.source)
    }
    if (ownership === "main") {
      for (const [identity, retained] of this.#dynamicBrushInstances) addProp(retained.instance, "dynamic-prop", identity)
      for (const [identity, object] of this.#active.modelOccurrenceInstances) addProp(object, "dynamic-prop", identity)
      for (const [identity, retained] of this.#dynamicModelInstances) addProp(retained.instance, "dynamic-prop", identity)
    }
    const displacements = new Set(this.#active.map.displacements.map((displacement) => displacement.face))
    const water = new Set(this.#active.waterMeshes.map((resource) => resource.mesh))
    const samples = [] as GeometryEvidence["samples"][number][]
    for (const y of [-0.8, -0.4, 0, 0.4, 0.8]) {
      for (const x of [-0.8, -0.4, 0, 0.4, 0.8]) {
        this.#serviceAudio?.()
        raycaster.setFromCamera(new THREE.Vector2(x, y), this.#camera)
        const intersection = raycaster.intersectObjects(meshes, false)[0]
        if (!intersection || intersection.faceIndex === undefined) {
          samples.push(Object.freeze({ x, y, disposition: "background", family: null, depth: null, primitive: null, object: null, material: null, lightmap: null }))
          continue
        }
        const mesh = intersection.object as THREE.Mesh
        const staticBatch = batchedProps.get(mesh)
        const source = staticBatch?.sourceAtFace(intersection.faceIndex)
        const prop = source === undefined ? propMeshes.get(mesh) : { family: "static-prop" as const, identity: source }
        if (prop) {
          samples.push(Object.freeze({
            x,
            y,
            disposition: prop.family,
            family: prop.family,
            depth: intersection.distance,
            primitive: Number.isSafeInteger(mesh.userData.sourcePrimitive) ? Number(mesh.userData.sourcePrimitive) : null,
            object: prop.identity,
            material: String(mesh.userData.materialIdentity),
            lightmap: null,
          }))
          continue
        }
        const object = worldMeshes.get(mesh)
        if (object === undefined) throw new RenderingError("InvalidState", "visible world object identity is unavailable")
        const batch = batches[object]!
        const current = batch.index.array as Uint32Array
        const at = intersection.faceIndex * 3
        let sourceTriangle = -1
        for (let triangle = 0; triangle < batch.sourceIndices.length / 3; triangle += 1) {
          const source = triangle * 3
          if (batch.sourceIndices[source] === current[at] && batch.sourceIndices[source + 1] === current[at + 1] && batch.sourceIndices[source + 2] === current[at + 2]) {
            sourceTriangle = triangle
            break
          }
        }
        if (sourceTriangle < 0) throw new RenderingError("InvalidState", "visible primitive identity is unavailable")
        const atlas = this.#active.map.lightmap!
        const uv = intersection.uv1
        const lightmap = uv ? (() => {
          const sampleX = Math.max(0, Math.min(atlas.width - 1, Math.floor(uv.x * atlas.width)))
          const sampleY = Math.max(0, Math.min(atlas.height - 1, Math.floor(uv.y * atlas.height)))
          const offset = (sampleY * atlas.width + sampleX) * 4
          return Object.freeze([atlas.flat[offset]!, atlas.flat[offset + 1]!, atlas.flat[offset + 2]!]) as readonly [number, number, number]
        })() : null
        samples.push(Object.freeze({
          x,
          y,
          disposition: "main-world",
          family: water.has(batch.mesh) ? "water" : displacements.has(batch.faces[sourceTriangle]!) ? "displacement" : "world",
          depth: intersection.distance,
          primitive: batch.faces[sourceTriangle]!,
          object,
          material: String(batch.mesh.userData.materialIdentity),
          lightmap,
        }))
      }
    }
    return Object.freeze({ sceneGeneration: this.#sceneGeneration, samples: Object.freeze(samples) })
  }

  #modelEvidenceOccluders(): THREE.Mesh[] {
    const meshes: THREE.Mesh[] = []
    const add = (object: THREE.Object3D): void => {
      object.traverseVisible(child => {
        if (!(child instanceof THREE.Mesh)) return
        const materials = Array.isArray(child.material) ? child.material : [child.material]
        if (materials.some(material => material.visible && material.depthWrite && !material.transparent && !material.alphaTest)) meshes.push(child)
      })
    }
    for (const batch of this.#active!.worldBatches) add(batch.mesh)
    for (const { ownership, batch } of this.#active!.staticPropBatches) if (ownership === 0) add(batch.mesh)
    for (const prop of this.#active!.staticPropInstances) if (prop.ownership === 0) add(prop.object)
    for (const [identity, object] of this.#active!.modelOccurrenceInstances) add(this.#dynamicModelInstances.get(identity)?.instance ?? object)
    for (const retained of this.#dynamicBrushInstances.values()) add(retained.instance)
    return meshes
  }

  captureViewModelEvidence(camera: Camera): ModelGeometryEvidence {
    if (!this.#active || this.#lifecycle !== "Ready") {
      throw new RenderingError("InvalidState", "renderer viewmodel evidence is unavailable")
    }
    this.#setCamera(camera)
    this.#scene.updateMatrixWorld(true)
    const viewRay = new THREE.Raycaster()
    viewRay.layers.set(1)
    const worldRay = new THREE.Raycaster()
    const world = this.#modelEvidenceOccluders()
    const samples: ModelGeometryEvidence["samples"][number][] = []
    for (const y of [-0.8, -0.6, -0.4, -0.2, 0, 0.2, 0.4, 0.6]) {
      for (const x of [0.1, 0.25, 0.4, 0.55, 0.7, 0.85]) {
        viewRay.setFromCamera(new THREE.Vector2(x, y), this.#viewCamera)
        const hit = viewRay.intersectObjects(this.#viewModels.children, true).find(hit => visibleModelIntersection(hit, this.#viewCamera))
        if (!hit || samples.length >= 12) continue
        let owner: THREE.Object3D | null = hit.object
        while (owner && !Number.isSafeInteger(owner.userData.identity)) owner = owner.parent
        if (!owner) throw new RenderingError("IdentityMismatch", "viewmodel evidence has no authored owner")
        let worldDepth: number | null = null
        if (samples.length < 2) {
          worldRay.setFromCamera(new THREE.Vector2(x, y), this.#camera)
          worldDepth = worldRay.intersectObjects(world, false)[0]?.distance ?? null
        }
        samples.push(Object.freeze({
          x,
          y,
          identity: owner.userData.identity,
          material: String(hit.object.userData.materialIdentity),
          modelDepth: hit.distance,
          worldDepth,
          ...modelNormalEvidence(hit),
        }))
      }
    }
    return Object.freeze({ sceneGeneration: this.#sceneGeneration, samples: Object.freeze(samples) })
  }

  captureWorldModelEvidence(camera: Camera): ModelGeometryEvidence {
    if (!this.#active || this.#lifecycle !== "Ready") {
      throw new RenderingError("InvalidState", "renderer player-model evidence is unavailable")
    }
    if (this.#dynamicModelInstances.size === 0) {
      return Object.freeze({ sceneGeneration: this.#sceneGeneration, samples: Object.freeze([]) })
    }
    this.#setCamera(camera)
    this.#scene.updateMatrixWorld(true)
    const ray = new THREE.Raycaster()
    const world = this.#modelEvidenceOccluders()
    const instances = [...this.#dynamicModelInstances.values()].map((instance) => instance.instance).filter(instance => instance.visible)
    const samples: ModelGeometryEvidence["samples"][number][] = []
    for (const y of [-0.45, -0.3, -0.15, 0, 0.15, 0.3, 0.45]) {
      for (const x of [-0.4, -0.25, -0.1, 0, 0.1, 0.25, 0.4]) {
        if (samples.length >= 12) break
        ray.setFromCamera(new THREE.Vector2(x, y), this.#camera)
        const hit = ray.intersectObjects(instances, true).find(hit => visibleModelIntersection(hit, this.#camera))
        if (!hit) continue
        let owner: THREE.Object3D | null = hit.object
        while (owner && !Number.isSafeInteger(owner.userData.identity)) owner = owner.parent
        if (!owner) continue
        const worldDepth = ray.intersectObjects(world, false)[0]?.distance ?? null
        if (worldDepth !== null && hit.distance > worldDepth + 0.01) continue
        samples.push(Object.freeze({
          x,
          y,
          identity: owner.userData.identity,
          material: String(hit.object.userData.materialIdentity),
          modelDepth: hit.distance,
          worldDepth,
          ...modelNormalEvidence(hit),
        }))
      }
    }
    return Object.freeze({ sceneGeneration: this.#sceneGeneration, samples: Object.freeze(samples) })
  }

  async captureWaterTargetEvidence(x: number, y: number): Promise<WaterTargetEvidence> {
    const scene = this.#active
    if (!scene || this.#lifecycle !== "Ready") {
      throw new RenderingError("InvalidState", "renderer Water target evidence is unavailable")
    }
    if (
      !Number.isSafeInteger(x) || !Number.isSafeInteger(y)
      || x < 0 || y < 0
      || !scene.reflectionTarget || !scene.refractionTarget
      || x >= scene.reflectionTarget.width || y >= scene.reflectionTarget.height
    ) {
      throw new RenderingError("MalformedInput", "Water target evidence coordinates are invalid")
    }
    const [reflection, refraction] = await Promise.all([
      this.#backend.readRenderTargetPixelsAsync(scene.reflectionTarget, x, y, 1, 1),
      this.#backend.readRenderTargetPixelsAsync(scene.refractionTarget, x, y, 1, 1),
    ])
    if (reflection.length !== 4 || refraction.length !== 4) {
      throw new RenderingError("CaptureFailed", "Water target evidence does not contain exact RGBA pixels")
    }
    return Object.freeze({
      x,
      y,
      reflection: Object.freeze(Array.from(reflection)) as readonly [number, number, number, number],
      refraction: Object.freeze(Array.from(refraction)) as readonly [number, number, number, number],
    })
  }

  async initialize(): Promise<this> {
    const gpu=(globalThis.navigator as WebGpuNavigator|undefined)?.gpu
    if (!gpu || !this.#canvas || typeof this.#canvas.getContext !== "function") {
      throw new RenderingError("UnsupportedEnvironment", "WebGPU canvas is unavailable")
    }
    if (gpu.getPreferredCanvasFormat() !== this.configuration.canvasFormat) {
      throw new RenderingError("UnsupportedFeature", "preferred WebGPU canvas format differs from the required format")
    }
    this.#backend = await this.#createBackend()
    this.#particleVisibility.attach(this.#backend)
    this.#deviceGeneration = 1
    this.#lifecycle = "Ready"
    return this
  }

  async #createBackend(): Promise<Backend> {
    const profiler = browserFrameProfiler()
    const gpu = (globalThis.navigator as any).gpu
    const core = await requestCoreWebGpuDevice(gpu, this.#powerPreference)
    if (profiler) {
      ;(profiler.capabilities as any).adapter = {
        vendor: core.adapter.info?.vendor ?? null,
        architecture: core.adapter.info?.architecture ?? null,
        device: core.adapter.info?.device ?? null,
        description: core.adapter.info?.description ?? null,
        fallback: core.adapter.info?.isFallbackAdapter ?? null,
        core: core.features.includes("core-features-and-limits"),
        textureCompressionBc: core.features.includes("texture-compression-bc"),
        features: core.features,
        limits: {
          maxTextureDimension2D: core.adapter.limits?.maxTextureDimension2D ?? null,
          maxBufferSize: core.adapter.limits?.maxBufferSize ?? null,
        },
      }
    }
    const backend = new THREE.WebGPURenderer({
      canvas: this.#canvas,
      device: core.device,
      antialias: this.sampleCount === 4,
      alpha: this.configuration.alphaMode === "premultiplied",
      powerPreference: this.#powerPreference,
      ...(profiler ? { trackTimestamp: true } : {}),
    }) as Backend
    backend.onDeviceLost = (info) => {
      profiler?.losses.push({ kind: "device", at: performance.now(), message: String((info as { message?: unknown })?.message ?? "WebGPU device was lost") })
      void this.#recover()
    }
    let context: Backend["backend"]["context"]
    try {
      await backend.init()
      if (this.#serviceAudio) installRenderService(backend, this.#serviceAudio)
      if (!backend.backend.isWebGPUBackend) throw new Error("fallback backend")
      this.#renderObjectLifetime = installRenderObjectLifetime((backend as any)._objects)
      this.#restoreGeometryAttributeLifetime = installGeometryAttributeLifetime((backend as any)._geometries)
      this.#restoreTextureBindingLifetime = installTextureBindingLifetime((backend as any)._textures, backend.backend as any)
      const diagnostics = (globalThis as any).__playsrcProfile
      if (diagnostics) diagnostics.rendererOwnership = () => ({
        scene: this.#sceneGeneration,
        resources: this.#active?.disposables.snapshot().resources ?? 0,
        templates: this.#active?.modelTemplates.size ?? 0,
        modelSamples: this.#active?.modelBaseSamples.size ?? 0,
        worldGraphs: this.#active?.modelLightingGraphs.size ?? 0,
        panelGraphs: this.#active?.modelPanelLightingGraphs.size ?? 0,
        prepared: this.#preparedModelInstances.size,
        parkedPanels: this.#retainedModelPanels.size,
        parkedModels: this.#retainedModels.size,
        activePanels: this.#modelPanelInstances.size,
        draws: this.#renderObjectLifetime?.size ?? 0,
        nodeStates: (this.#backend as any)?._nodes?.nodeBuilderCache?.size ?? 0,
        ...Object.fromEntries(Object.entries((this.#backend as any)?.info?.memory ?? {}).filter(([, value]) => typeof value === "number")),
      })
      this.#restoreBufferNames = installWebGpuBufferNames(backend.backend as unknown as BufferNamingBackend)
      if (profiler) {
        this.#instrumentation = new RendererFrameInstrumentation(
          backend.info as unknown as ConstructorParameters<typeof RendererFrameInstrumentation>[0],
          profiler,
          (backend.backend.device as { features?: { has(feature: string): boolean } } | undefined)?.features,
        )
        this.#restoreNodeBuilderInstrumentation = installNodeBuilderInstrumentation(
          (backend as unknown as { _nodes: { _createNodeBuilder: (...arguments_: any[]) => any } })._nodes,
          profiler,
        )
        if (profiler.renderOwnerPlan === RENDER_OWNER_PLAN && (profiler.renderOwners?.length ?? 0) < 4) {
          this.#renderOwnerProbe = new RenderOwnerProbe(backend, profiler)
          ;(profiler.renderOwners ??= []).push(this.#renderOwnerProbe.evidence)
        }
      }
      this.#restoreOrderedBundles = installOrderedWebGpuBundles(backend.backend as unknown as OrderedBundleBackend)
      this.#framePresentation = new WebGpuFramePresentation(
        backend as unknown as FramePresentationBackend<THREE.RenderTarget>,
        backend.backend.device?.queue,
      )
      this.#uploadBatch = new WebGpuUploadBatch(backend.backend as unknown as UploadBatchBackend)
      this.#camera.coordinateSystem = backend.coordinateSystem
      this.#viewCamera.coordinateSystem = backend.coordinateSystem
      this.#modelPanelCamera.coordinateSystem = backend.coordinateSystem
      this.#camera.updateProjectionMatrix()
      this.#viewCamera.updateProjectionMatrix()
      this.#modelPanelCamera.updateProjectionMatrix()
      backend.outputColorSpace = THREE.SRGBColorSpace
      backend.toneMapping = THREE.NoToneMapping
      context = backend.backend.context
      const actual = context?.getConfiguration?.()
      if (actual) {
        if (
          actual.format !== this.configuration.canvasFormat ||
          actual.alphaMode !== this.configuration.alphaMode ||
          (actual.colorSpace ?? "srgb") !== this.configuration.outputColorSpace
        ) {
          throw new Error("canvas configuration mismatch")
        }
      }
      if (this.configuration.lightingProfile === "hdr" && backend.backend.device && context) {
        this.#exposureSampler = new SourceExposureSampler(
          backend.backend.device,
          context,
          this.configuration.canvasFormat,
        )
      }
      return backend
    } catch (error) {
      this.#uploadBatch?.dispose()
      this.#uploadBatch = undefined
      this.#framePresentation?.dispose()
      this.#framePresentation = undefined
      this.#restoreOrderedBundles?.()
      this.#restoreOrderedBundles = undefined
      this.#restoreNodeBuilderInstrumentation?.()
      this.#restoreNodeBuilderInstrumentation = undefined
      this.#renderOwnerProbe?.dispose()
      this.#renderOwnerProbe = undefined
      this.#renderObjectLifetime?.restore()
      this.#renderObjectLifetime = undefined
      this.#restoreGeometryAttributeLifetime?.()
      this.#restoreGeometryAttributeLifetime = undefined
      this.#restoreTextureBindingLifetime?.()
      this.#restoreTextureBindingLifetime = undefined
      this.#restoreBufferNames?.()
      this.#restoreBufferNames = undefined
      disposeWebGpuBackend(backend)
      try {
        context?.unconfigure()
      } catch {
        /* already unconfigured */
      }
      throw new RenderingError("UnsupportedEnvironment", `WebGPU renderer initialization failed: ${String(error)}`)
    }
  }

  async admitModels(request: ModelAdmissionRequest): Promise<void> {
    this.#requireReady()
    if (this.#renderBusy) throw new RenderingError("InvalidState", "model admission overlaps rendering")
    if (request.models.length > 128) throw new RenderingError("BoundExceeded", "model admission count")
    const active = this.#active
    const previousPanel = this.#panelAssets
    const disposables = active?.disposables ?? new OwnedResourceGeneration(this.#deviceGeneration, Math.max(1, this.#sceneGeneration))
    const residency = active?.textureResidency ?? new SharedTextureResidency<THREE.Texture>(disposables, 4, () => this.#backend.backend.device?.queue.onSubmittedWorkDone() ?? Promise.resolve())
    const materials = new Map([...active?.loadRequest.modelMaterials ?? [], ...request.modelMaterials])
    const textures = new Map([...active?.loadRequest.authoredTextures ?? [], ...request.authoredTextures])
    const states = new Map([...active?.materialStates ?? [], ...request.materialStates])
    const facing = new Map([...active?.loadRequest.modelFacing ?? [], ...request.modelFacing])
    const templates = new Map<string, THREE.Group>()
    const lightingTextures = new Map(active?.modelLightingTextures ?? [])
    const animations = new Map<string, ModelPanelMaterialAnimation[]>([...active?.modelPanelMaterialAnimations ?? []].map(([key, value]) => [key, [...value]]))
    const retain = (input: AuthoredTextureInput, space: string, frame = 0) => residency.retain(`${input.sourceSha256}:${input.sourceFormat ?? "rgba"}:${input.scalarEncoding}:${space}:${frame}`, () => textureFromAuthored(input, space, frame, this.textureQuality))
    const texture = (identity: string, role: number, kind: "material" | "model" = "material") => {
      const binding = materials.get(identity.toLowerCase())?.bindings.find((value) => value.kind === kind && value.role === role)
      if (!binding) return undefined
      const input = textures.get(binding.logicalPath.toLowerCase())
      if (!input || binding.colorRead === "format-dependent") throw new RenderingError("MissingInput", `model texture ${binding.logicalPath}`)
      return { input, texture: retain(input, binding.colorRead === "srgb" ? THREE.SRGBColorSpace : THREE.NoColorSpace) }
    }
    const cubemap = (identity: string) => {
      const binding = materials.get(identity.toLowerCase())?.bindings.find((value) => value.kind === "material" && value.role === 12)
      if (!binding) return active?.modelCubemap(identity)
      const input = textures.get(binding.logicalPath.toLowerCase())
      if (!input || binding.colorRead === "format-dependent") throw new RenderingError("MissingInput", `model cubemap ${binding.logicalPath}`)
      const space = binding.colorRead === "srgb" ? THREE.SRGBColorSpace : THREE.NoColorSpace
      const value = residency.retain(`cubemap:${input.sourceSha256}:${space}`, () => textureFromAuthoredCubemap(input, space))
      if (!(value instanceof THREE.CubeTexture)) throw new RenderingError("IdentityMismatch", binding.logicalPath)
      return value
    }
    const waterFog = active?.waterFogUniforms ?? createSourceWaterFogUniforms()
    const particleTextures = active?.particleTextures ?? new Map<string, THREE.Texture>()
    const particleBatchMaterials = active?.particleBatchMaterials ?? new Map<string, THREE.MeshBasicNodeMaterial>()
    const particleDepth = active?.particleDepth ?? disposables.add(new SourceParticleDepth(this.#backend.backend))
    const particlePipelineMeshes = active?.particlePipelineMeshes ?? new THREE.Group()
    const models = request.models.filter((model) => !active?.modelTemplates.has(model.logicalPath))
    try {
      for (const model of models) {
        if (!facing.has(model.logicalPath.split("#skin=")[0]!.toLowerCase()) || model.materials.some((material) => !materials.has(material.logicalPath.toLowerCase()) || !states.has(material.logicalPath.toLowerCase()))) throw new RenderingError("MissingInput", `model admission closure ${model.logicalPath}`)
      }
      buildModelTemplates(models, { request: { modelMaterials: materials, modelFacing: facing }, materialStates: states, disposables,
        texture, cubemap, frames: (input, space) => ({ frameCount: input.frameCount, select: (frame, consumer) => {
          if (!Number.isSafeInteger(frame) || frame < 0 || frame >= input.frameCount) throw new RenderingError("MalformedInput", "model frame")
          return residency.select(`${input.sourceSha256}:${space}`, frame, consumer, () => textureFromAuthored(input, space, frame, this.textureQuality), input.frameCount)
        } }), modelLightingTextures: lightingTextures, modelPanelMaterialAnimations: animations, modelTemplates: templates, modelBaseSamples: new Map(), waterFogUniforms: waterFog })
      this.#buildParticleMaterials(request.particleTextures ?? [], states, disposables, waterFog, particleDepth, particleTextures, particleBatchMaterials, particlePipelineMeshes, TSL.float(1), false)
      if (active) {
        for (const [key, template] of templates) active.modelTemplates.set(key, template)
        active.modelLookup = new Map([...active.modelLookup, ...models.map((model) => [model.logicalPath, model] as const)])
        active.loadRequest = { ...active.loadRequest, modelMaterials: materials, modelFacing: facing, authoredTextures: textures }
        active.materialStates = states; active.modelLightingTextures = lightingTextures; active.modelPanelMaterialAnimations = animations
      } else {
        this.#clearParticleBatches()
        this.#modelPanelScene.clear()
        for (const retained of this.#modelPanelInstances.values()) this.#disposeDynamicInstance(retained.instance)
        this.#modelPanelInstances.clear(); this.#retainedModelPanels.clear(); this.#preparedModelInstances.clear()
        disposables.activate()
        this.#panelAssets = { loadRequest: { modelMaterials: materials, modelFacing: facing, authoredTextures: textures }, modelTemplates: templates, modelLightingTextures: lightingTextures,
          modelPipelineKeys: new Set(),
          modelPanelLightingGraphs: new ModelLightingGraphs(), modelPanelMaterialAnimations: animations, materialStates: states, disposables, textureResidency: residency, waterFogUniforms: waterFog, modelCubemap: cubemap,
          particleTextures, particleBatchMaterials, particleDepth, particlePipelineMeshes }
        if (previousPanel) await previousPanel.disposables.retire(this.#backend.backend.device?.queue.onSubmittedWorkDone() ?? Promise.resolve())
      }
    } catch (error) { if (!active) disposables.dispose(); throw error }
  }

  async loadMap(request: MapLoadRequest): Promise<SceneResult> {
    this.#requireReady()
    if(!request.diagnostic)throw new RenderingError("UnsupportedFeature","ordinary rendering requires an explicit WebGPU frame encoder; the current Three.js adapter is diagnostic-only")
    if (!HASH.test(request.payloadSha256))
      throw new RenderingError("MalformedInput", "runtime map payload SHA-256 is invalid")
    if (request.resourceIdentity !== undefined && !HASH.test(request.resourceIdentity))
      throw new RenderingError("MalformedInput", "presentation resource identity is invalid")
    const ordinal = ++this.#loadOrdinal
    const payload = request.payload
    this.#checkAbort(request.signal, ordinal)
    if ((await digest(payload)) !== request.payloadSha256)
      throw new RenderingError("IdentityMismatch", "runtime map payload identity differs")
    this.#checkAbort(request.signal, ordinal)
    let map: RuntimeMap
    try {
      map = this.#active?.payload === payload ? this.#active.map : await parseRuntimeMapVerified(payload)
    } catch (error) {
      throw new RenderingError("MalformedInput", `runtime map validation failed: ${String(error)}`)
    }
    if (map.lighting.profile !== this.configuration.lightingProfile) {
      throw new RenderingError("IdentityMismatch", "runtime map lighting profile differs from renderer configuration")
    }
    if (map.lighting.profile === "hdr" && request.lightStyles) {
      map = Object.freeze({ ...map, lightmap: buildRuntimeLightmap(map, request.lightStyles) })
    }
    if (!map.lightmap) throw new RenderingError("MissingInput", "explicit light-style scalars are required")
    if(!request.brushModels||request.brushModels.length<1)throw new RenderingError("MissingInput","complete brush-model descriptors are required")
    for(let index=0;index<request.brushModels.length;index++){const descriptor=request.brushModels[index]!,geometry=map.brushModels.find(model=>model.index===index);if(descriptor.index!==index||descriptor.surfaceRange[1]<descriptor.surfaceRange[0]||(geometry&&geometry.batches.some(batch=>!descriptor.materials.includes(batch.material))))throw new RenderingError("IdentityMismatch","brush-model geometry differs from its descriptor")}
    const directionalInputs = validateDirectionalInputs(request.directionalTextures ?? [])
    if (
      request.environment &&
      (request.environment.profile !== map.lighting.profile ||
        !HASH.test(request.environment.identity) ||
        !HASH.test(request.environment.collisionWorldIdentity) ||
        request.environment.receiverSnapshotRevision < 1n || request.environment.placementRevision < 1n ||
        Object.values(request.environment).some(
          (value) => typeof value === "number" && (!Number.isSafeInteger(value) || value < 0),
        ))
    )
      throw new RenderingError("MalformedInput", "world environment input is invalid")
    for (const texture of request.environment?.textures ?? []) {
      if (
        texture.width * texture.height * 4 !== texture.rgba.byteLength ||
        (await digest(texture.rgba)) !== texture.sha256
      )
        throw new RenderingError("MalformedInput", "environment texture input is invalid")
    }
    if ((request.particleTextures?.length ?? 0) > 256) throw new RenderingError("BoundExceeded", "particle pipeline material closure exceeds 256 variants")
    for (const texture of request.particleTextures ?? []) {
      if (!texture.material || texture.mipCount < 1 || texture.planes.length < texture.mipCount)
        throw new RenderingError("MalformedInput", "particle texture input is invalid")
    }
    const materialStates = new Map<string, MaterialStateInput>()
    for (const [identity, state] of request.materialStates ?? []) {
      const key = identity.toLowerCase()
      if (
        !key ||
        materialStates.has(key) ||
        !Number.isFinite(state.alphaTestReference) ||
        !Number.isFinite(state.alphaModulation) || state.alphaModulation < 0 || state.alphaModulation > 1 ||
        state.fragmentDiscard.reference !== (state.fragmentDiscard.kind === "alpha" ? state.alphaTestReference : 0) ||
        state.alphaTest !== (state.fragmentDiscard.kind === "alpha") ||
        (state.fragmentDiscard.kind === "none" && (state.fragmentDiscard.source !== "base-texture-or-one" || state.fragmentDiscard.pass !== "greater")) ||
        Object.values(state.alphaOwnership).some((value) => typeof value !== "boolean") ||
        typeof state.samplingAvailable !== "boolean" ||
        typeof state.mipmapped !== "boolean" ||
        typeof state.noLod !== "boolean" ||
        typeof state.allMips !== "boolean" ||
        (state.samplingAvailable && (
          state.wrapS < 0 || state.wrapS > 2 ||
          state.wrapT < 0 || state.wrapT > 2 ||
          state.wrapU < 0 || state.wrapU > 2 ||
          state.minFilter < 0 || state.minFilter > 4 ||
          state.magFilter < 0 || state.magFilter > 2
        ))
      ) throw new RenderingError("MalformedInput", "material state input is invalid")
      materialStates.set(key, Object.freeze({ ...state }))
    }
    for (const mark of request.environment?.markRecords ?? []) {
      const state = materialStates.get(mark.material.toLowerCase())
      if (mark.status === 0 && (!state || (mark.polygonOffset === "decal") !== (state.polygonOffset === 1)
        || !projectedDecalReceiverIsValid(mark))) {
        throw new RenderingError("IdentityMismatch", "projected mark receiver or material contract differs")
      }
    }
    const authoredTextures = new Map<string, AuthoredTextureInput>()
    for (const [identity, texture] of request.authoredTextures ?? []) {
      const key = identity.toLowerCase()
      if (key !== texture.logicalPath.toLowerCase() || authoredTextures.has(key) || !HASH.test(texture.sourceSha256) ||
        texture.width < 1 || texture.height < 1 || texture.depth < 1 || texture.mipCount < 1 || texture.frameCount < 1 ||
        texture.faces.length < 1 || new Set(texture.faces).size !== texture.faces.length ||
        texture.sampling.wrapS < 0 || texture.sampling.wrapS > 2 || texture.sampling.wrapT < 0 || texture.sampling.wrapT > 2 ||
        texture.sampling.wrapU < 0 || texture.sampling.wrapU > 2 || texture.sampling.minFilter < 0 || texture.sampling.minFilter > 4 ||
        texture.sampling.magFilter < 0 || texture.sampling.magFilter > 2 || texture.sampling.anisotropyLevel < 1 ||
        texture.planes.some((plane) => plane.width < 1 || plane.height < 1 || plane.rgba.length !== (texture.sourceFormat === null
          ? plane.width * plane.height * 4 * (texture.scalarEncoding === "u8" ? 1 : 2)
          : [2, 3].includes(texture.sourceFormat)
            ? plane.width * plane.height * 3
            : [0, 1, 11, 12, 16].includes(texture.sourceFormat)
              ? plane.width * plane.height * 4
            : texture.sourceFormat === 24
              ? plane.width * plane.height * 8
              : Math.max(1, Math.ceil(plane.width / 4)) * Math.max(1, Math.ceil(plane.height / 4)) * ([13, 20].includes(texture.sourceFormat) ? 8 : 16)))) {
        throw new RenderingError("MalformedInput", "authored model texture input is invalid")
      }
      authoredTextures.set(key, texture)
    }
    const modelMaterials = new Map<string, ModelMaterialInput>()
    for (const [identity, material] of request.modelMaterials ?? []) {
      const key = identity.toLowerCase()
      if (key !== material.identity.toLowerCase() || modelMaterials.has(key) || material.vertexRequirements < 0 || !Number.isInteger(material.cloakProxy) || material.cloakProxy < 0 || material.cloakProxy > 7 ||
        new Set(material.requiredInputs).size !== material.requiredInputs.length ||
        material.requiredInputs.some((input) => ![
          "ambient-cube", "local-lights", "camera-position", "studio-eye-parameters",
          "local-environment", "current-framebuffer", "authored-texture-planes", "game-proxy-values",
        ].includes(input)) ||
        material.bindings.some((binding) => !authoredTextures.has(binding.logicalPath.toLowerCase()))) {
        throw new RenderingError("MalformedInput", "model material input is invalid")
      }
      modelMaterials.set(key, material)
    }
    const modelFacing = new Map(request.modelFacing ?? [])
    for (const model of map.models) {
      const identity = model.logicalPath.split("#")[0]!.toLowerCase()
      const facing = modelFacing.get(identity)
      if (!facing) throw new RenderingError("MissingInput", `StudioModel facing ${identity} is unavailable`)
      sourceModelSide(facing)
    }
    const modelDrawInputs = new Map<number, NonNullable<MapLoadRequest["modelDrawInputs"]>[number]>()
    const modelEntities = new Set(map.modelOccurrences.map((occurrence) => occurrence.entity))
    for (const input of request.modelDrawInputs ?? []) {
      if (!modelEntities.has(input.entity) || modelDrawInputs.has(input.entity)) {
        throw new RenderingError("IdentityMismatch", "model draw-input identity differs")
      }
      try {
        validateModelLightingInput(input.lighting)
        validateModelEyeStates(input.eyes)
      } catch (error) {
        throw new RenderingError("MalformedInput", `model draw input is invalid: ${String(error)}`)
      }
      modelDrawInputs.set(input.entity, Object.freeze({ ...input, eyes: Object.freeze([...input.eyes]) }))
    }
    const staticProps=request.staticProps
    if(staticProps){const count=staticProps.count;if(!Number.isSafeInteger(count)||count<0||count>65536||staticProps.source.length!==count||staticProps.dictionaryModel.length!==count||staticProps.presentationModel.length!==count||staticProps.transform.length!==count*6||staticProps.skin.length!==count||staticProps.body.length!==count||staticProps.lod.length!==count||staticProps.fades.length!==count*3||staticProps.flags.length!==count||staticProps.solidity.length!==count||staticProps.ownership.length!==count||staticProps.lightingKind.length!==count||staticProps.lightingOrigin.length!==count*3||staticProps.leafOffsets.length!==count+1||staticProps.vhvObjects.length!==count*2||staticProps.runtimeAmbient.length!==count*18||staticProps.runtimeLightOffsets.length!==count+1||staticProps.models.length!==modelFacing.size||staticProps.source.some((value,index)=>index>0&&value<=staticProps.source[index-1]!)||staticProps.presentationModel.some(value=>value>=staticProps.models.length)||staticProps.ownership.some(value=>value>1)||staticProps.lightingKind.some(value=>value>1)||staticProps.leafOffsets[count]!==staticProps.leaves.length||staticProps.leaves.length!==staticProps.areas.length||staticProps.runtimeLightOffsets[count]!==staticProps.runtimeLights.length)throw new RenderingError("MalformedInput","static-prop artifact input is invalid")}
    const materialIdentities = new Set([
      ...map.materials.map((material) => material.logicalPath.toLowerCase()),
      ...map.models.flatMap((model) => model.materials.map((material) => material.logicalPath.toLowerCase())),
    ])
    if ([...directionalInputs.keys()].some((identity) => !materialIdentities.has(identity))) {
      throw new RenderingError("MalformedInput", "directional texture names an unavailable material")
    }
    this.#checkAbort(request.signal, ordinal)
    const normalizedRequest = Object.freeze({
      ...request,
      materialStates,
      authoredTextures,
      modelMaterials,
      modelFacing,
      modelDrawInputs: Object.freeze([...modelDrawInputs.values()]),
    })
    const staged = this.#buildScene(
      map,
      payload,
      request.payloadSha256,
      directionalInputs,
      normalizedRequest,
      this.#sceneGeneration + 1,
    )
    try {
      this.#checkAbort(request.signal, ordinal)
      if (staged.diagnostics.length > 0 && !request.diagnostic) {
        const missing = staged.diagnostics.some((item) => item.code.startsWith("Missing"))
        throw new RenderingError(
          missing ? "MissingInput" : "UnsupportedFeature",
          "required material, lighting, or profile presentation inputs are unavailable",
        )
      }
      const stagedWorld = this.#world.children.map((child) => Object.freeze({ child, visible: child.visible }))
      for (const { child } of stagedWorld) child.visible = false
      this.#world.add(staged.group)
      this.#world.updateMatrixWorld(true)
      try {
        await this.#prepareReachablePipelines(staged, request.signal, ordinal)
        await this.#prepareWaterPipelines(staged, request.environment, request.signal, ordinal)
      } finally {
        this.#world.remove(staged.group)
        for (const { child, visible } of stagedWorld) child.visible = visible
      }
      this.#checkAbort(request.signal, ordinal)

      const prior = this.#active
      staged.textureResidency.commitTransfers(staged.borrowedResources)
      staged.borrowedResources.length = 0
      this.#clearDynamic(this.#effects)
      this.#dynamicModelInstances.clear()
      this.#dynamicBrushInstances.clear()
      this.#clearParticleBatches()
      this.#clearDynamic(this.#viewModels)
      this.#viewModelInstances.clear()
      this.#retainedModels.clear()
      const parkPanel = (retained: { model: string; instance: THREE.Group; meshes?: THREE.Mesh[] }) => {
        if (staged.modelPipelineKeys.has(retained.model) && staged.modelTemplates.get(retained.model) === prior?.modelTemplates.get(retained.model)) {
          retained.instance.removeFromParent()
          this.#preparedModelInstances.retain(`${retained.meshes ? "panel" : "panel-bind"}:${retained.model}`, retained)
        } else this.#disposeDynamicInstance(retained.instance)
      }
      for (const retained of this.#modelPanelInstances.values()) parkPanel(retained)
      this.#modelPanelInstances.clear()
      for (const retained of this.#retainedModelPanels.drain()) parkPanel(retained)
      this.#preparedModelInstances.discardWhere(value => !staged.modelTemplates.has(value.model) || staged.modelTemplates.get(value.model) !== prior?.modelTemplates.get(value.model))
      this.#stagedDynamic = undefined
      this.#worldVisibilitySurfaces = undefined
      this.#worldVisibilityIdentity = undefined
      this.#skyWorldVisibilitySurfaces = undefined
      this.#skyWorldVisibilityIdentity = undefined
      this.#visibleStaticIndices[0].clear()
      this.#visibleStaticIndices[1].clear()
      this.#nextVisibleStaticIndices[0].clear()
      this.#nextVisibleStaticIndices[1].clear()
      this.#visibleStaticSources = [Object.freeze([]), Object.freeze([])]
      this.#visibleWorldMarkFaces.clear()
      this.#visibleProjectedMarkCount = 0
      this.#world.clear()
      this.#world.add(staged.group)
      this.#world.updateMatrixWorld(true)
      this.#scene.background = request.diagnostic ? new THREE.Color(0x111820) : null
      this.#active = staged
      if (this.#panelAssets) {
        const panel = this.#panelAssets
        this.#panelAssets = undefined
        await panel.disposables.retire(this.#backend.backend.device?.queue.onSubmittedWorkDone() ?? Promise.resolve())
        panel.textureResidency.clear()
      }
      this.#sceneGeneration += 1
      if (prior) await this.#retire(prior)
      return staged.result
    } catch (error) {
      this.#renderObjectLifetime?.release(staged.group)
      disposeScene(staged)
      throw error
    }
  }

  async prepareVisiblePipelines(camera: Camera, leaves: readonly number[]): Promise<void> {
    this.#requireReady()
    if (!this.#active) throw new RenderingError("InvalidState", "pipeline preparation has no active map")
    if (leaves.some(leaf => !Number.isSafeInteger(leaf) || leaf < 0)) {
      throw new RenderingError("MalformedInput", "pipeline preparation visibility leaves are invalid")
    }
    this.#setCamera(camera)
    await this.#prepareReachablePipelines(this.#active, undefined, this.#loadOrdinal, leaves)
  }

  async prepareModelPipelines(models: readonly Readonly<{ item: ModelItem; pass: "panel" | "view" | "world"; unposedPanel?: boolean }>[], camera: Camera, fog?: FogInput): Promise<void> {
    this.#requireReady()
    const assets = this.#active ?? this.#panelAssets
    if (!assets || this.#renderBusy || !this.#active && models.some((model) => model.pass !== "panel")) throw new RenderingError("InvalidState", "model pipeline preparation requires admitted model assets")
    const variantCount=models.length+models.filter(model=>model.unposedPanel).length
    if (variantCount > MAX_PREPARED_MODEL_VARIANTS) throw new RenderingError("BoundExceeded", `model pipeline preparation exceeds the resident model bound: ${variantCount}/${MAX_PREPARED_MODEL_VARIANTS}`)
    const ordinal = this.#loadOrdinal
    const owner = assets, backend = this.#backend, deviceGeneration = this.#deviceGeneration
    const checkOwner = () => {
      this.#checkAbort(undefined, ordinal)
      this.#requireReady()
      if (owner !== (this.#active ?? this.#panelAssets) || backend !== this.#backend || deviceGeneration !== this.#deviceGeneration) {
        throw new RenderingError("InvalidState", "model pipeline preparation generation was replaced")
      }
    }
    const staged: { key: string; retained: { model: string; instance: THREE.Group; meshes?: THREE.Mesh[] } }[] = []
    const restoreVisibility: (() => void)[] = []
    const viewModels = new THREE.Group(), panels = new THREE.Group(), world = createWorldClipGroup(this.#waterClipPlane)
    viewModels.layers.set(1)
    const previousFog = this.#scene.fog as THREE.Fog | null
    const keys = new Set<string>()
    const started = performance.now()
    this.#renderBusy = true
    const preparationProfile = browserFrameProfiler()
    if (preparationProfile) {
      preparationProfile.captureModelPrograms = true
      preparationProfile.modelPreparation = { started, models: models.map(({ item, pass }) => ({ model: item.model, skin: item.skin ?? 0, pass })) }
    }
    try {
      this.#setCamera(camera)
      for (const { item, pass, unposedPanel } of models) {
        const unposedWorld = pass === "world" && !item.pose && !item.modelLighting
        const model = modelKey(item.model, item.skin ?? 0, item.body ?? 0), key = `${unposedWorld ? "world-bind" : pass}:${model}`
        if (keys.has(key)) continue
        keys.add(key)
        const template = assets.modelTemplates.get(model)
        if (!template || !unposedWorld && (!item.pose || !item.modelLighting)) throw new RenderingError("MissingInput", `model pipeline preparation inputs unavailable: ${model}`)
        if (unposedWorld) {
          const instance = template.clone(true)
          restoreVisibility.push(prepareReachablePipelineVisibility(instance).restore)
          staged.push({ key, retained: { model, instance } })
          world.add(instance)
          continue
        }
        if (unposedPanel) {
          if (pass !== "panel") throw new RenderingError("MalformedInput", "unposed model preparation requires a panel")
          const bindKey = `panel-bind:${model}`
          const bind = this.#preparedModelInstances.take(bindKey)?.instance ?? template.clone(true)
          restoreVisibility.push(prepareReachablePipelineVisibility(bind).restore)
          staged.push({ key: bindKey, retained: { model, instance: bind } })
          keys.add(bindKey)
          preparationProfile?.modelPreparation?.models.push({ model: item.model, skin: item.skin ?? 0, pass: "panel-bind" })
          panels.add(bind)
        }
        const prepared = this.#preparedModelInstances.take(key)
        if (prepared && preparationProfile) preparationProfile.counters.reusedPreparedModels = (preparationProfile.counters.reusedPreparedModels ?? 0) + 1
        const instance = prepared?.instance ?? template.clone(true)
        const authoredViewState: { transparent: boolean; depthWrite: boolean }[][] = []
        if (pass === "view") instance.traverse(object => {
          if (object instanceof THREE.Mesh) authoredViewState.push((Array.isArray(object.material) ? object.material : [object.material])
            .map(material => ({ transparent: material.transparent, depthWrite: material.depthWrite })))
        })
        const retained = prepared ?? { model, instance, meshes: [] as THREE.Mesh[] }
        staged.push({ key, retained })
        // Use the same palette, flex, primitive ordering and world/panel depth
        // state owner as drawing. Standalone viewmodel metadata is not a weapon
        // composition, so its pass uses the authored material classification.
        retained.meshes = this.#applyPose(instance, { ...item.pose!, flex: undefined }, false)
        if (pass === "view") for (const [index, mesh] of retained.meshes.entries()) {
          const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
          for (const [slot, material] of materials.entries()) Object.assign(material, authoredViewState[index]![slot]!)
        }
        this.#applyDynamicModelLighting(retained, item, pass === "panel" ? {
          materials: assets.loadRequest.modelMaterials, states: assets.materialStates,
          textures: assets.modelLightingTextures, cubemap: assets.modelCubemap,
          exposure: this.#modelPanelExposure, graphs: assets.modelPanelLightingGraphs,
        } : undefined)
        if (pass === "view") instance.traverse(object => object.layers.set(1))
        ;(pass === "panel" ? panels : pass === "view" ? viewModels : world).add(instance)
      }
      // compileAsync waits for actual native pipeline readiness. It neither
      // submits a game frame nor advances simulation/animation/input clocks.
      await withBoundedPipelineCompilation((backend as any)._pipelines, async () => {
        for (const [group, targetScene, camera] of [[viewModels, this.#scene, this.#viewCamera], [panels, this.#modelPanelScene, this.#modelPanelCamera]] as const) {
          if (group.children.length) await backend.compileAsync(group, camera, targetScene)
          checkOwner()
        }
        this.#setSceneFog(this.#fog(fog))
        if (world.children.length) await prepareWorldViewPipelines(backend,world,this.#camera,this.#scene,world,this.#waterPreparationTargets(),this.#waterPreparationFogs())
      })
      checkOwner()
      for (const { key, retained } of staged) {
        retained.instance.removeFromParent()
        this.#preparedModelInstances.retain(key, retained)
        owner.modelPipelineKeys.add(retained.model)
      }
      staged.length = 0
      const profile = browserFrameProfiler()
      if (profile) {
        profile.counters.preparedModelVariants = keys.size
        profile.counters.modelPipelinePreparationMilliseconds = performance.now() - started
        profile.modelPreparation!.ended = performance.now()
      }
    } finally {
      for (const restore of restoreVisibility) restore()
      this.#setSceneFog(previousFog)
      for (const { retained } of staged) this.#disposeDynamicInstance(retained.instance)
      this.#renderBusy = false
    }
  }

  async prepareParticlePipelines(camera: Camera, fog?: FogInput): Promise<void> {
    this.#requireReady()
    const owner = this.#active ?? this.#panelAssets
    if (!owner || this.#renderBusy) throw new RenderingError("InvalidState", "particle pipeline preparation requires idle admitted assets")
    const backend = this.#backend, ordinal = this.#loadOrdinal, deviceGeneration = this.#deviceGeneration
    const coldTextures = [...new Set(owner.particleTextures.values())].filter(texture => !backend.backend.has(texture))
    const previousFog = this.#scene.fog as THREE.Fog | null
    const started = performance.now()
    this.#renderBusy = true
    try {
      await owner.particleDepth.prepare()
      if ("legacyVisuals" in owner && owner.legacyVisuals) await Promise.all(owner.legacyVisuals.map(view => view.prepare()))
      this.#setCamera(camera)
      this.#setSceneFog(this.#fog(fog))
      const profile = browserFrameProfiler()
      if (profile) profile.particlePreparation = { started, generation: this.#sceneGeneration,
        materials: [...owner.particleBatchMaterials].map(([key, material]) => ({ id: material.id, identity: material.name, key })) }
      // The retained one-quad layouts use the exact authored material closure,
      // attributes and main-pass attachments. compileAsync submits no pixels.
      await withBoundedPipelineCompilation((backend as any)._pipelines, async () => {
        await this.#particleVisibility.prepare()
        const clipping=createWorldClipGroup(this.#waterClipPlane)
        clipping.add(owner.particlePipelineMeshes)
        try{if (owner.particlePipelineMeshes.children.length) await prepareWorldViewPipelines(backend,clipping,this.#camera,this.#scene,clipping,this.#waterPreparationTargets(),this.#waterPreparationFogs())}
        finally{clipping.remove(owner.particlePipelineMeshes)}
        if("legacyVisuals" in owner&&owner.legacyVisuals?.length)await owner.legacyVisuals[0]!.prepareMaterials(backend,this.#camera,this.#scene,this.#waterClipPlane,this.#waterPreparationTargets())
      })
      this.#checkAbort(undefined, ordinal)
      this.#requireReady()
      if (owner !== (this.#active ?? this.#panelAssets) || backend !== this.#backend || deviceGeneration !== this.#deviceGeneration) throw new RenderingError("InvalidState", "particle pipeline preparation generation was replaced")
      if (profile) {
        profile.particlePreparation!.ended = performance.now()
        profile.counters.preparedParticleVariants = owner.particlePipelineMeshes.children.length
        profile.counters.particlePipelinePreparationMilliseconds = performance.now() - started
      }
    } finally {
      for (const texture of coldTextures) {
        texture.dispose()
        texture.needsUpdate = true
      }
      this.#setSceneFog(previousFog)
      this.#renderBusy = false
    }
  }

  requestParticleDepthEvidence(): void { this.#active?.particleDepth.requestEvidence() }
  takeParticleVisibilitySamples(): readonly ParticleVisibilitySample[] { return this.#particleVisibility.takeSamples() }
  captureParticleVisibilityEvidence(): ReturnType<ParticleVisibilityQueries["evidence"]> { return this.#particleVisibility.evidence() }
  pixelVisibilityFeedback(view:LegacyViewKind=0):readonly PixelVisibilityFeedback[] {return this.#active?.legacyVisuals?.[view]?.feedback()??[]}
  legacyVisualViewport(view:LegacyViewKind):Readonly<{width:number;height:number}>{
    if(view===2||view===3){const target=view===2?this.#active?.reflectionTarget:this.#active?.refractionTarget;if(!target)throw new RenderingError("MissingInput","Legacy water view target is unavailable");return {width:target.width,height:target.height}}
    const size=this.#backend.getDrawingBufferSize(new THREE.Vector2());return {width:size.x,height:size.y}
  }
  captureLegacyVisualEvidence() { return this.#active?.legacyVisuals?.map(view=>view.evidence()) ?? [] }
  readParticleDepthEvidence(): ReturnType<SourceParticleDepth["readEvidence"]> { return this.#active?.particleDepth.readEvidence() ?? Promise.resolve(null) }

  #waterPreparationTargets(scene=this.#active):THREE.RenderTarget[]{
    return scene?[scene.reflectionTarget,scene.refractionTarget].filter((target):target is THREE.RenderTarget=>target!==null):[]
  }

  #waterPreparationFogs(scene=this.#active):(THREE.Fog|null)[]{
    // Height-fog refraction uses the Source fragment uniforms and disables
    // Three fog. Ordinary underwater views use the authored Water fog object.
    return scene&&this.#waterPreparationTargets(scene).length?[null,...[...scene.waterMaterials.values()].map(water=>water.fog)]:[]
  }

  async #prepareReachablePipelines(scene: SceneResources, signal: AbortSignal | undefined, ordinal: number, leaves?: readonly number[]): Promise<void> {
    this.#checkAbort(signal, ordinal)
    const waterTargets=this.#waterPreparationTargets(scene)
    // A material binding can visit a sampled water attachment during compilation.
    // Admit its complete target first so the later target pass cannot replace it.
    if (scene.reflectionTarget) this.#backend.initRenderTarget(scene.reflectionTarget)
    if (scene.refractionTarget) this.#backend.initRenderTarget(scene.refractionTarget)
    const visibleLeaves = new Set(leaves)
    const eligibleProps = new Set(scene.staticPropInstances.filter(prop =>
      leaves === undefined || prop.ownership === 1 || prop.leaves.some(leaf => visibleLeaves.has(leaf)),
    ))
    const eligibleGroups = new Set([...eligibleProps].map(prop => prop.object))
    const eligibleSources = new Set([...eligibleProps].map(prop => prop.source))
    const fadeVariants=new THREE.Group()
    for(const prop of eligibleProps){
      for(const binding of prop.fadeBindings){
        const mesh=new THREE.Mesh(binding.mesh.geometry,binding.faded)
        bindStaticPropFade(mesh,prop.fadeUniform)
        fadeVariants.add(mesh)
      }
    }
    scene.group.add(fadeVariants)
    const batchSources = new Map(scene.staticPropBatches.map(({ batch }) => [batch.mesh, batch.sources] as const))
    const visibility = prepareReachablePipelineVisibility(
      scene.group,
      scene.waterMeshes.map(water => water.mesh),
      [],
      mesh => {
        const sources = batchSources.get(mesh)
        if (sources) return sources.some(source => eligibleSources.has(source))
        for (let parent: THREE.Object3D | null = mesh; parent; parent = parent.parent) {
          if (eligibleGroups.has(parent)) return true
           if (parent === scene.mainStaticProps || parent === scene.skyStaticProps) return false
        }
        return true
      },
    )
    const started = performance.now()
    try {
      // Keep render-list construction, node/resource updates and visibility in
      // their original order. Independent native pipelines need not each hold
      // the entire preparation owner while they compile; all still settle
      // before visibility is restored or this preparation can publish Ready.
      const compile = () => prepareWorldViewPipelines(this.#backend,this.#scene,this.#camera,this.#scene,this.#waterClipping,waterTargets,this.#waterPreparationFogs(scene))
      if (leaves) await withBoundedPipelineCompilation((this.#backend as any)._pipelines, compile, 2)
      else await compile()
      this.#checkAbort(signal, ordinal)
      const profile = browserFrameProfiler()
      if (profile) {
        profile.counters.warmedPipelineVariants = (profile.counters.warmedPipelineVariants ?? 0) + visibility.variants
        profile.counters.pipelineWarmupMilliseconds = (profile.counters.pipelineWarmupMilliseconds ?? 0) + performance.now() - started
      }
    } finally {
      visibility.restore()
      fadeVariants.removeFromParent();fadeVariants.clear()
    }
  }

  async #prepareWaterPipelines(
    scene: SceneResources,
    environment: EnvironmentInput | undefined,
    signal: AbortSignal | undefined,
    ordinal: number,
  ): Promise<void> {
    if (!environment || scene.waterMeshes.length === 0 || environment.waterVolumeFacts.length === 0) return
    const volume = environment.waterVolumeFacts[0]!
    const depth = volume.surfaceZ - volume.minimumZ
    if (!Number.isFinite(depth) || depth <= 0 || !volume.bounds.flat().every(Number.isFinite)) {
      throw new RenderingError("MalformedInput", "Water pipeline preparation requires complete finite volume bounds")
    }
    const centerX = (volume.bounds[0][0] + volume.bounds[1][0]) * 0.5
    const centerY = (volume.bounds[0][1] + volume.bounds[1][1]) * 0.5
    const initializedTextures = new Set<THREE.Texture>()
    for (const water of scene.waterMaterials.values()) {
      const texture = scene.textureResidency.selected(water.normalConsumer)
      if (!texture || initializedTextures.has(texture)) continue
      this.#checkAbort(signal, ordinal)
      this.#backend.initTexture(texture)
      initializedTextures.add(texture)
    }

    const previousPosition = this.#camera.position.clone()
    const previousQuaternion = this.#camera.quaternion.clone()
    const previousFog = this.#scene.fog
    const previousTarget = this.#backend.getRenderTarget()
    const restoreSceneVisibility = prepareWaterPipelineVisibility(scene.group, scene.waterMeshes.map((water) => water.mesh))
    try {
      const combinations = [
        ...(scene.reflectionTarget ? [{ target: scene.reflectionTarget, height: volume.surfaceZ + depth * 0.5, keep: "above" as const }] : []),
        ...(scene.refractionTarget ? [{ target: scene.refractionTarget, height: volume.surfaceZ + depth * 0.5, keep: "below" as const }] : []),
        { target: null, height: volume.surfaceZ + depth * 0.5, keep: null },
        { target: null, height: volume.surfaceZ - depth * 0.5, keep: "below" as const },
      ]
      for (const combination of combinations) {
        this.#checkAbort(signal, ordinal)
        this.#camera.position.set(centerX, centerY, combination.height)
        this.#camera.lookAt(centerX + depth, centerY, volume.surfaceZ)
        this.#camera.updateMatrixWorld()
        this.#backend.setRenderTarget(combination.target)
        const restore = this.#setClip(combination.keep === null
          ? null
          : { height: volume.surfaceZ, keep: combination.keep })
        try {
          await this.#backend.compileAsync(this.#scene, this.#camera)
        } finally {
          restore()
        }
      }
      this.#checkAbort(signal, ordinal)
    } finally {
      this.#backend.setRenderTarget(previousTarget)
      this.#camera.position.copy(previousPosition)
      this.#camera.quaternion.copy(previousQuaternion)
      this.#camera.updateMatrixWorld()
      this.#setSceneFog(previousFog as THREE.Fog | null)
      restoreSceneVisibility()
    }
  }

  #buildScene(
    map: RuntimeMap,
    payload: Uint8Array,
    payloadSha256: string,
    directionalInputs: Map<string, DirectionalTextureInput>,
    request: MapLoadRequest,
    sceneGeneration: number,
  ): SceneResources {
    const group = new THREE.Group()
    group.matrixAutoUpdate = false
    const worldBundle = new THREE.BundleGroup()
    worldBundle.matrixAutoUpdate = false
    const skyWorldBundle = new THREE.BundleGroup()
    skyWorldBundle.matrixAutoUpdate = false
    skyWorldBundle.visible = false
    const mainAnimatedWorld = new THREE.Group()
    const skyAnimatedWorld = new THREE.Group()
    skyAnimatedWorld.visible = false
    const mainTransparentWorld = new THREE.Group()
    const skyTransparentWorld = new THREE.Group()
    skyTransparentWorld.visible = false
    group.add(worldBundle, mainAnimatedWorld, mainTransparentWorld, skyWorldBundle, skyAnimatedWorld, skyTransparentWorld)
    const mainStaticProps = new RetainedStaticSceneGroup()
    const skyStaticProps = new RetainedStaticSceneGroup()
    const mainModelOccurrences = new RetainedStaticSceneGroup()
    const projectedMarkGroup = new THREE.Group()
    projectedMarkGroup.matrixAutoUpdate = false
    skyStaticProps.visible = false
    const staticPropInstances: StaticPropResource[] = []
    const staticPropBatches: { ownership: 0 | 1; batch: StaticPropBatch }[] = []
    group.add(mainStaticProps, skyStaticProps, mainModelOccurrences, projectedMarkGroup)
    // An exact same-world/resource replacement still builds a fresh world and
    // resets every occurrence. Only immutable samples/graphs and pinned texture
    // objects cross the successful generation handoff, never old poses/entities.
    const retained = retainedSceneSource(this.#active, this.#deviceGeneration, payloadSha256, request.resourceIdentity)
    const modelTemplates = new Map<string, THREE.Group>()
    const modelTemplateResources = new Map<string, (THREE.BufferGeometry | THREE.Material)[]>()
    const modelPipelineKeys = new Set<string>()
    const borrowedResources: (THREE.BufferGeometry | THREE.Material | THREE.Texture)[] = []
    const handoffProfile = browserFrameProfiler()
    if (handoffProfile) handoffProfile.counters.retainedModelTemplates = 0
    const modelBaseSamples = retained?.modelBaseSamples ?? new Map<string, any>()
    const modelLightingTextures = new Map<string, ModelLightingTextures>()
    const modelLightingGraphs = retained?.modelLightingGraphs ?? new ModelLightingGraphs()
    const modelPanelLightingGraphs = retained?.modelPanelLightingGraphs ?? new ModelLightingGraphs()
    const modelPanelMaterialAnimations = new Map<string, ModelPanelMaterialAnimation[]>()
    const modelOccurrenceInstances=new Map<number,THREE.Group>()
    const modelOccurrenceLighting: SourceModelLightingUniforms[] = []
    const brushModelTemplates=new Map<number,THREE.Group>()
    const particleTextures = new Map<string, THREE.Texture>()
    const particleBatchMaterials = new Map<string, THREE.MeshBasicNodeMaterial>()
    const particlePipelineMeshes = new THREE.Group()
    const materialStates = new Map(request.materialStates ?? [])
    const disposables = new OwnedResourceGeneration(this.#deviceGeneration, sceneGeneration)
    const particleDepth = disposables.add(new SourceParticleDepth(this.#backend.backend))
    let legacyVisuals: readonly LegacyVisuals[] | null = null
    const attributeBackend = this.#backend.backend as unknown as ConstructorParameters<typeof PersistentWorldDraws>[1]
    const attributes = (this.#backend as unknown as {
      _geometries: { attributes: { delete(attribute: THREE.BufferAttribute): unknown } }
    })._geometries.attributes
    const releaseAttribute = (attribute: THREE.BufferAttribute): void => {
      attributes.delete(attribute)
    }
    const worldDraws = disposables.add(new PersistentWorldDraws(map.batches.length, attributeBackend, releaseAttribute))
    const skyWorldDraws = disposables.add(new PersistentWorldDraws(map.batches.length, attributeBackend, releaseAttribute))
    const diagnostics: SceneDiagnostic[] = []
    const worldBatches: WorldBatchResource[] = []
    const skyWorldBatches: WorldBatchResource[] = []
    const projectedMarks: ProjectedMarkResource[] = []
    let combatDecalTexture: THREE.Texture | null = null
    const waterMeshes: WaterMeshResource[]=[]
    const targetWidth=Math.max(1,Number((this.#canvas as {width?:number}).width??1)),targetHeight=Math.max(1,Number((this.#canvas as {height?:number}).height??1))
    let reflectionTarget: THREE.RenderTarget | null = null
    let refractionTarget: THREE.RenderTarget | null = null
    const waterTarget = (kind: "reflection" | "refraction"): THREE.RenderTarget => {
      const retained = kind === "reflection" ? reflectionTarget : refractionTarget
      if (retained) return retained
      const target = disposables.add(new THREE.RenderTarget(targetWidth, targetHeight, { depthBuffer: true }))
      target.texture.name = `playsrc-water-${kind}-${sceneGeneration}`
      target.texture.colorSpace = THREE.NoColorSpace
      if (kind === "reflection") reflectionTarget = target
      else refractionTarget = target
      return target
    }
    const waterMaterials = new Map<string, WaterMaterialResource>()
    const refractMaterials = new Map<string, RefractMaterialResource>()
    const worldMaterials = new Map<string, WorldMaterialResource>()
    const animatedTextureFrames = new Map<string, AuthoredTextureFrames>()
    const cubemapTextures = new Map<number, THREE.CubeTexture>()
    const cubemapFacts = new Map((request.environment?.cubemapFacts ?? []).map((fact) =>
      [fact.logicalPath.toLowerCase(), fact] as const))
    let skyGroup:THREE.Group|null=null
    const occurrenceMatrices = modelOccurrenceMatrices(map, request.modelOccurrences)
    const lightmap = map.lightmap
    if (!lightmap) throw new RenderingError("MissingInput", "runtime lightmap is unavailable")
    const borrowedLightmap = borrowWorldLightmapTextures(lightmap, retained?.lightmapTextures)
    const lightmapTextures = borrowedLightmap ?? createWorldLightmapTextures(lightmap, disposables)
    if (borrowedLightmap) borrowedResources.push(borrowedLightmap[0])
    const lightmapProfile = (globalThis as typeof globalThis & { __playsrcProfile?: { lightmapStages?: object[] } }).__playsrcProfile
    if (lightmapProfile?.lightmapStages && lightmapProfile.lightmapStages.length < 256) lightmapProfile.lightmapStages.push({ at: performance.now(), generation: sceneGeneration,
      retainedSource: !!retained, samePlane: retained?.lightmapTextures[0].image.data === lightmap.flat,
      borrowed: !!borrowedLightmap,
      bytes: lightmap.flat.byteLength * (lightmap.directional ? 4 : 1), textureIds: lightmapTextures.map(texture => texture!.id) })
    const exposureUniform = retained?.exposureUniform ?? TSL.uniform(
      this.configuration.lightingProfile === "hdr" ? this.#exposure.snapshot().current : 1,
      "float",
    )
    const waterFogUniforms = retained?.waterFogUniforms ?? createSourceWaterFogUniforms()
    const directionalGpu = new Map<string, { input: DirectionalTextureInput; texture: THREE.Texture }>()
    const textureResidency = new SharedTextureResidency<THREE.Texture>(disposables, 4, () =>
      this.#backend.backend.device?.queue.onSubmittedWorkDone() ?? Promise.resolve(), retained?.textureResidency)
    const authoredTextureKey = (input: AuthoredTextureInput, colorSpace: string, frame = 0): string =>
      `${input.sourceSha256}:${input.sourceFormat ?? "rgba"}:${input.scalarEncoding}:${colorSpace}:${frame}`
    const retainAuthoredTexture = (input: AuthoredTextureInput, colorSpace: string, frame = 0): THREE.Texture =>
      textureResidency.retain(authoredTextureKey(input, colorSpace, frame), () => textureFromAuthored(input, colorSpace, frame, this.textureQuality))
    const modelDrawInputs = new Map((request.modelDrawInputs ?? []).map((input) => [input.entity, input] as const))
    const modelsRequiringLighting = new Set(map.models
      .filter((model) => model.materials.some((material) => {
        const shader = request.modelMaterials?.get(material.logicalPath.toLowerCase())?.shader
        return shader !== "unlit-generic" && shader !== "unlit-two-texture" && shader !== "modulate"
      }))
      .map((model) => model.logicalPath))
    const missingLightingEntities = map.modelOccurrences
      .filter((occurrence) => modelsRequiringLighting.has(map.models[occurrence.model]!.logicalPath) && !modelDrawInputs.has(occurrence.entity))
      .map((occurrence) => occurrence.entity)
    if (missingLightingEntities.length > 0) {
      diagnostics.push(diagnostic(
        "MissingModelLighting",
        "model-lightcache",
        `explicit ModelLightingInput is unavailable for entities ${missingLightingEntities.join(",")}`,
      ))
    }
    const modelsWithEyes = new Set(map.models
      .filter((model) => model.materials.some((material) => {
        const shader = request.modelMaterials?.get(material.logicalPath.toLowerCase())?.shader
        return shader === "eye-refract" || shader === "eyes"
      }))
      .map((model) => model.logicalPath))
    const missingEyeEntities = map.modelOccurrences
      .filter((occurrence) => modelsWithEyes.has(map.models[occurrence.model]!.logicalPath) && !(modelDrawInputs.get(occurrence.entity)?.eyes.length))
      .map((occurrence) => occurrence.entity)
    if (missingEyeEntities.length > 0) {
      diagnostics.push(diagnostic(
        "MissingModelEyeState",
        "studio-eye-state",
        `explicit StudioModel eye state is unavailable for entities ${missingEyeEntities.join(",")}`,
      ))
    }
    const missingMipInputs = new Set<string>()
    const requireMipInputs = (identity: string, state: MaterialStateInput | undefined): void => {
      const key = identity.toLowerCase()
      if (!state?.samplingAvailable || !state.mipmapped || missingMipInputs.has(key)) return
      missingMipInputs.add(key)
      diagnostics.push(diagnostic("MissingTextureMips", identity, "the supplied texture contains mip zero only"))
    }
    for (const [identity, input] of directionalInputs) {
      const texture = retainAuthoredTexture(input.authored, THREE.NoColorSpace)
      directionalGpu.set(identity, { input, texture })
    }

    const createModelTexture = (identity: string, role: number, kind: "material" | "model" = "material"): Readonly<{texture:THREE.Texture;input:AuthoredTextureInput}> | undefined => {
      const material = request.modelMaterials?.get(identity.toLowerCase())
      if (!material) {
        diagnostics.push(diagnostic("MissingMaterial", identity, "typed model material state is unavailable"))
        return undefined
      }
      const binding = material.bindings.find((value) => value.kind === kind && value.role === role)
      if (!binding) return undefined
      if (binding.colorRead === "format-dependent") {
        throw new RenderingError("MissingInput", `model texture ${binding.logicalPath} lacks a resolved color interpretation`)
      }
      const input = request.authoredTextures?.get(binding.logicalPath.toLowerCase())
      if (!input) throw new RenderingError("MissingInput", `authored model texture ${binding.logicalPath} is unavailable`)
      const texture = retainAuthoredTexture(input, binding.colorRead === "srgb" ? THREE.SRGBColorSpace : THREE.NoColorSpace)
      return Object.freeze({texture,input})
    }
    const createModelCubemap = (identity: string): THREE.CubeTexture | undefined => {
      const material = request.modelMaterials?.get(identity.toLowerCase())
      const binding = material?.bindings.find((value) => value.kind === "material" && value.role === 12)
      if (!binding) return undefined
      if (binding.colorRead === "format-dependent") {
        throw new RenderingError("MissingInput", `model cubemap ${binding.logicalPath} lacks a resolved color interpretation`)
      }
      const input = request.authoredTextures?.get(binding.logicalPath.toLowerCase())
      if (!input) throw new RenderingError("MissingInput", `authored model cubemap ${binding.logicalPath} is unavailable`)
      const colorSpace = binding.colorRead === "srgb" ? THREE.SRGBColorSpace : THREE.NoColorSpace
      const texture = textureResidency.retain(`cubemap:${input.sourceSha256}:${colorSpace}`, () =>
        textureFromAuthoredCubemap(input, colorSpace))
      if (!(texture instanceof THREE.CubeTexture)) {
        throw new RenderingError("IdentityMismatch", `authored model cubemap ${binding.logicalPath} differs`)
      }
      return texture
    }
    const createWorldTexture = (
      source: Readonly<{ logicalPath: string; width: number; height: number }>,
      colorSpace: string,
    ): THREE.Texture => {
      const identity = source.logicalPath.toLowerCase()
      const authored = request.environment?.authoredTextures.get(identity)
      if (!authored || authored.width !== source.width || authored.height !== source.height) {
        throw new RenderingError("MissingInput", `authored world texture ${source.logicalPath} is unavailable`)
      }
      return retainAuthoredTexture(authored, colorSpace)
    }
    const createBase = (resolved: RuntimeMaterial, identity: string): THREE.Texture | undefined => {
      const source = resolved.baseTexture
      if (!source) {
        diagnostics.push(diagnostic("MissingMaterial", identity, "resolved base texture is unavailable"))
        return undefined
      }
      return createWorldTexture(source, THREE.SRGBColorSpace)
    }
    const loadCubemap = (fact: EnvironmentInput["cubemapFacts"][number]): THREE.CubeTexture => {
      const existing = cubemapTextures.get(fact.index)
      if (existing) return existing
      if (!Number.isSafeInteger(fact.index) || fact.index < 0 || !HASH.test(fact.sha256) || !finite(fact.origin)) {
        throw new RenderingError("MalformedInput", "cubemap occurrence input is invalid")
      }
      const authored = request.environment?.authoredTextures.get(fact.logicalPath.toLowerCase())
      if (!authored || authored.sourceSha256 !== fact.sha256 || authored.width !== fact.width || authored.height !== fact.height) {
        throw new RenderingError("IdentityMismatch", "cubemap authored texture identity differs")
      }
      const texture = textureResidency.retain(`cubemap:${authored.sourceSha256}:${THREE.NoColorSpace}`, () =>
        textureFromAuthoredCubemap(authored, THREE.NoColorSpace)) as THREE.CubeTexture
      cubemapTextures.set(fact.index, texture)
      return texture
    }
    const modelCubemap = (identity: string): THREE.CubeTexture | undefined => {
      const fact = cubemapFacts.get(identity.toLowerCase())
      return fact ? loadCubemap(fact) : undefined
    }

    const waterSurfacePlanes = new Map<number, readonly [number, number, number, number]>()
    for (const surface of request.environment?.waterSurfaceFacts ?? []) {
      if (!surface.selected) continue
      if (waterSurfacePlanes.has(surface.face)) {
        throw new RenderingError("IdentityMismatch", `Water surface ${surface.face} has duplicate selected planes`)
      }
      waterSurfacePlanes.set(surface.face, surface.plane)
    }

    const authoredFrames = (authored: AuthoredTextureInput, colorSpace: string): AuthoredTextureFrames => {
      const quality = this.textureQuality
      const identity = `${authored.sourceSha256}:${authored.sourceFormat ?? "rgba"}:${authored.scalarEncoding}:${colorSpace}`
      let frames = animatedTextureFrames.get(identity)
      if (!frames) {
        frames = Object.freeze({
          frameCount: authored.frameCount,
          select(frame: number, consumer: string): THREE.Texture {
            if (!Number.isSafeInteger(frame) || frame < 0 || frame >= authored.frameCount) {
              throw new RenderingError("MalformedInput", `authored texture ${authored.logicalPath} selected an invalid frame`)
            }
            return textureResidency.select(identity, frame, consumer, () =>
              textureFromAuthored(authored, colorSpace, frame, quality), authored.frameCount)
          },
        })
        animatedTextureFrames.set(identity, frames)
      }
      return frames
    }

    for (const [identity, state] of request.environment?.refractMaterials ?? []) {
      const key = identity.toLowerCase()
      if (key !== state.identity.toLowerCase() || refractMaterials.has(key)
        || state.normal.role !== 8 || state.normal.disposition !== "source" || state.normal.colorRead !== "linear") {
        throw new RenderingError("MalformedInput", "authored Refract material identity is invalid")
      }
      const normal = request.environment!.authoredTextures.get(state.normal.logicalPath.toLowerCase())
      if (!normal || !Number.isSafeInteger(state.normalFrame)
        || state.normalFrame < 0 || state.normalFrame >= normal.frameCount) {
        throw new RenderingError("MissingInput", `authored Refract normal ${state.normal.logicalPath} is unavailable`)
      }
      const normalFrames = authoredFrames(normal, THREE.NoColorSpace)
      const normalConsumer = `refract:${key}`
      const overlay = createSourceRefractMaterial({
        state: {
          refractAmount: state.refractAmount,
          refractTint: state.refractTint,
          blurAmount: state.blurAmount,
          ignoreDepth: state.ignoreDepth,
        },
        normal: normalFrames.select(state.normalFrame, normalConsumer),
      })
      disposables.add(overlay.material)
      refractMaterials.set(key, Object.freeze({ ...overlay, normalFrames, normalConsumer, state }))
    }

    const createWaterMaterial = (identity: string, geometry: THREE.BufferGeometry): WaterMaterialResource => {
      const key = identity.toLowerCase()
      const existing = waterMaterials.get(key)
      if (existing) return existing
      const state = request.environment?.waterMaterials.get(key)
      if (!state) throw new RenderingError("MissingInput", `Water material ${identity} is unavailable`)
      const expectedShader = this.configuration.lightingProfile === "hdr" ? "dx9-hdr" : "dx90"
      if (state.shader !== expectedShader) {
        throw new RenderingError("IdentityMismatch", `Water material ${identity} selected ${state.shader} instead of ${expectedShader}`)
      }

      const normalRequest = state.textures.find((texture) => texture.role === 8 && texture.disposition === "source")
      if (!normalRequest?.logicalPath || normalRequest.colorRead !== "linear") {
        throw new RenderingError("MissingInput", `Water linear normal texture ${identity} is unavailable`)
      }
      const authored = request.environment?.authoredTextures.get(normalRequest.logicalPath.toLowerCase())
      if (!authored) {
        throw new RenderingError("MissingInput", `Water authored normal ${normalRequest.logicalPath} is unavailable`)
      }
      if (!Number.isSafeInteger(state.normalFrame.value) || state.normalFrame.value < 0 || state.normalFrame.value >= authored.frameCount) {
        throw new RenderingError("MalformedInput", `Water normal frame ${state.normalFrame.value} is outside its authored chain`)
      }
      const normalFrames = authoredFrames(authored, THREE.NoColorSpace)
      const normalConsumer = `water:${key}`
      const initialNormal = normalFrames.select(state.normalFrame.value, normalConsumer)

      const reflectionBinding = state.textures.find((texture) => texture.role === 16)
      const refractionBinding = state.textures.find((texture) => texture.role === 17)
      if (reflectionBinding && reflectionBinding.disposition !== "render-target") {
        throw new RenderingError("UnsupportedFeature", `Water reflection ${identity} is not an authored render target`)
      }
      if (refractionBinding && refractionBinding.disposition !== "render-target") {
        throw new RenderingError("UnsupportedFeature", `Water refraction ${identity} is not an authored render target`)
      }
      const environmentBinding = state.textures.find((texture) => texture.role === 12)
      let cubemap: THREE.CubeTexture | null = null
      if (environmentBinding?.logicalPath) {
        const selectedPath = this.configuration.lightingProfile === "hdr"
          ? environmentBinding.logicalPath.replace(/\.vtf$/i, ".hdr.vtf")
          : environmentBinding.logicalPath
        const fact = request.environment?.cubemapFacts.find((candidate) =>
          candidate.logicalPath.toLowerCase() === selectedPath.toLowerCase(),
        )
        if (!fact) throw new RenderingError("MissingInput", `Water cubemap ${selectedPath} is unavailable`)
        cubemap = loadCubemap(fact)
      } else if (environmentBinding?.disposition === "environment") {
        throw new RenderingError("MissingInput", `Water material ${identity} has no selected local cubemap identity`)
      }

      const shaderState = (mode: "expensive" | "cheap"): SourceWaterShaderState => Object.freeze({
        profile: this.configuration.lightingProfile,
        mode,
        aboveWater: state.aboveWater.value,
        reflectAmount: state.reflectAmount.value,
        refractAmount: state.refractAmount.value,
        reflectTint: state.reflectTint.value,
        refractTint: state.refractTint.value,
        fogColor: state.fog.color.value,
        fogStart: state.fog.start.value,
        fogEnd: state.fog.end.value,
        blurRefraction: state.blurRefraction.value,
        hasBaseTexture: state.textures.some((texture) => texture.role === 0),
        cheapBlend: mode === "cheap" && !state.forceCheap.value,
        cheapStart: state.cheapStart.value,
        cheapEnd: state.cheapEnd.value,
        reflectionBlendFactor: state.reflectionBlendFactor.value,
        fresnelEnabled: state.fresnel.cheapEnabled,
        linearLightScale: this.configuration.lightingProfile === "hdr" ? this.#exposure.snapshot().current : 1,
        environmentScale: this.configuration.lightingProfile === "hdr" ? 16 : 1,
      })

      const selectedMode = state.forceCheap.value || (!reflectionBinding && !refractionBinding) ? "cheap" : "expensive"
      const primary = createSourceWaterMaterial({
        geometry,
        state: shaderState(selectedMode),
        normal: initialNormal,
        reflection: reflectionBinding && !state.forceCheap.value ? waterTarget("reflection").texture : null,
        refraction: refractionBinding && !state.forceCheap.value ? waterTarget("refraction").texture : null,
        cubemap,
        refractionDepthEncoding: refractionBinding && state.aboveWater.value ? "source-water-fog-alpha" : null,
        linearLightScale: exposureUniform,
      })
      disposables.add(primary.material)
      let cheapMaterial: THREE.MeshBasicNodeMaterial | null = null
      let cheapNormalNode: ReturnType<typeof TSL.texture> | null = null
      if (selectedMode === "expensive" && cubemap) {
        const cheap = createSourceWaterMaterial({
          geometry,
          state: shaderState("cheap"),
          normal: initialNormal,
          reflection: null,
          refraction: refractionBinding ? waterTarget("refraction").texture : null,
          cubemap,
          refractionDepthEncoding: refractionBinding && state.aboveWater.value ? "source-water-fog-alpha" : null,
          linearLightScale: exposureUniform,
        })
        cheapMaterial = cheap.material
        cheapNormalNode = cheap.normalNode
        disposables.add(cheap.material)
      }
      const fog = state.fog.enabled?.value
        ? new THREE.Fog(
            new THREE.Color(
              sourceShaderGammaToLinear(state.fog.color.value[0]),
              sourceShaderGammaToLinear(state.fog.color.value[1]),
              sourceShaderGammaToLinear(state.fog.color.value[2]),
            ),
            state.fog.start.value,
            state.fog.end.value,
          )
        : null
      const resource = Object.freeze({
        material: primary.material,
        cheapMaterial,
        normalFrames,
        normalConsumer,
        normalNode: primary.normalNode,
        cheapNormalNode,
        fog,
        state,
      })
      waterMaterials.set(key, resource)
      return resource
    }
    const createWorldEnvironment = (
      resolved: RuntimeMaterial,
      batch: RuntimeBatch,
      geometry: THREE.BufferGeometry,
    ): SourceLightmappedEnvironmentNode | undefined => {
      const identity = resolved.logicalPath.toLowerCase()
      const state = request.environment?.worldMaterials.get(identity)
      if (!state) return undefined
      if (state.mapMaterial !== batch.material || state.shader !== "lightmapped-generic") {
        throw new RenderingError("IdentityMismatch", `animated world material ${resolved.logicalPath} has a different shader or map identity`)
      }
      const existing = worldMaterials.get(identity)
      if (existing) {
        if (!state.environmentMap) return { normalNode: existing.normalNode, specular: TSL.vec3(0) }
        throw new RenderingError("IdentityMismatch", `animated world material ${resolved.logicalPath} has multiple independent world batches`)
      }
      const binding = state.textures.find((texture) => texture.role === 7 && texture.disposition === "source")
      if (!binding?.logicalPath || binding.colorRead !== "linear") {
        throw new RenderingError("MissingInput", `animated world material ${resolved.logicalPath} has no authored linear bump texture`)
      }
      const authored = request.environment?.authoredTextures.get(binding.logicalPath.toLowerCase())
      if (!authored || binding.initialFrame === null || binding.initialFrame < 0 || binding.initialFrame >= authored.frameCount) {
        throw new RenderingError("MissingInput", `animated world material ${resolved.logicalPath} has an incomplete authored bump frame chain`)
      }
      if (!state.environmentMap) {
        throw new RenderingError("MissingInput", `animated world material ${resolved.logicalPath} has no authored environment state`)
      }
      const environmentBinding = state.textures.find((texture) => texture.role === 12)
      if (!environmentBinding?.logicalPath) {
        throw new RenderingError("MissingInput", `animated world material ${resolved.logicalPath} has no selected authored cubemap`)
      }
      const selectedPath = this.configuration.lightingProfile === "hdr"
        ? environmentBinding.logicalPath.replace(/\.vtf$/i, ".hdr.vtf")
        : environmentBinding.logicalPath
      const cubemap = request.environment?.cubemapFacts.find((fact) =>
        fact.logicalPath.toLowerCase() === selectedPath.toLowerCase(),
      )
      if (!cubemap) {
        throw new RenderingError("MissingInput", `animated world material ${resolved.logicalPath} has no selected cubemap ${selectedPath}`)
      }
      const surfacePlanes = new Map(waterSurfacePlanes)
      for (let triangle = 0; triangle < batch.faces.length; triangle += 1) {
        const face = batch.faces[triangle]!
        if (surfacePlanes.has(face)) continue
        const vertex = batch.indices[triangle * 3]!
        const offset = vertex * 3
        const normal = [
          batch.normals[offset]!,
          batch.normals[offset + 1]!,
          batch.normals[offset + 2]!,
        ] as const
        if (!normal.every(Number.isFinite) || Math.hypot(...normal) === 0) {
          throw new RenderingError("MalformedInput", `animated world material ${resolved.logicalPath} has no authored face normal`)
        }
        const distance = normal[0] * batch.positions[offset]!
          + normal[1] * batch.positions[offset + 1]!
          + normal[2] * batch.positions[offset + 2]!
        surfacePlanes.set(face, [normal[0], normal[1], normal[2], distance])
      }
      const tangents = sourceWaterTangentAttributes({
        positions: batch.positions,
        normals: batch.normals,
        uv: batch.uv,
        indices: batch.indices,
        faces: batch.faces,
        surfacePlanes,
      })
      geometry.setAttribute("sourceTangentS", new THREE.BufferAttribute(tangents.tangentS, 3))
      geometry.setAttribute("sourceTangentT", new THREE.BufferAttribute(tangents.tangentT, 3))
      const frames = authoredFrames(authored, THREE.NoColorSpace)
      const normalConsumer = `world:${identity}`
      const environment = createSourceLightmappedEnvironmentNode({
        geometry,
        normal: frames.select(binding.initialFrame, normalConsumer),
        cubemap: loadCubemap(cubemap),
        state: Object.freeze({
          tint: state.environmentMap.tint,
          contrast: state.environmentMap.contrast,
          saturation: state.environmentMap.saturation,
          fresnelReflection: state.fresnelReflection,
          environmentScale: this.configuration.lightingProfile === "hdr" ? 16 : 1,
        }),
      })
      worldMaterials.set(identity, Object.freeze({
        mapMaterial: state.mapMaterial,
        normalFrames: frames,
        normalConsumer,
        normalNode: environment.normalNode,
        state,
      }))
      return environment
    }

    const createWorldMesh=(batch:RuntimeBatch):THREE.Mesh|null=>{
        const geometry = new THREE.BufferGeometry()
        geometry.setAttribute("position", new THREE.BufferAttribute(batch.positions, 3))
        geometry.setAttribute("normal", new THREE.BufferAttribute(batch.normals, 3))
        geometry.setAttribute("uv", new THREE.BufferAttribute(batch.uv, 2))
        geometry.setAttribute("uv1", new THREE.BufferAttribute(batch.lightmapUv, 2))
        geometry.setAttribute("lightmapKind", new THREE.BufferAttribute(batch.lightmapKind, 1))
        geometry.setAttribute("displacementAlpha", new THREE.BufferAttribute(batch.displacementAlpha, 1))
        const index = new THREE.BufferAttribute(batch.indices.slice(), 1)
        index.setUsage(THREE.StreamDrawUsage)
        geometry.setIndex(index)
        geometry.computeBoundingSphere()
        disposables.add(geometry)
        const resolved = map.materials[batch.material]!
        const identity = resolved.logicalPath
        const materialState = materialStates.get(identity.toLowerCase())
        if (materialState?.noDraw) return null
        if (resolved.shader === 5) {
          const tangents = sourceWaterTangentAttributes({
            positions: batch.positions,
            normals: batch.normals,
            uv: batch.uv,
            indices: batch.indices,
            faces: batch.faces,
            surfacePlanes: waterSurfacePlanes,
          })
          geometry.setAttribute("sourceTangentS", new THREE.BufferAttribute(tangents.tangentS, 3))
          geometry.setAttribute("sourceTangentT", new THREE.BufferAttribute(tangents.tangentT, 3))
          const resource = createWaterMaterial(identity, geometry)
          const mesh = new THREE.Mesh(geometry, resource.material)
          mesh.userData.materialIdentity = identity
          waterMeshes.push(Object.freeze({ mesh, materialIdentity: identity.toLowerCase() }))
          return mesh
        }
        const baseTexture = createBase(resolved, identity)
        const secondTexture = resolved.secondTexture ? createWorldTexture(resolved.secondTexture, THREE.SRGBColorSpace) : undefined
        const detailTexture = resolved.detail ? createWorldTexture(resolved.detail.texture, THREE.NoColorSpace) : undefined
        const kinds = new Set(batch.lightmapKind)
        const requiresNormal = kinds.has(2)
        const requiresSsbump = kinds.has(3)
        const supplied = directionalGpu.get(identity.toLowerCase())
        if (
          (requiresNormal && supplied?.input.kind !== "normal") ||
          (requiresSsbump && supplied?.input.kind !== "ssbump")
        ) {
          diagnostics.push(
            diagnostic(
              "MissingDirectionalInput",
              identity,
              `a ${requiresSsbump ? "ssbump" : "normal"} texture plane is required`,
            ),
          )
        }
        const environment = createWorldEnvironment(resolved, batch, geometry)
        const material = worldNodeMaterial(
          resolved,
          identity,
          baseTexture,
          secondTexture,
          detailTexture,
          lightmapTextures,
          supplied?.texture,
          supplied?.input.kind,
          supplied?.input.uvTransform,
          exposureUniform,
          waterFogUniforms,
          environment,
          materialState,
        )
        disposables.add(material)
        const mesh = new THREE.Mesh(geometry, material)
        mesh.matrixAutoUpdate = false
        mesh.updateMatrix()
        mesh.userData.materialIdentity = identity
        return mesh
    }
    try {
      for (const batch of map.batches) {
        const mesh = createWorldMesh(batch)
        if (!mesh) continue
        const index = mesh.geometry.getIndex()
        if (!index) throw new RenderingError("MalformedInput", "world batch has no index buffer")
        const transparent = (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).some((material) => material.transparent)
        const animated = map.materials[batch.material]?.shader === 5
          || worldMaterials.has(String(mesh.userData.materialIdentity).toLowerCase())
        const bundled = !transparent && !animated
        const slot = worldBatches.length
        if (bundled) {
          worldDraws.attach(mesh.geometry, slot, batch.indices.length)
          mesh.frustumCulled = false
        }
        worldBatches.push({ mesh, faces: batch.faces, sourceIndices: batch.indices, index, transparent, bundled })
        ;(transparent ? mainTransparentWorld : animated ? mainAnimatedWorld : worldBundle).add(mesh)

        const skyGeometry = new THREE.BufferGeometry()
        for (const name of Object.keys(mesh.geometry.attributes)) {
          skyGeometry.setAttribute(name, mesh.geometry.getAttribute(name))
        }
        const skyIndex = new THREE.BufferAttribute(batch.indices.slice(), 1).setUsage(THREE.StreamDrawUsage)
        skyGeometry.setIndex(skyIndex)
        skyGeometry.boundingBox = mesh.geometry.boundingBox
        skyGeometry.boundingSphere = mesh.geometry.boundingSphere
        disposables.add(skyGeometry)
        const skyMesh = new THREE.Mesh(skyGeometry, mesh.material)
        skyMesh.matrixAutoUpdate = false
        skyMesh.updateMatrix()
        skyMesh.userData.materialIdentity = mesh.userData.materialIdentity
        skyMesh.userData.skyWater = map.materials[batch.material]?.shader === 5
        if (bundled) {
          skyWorldDraws.attach(skyGeometry, slot, batch.indices.length)
          skyMesh.frustumCulled = false
        }
        skyWorldBatches.push({ mesh: skyMesh, faces: batch.faces, sourceIndices: batch.indices, index: skyIndex, transparent, bundled })
        ;(transparent ? skyTransparentWorld : animated ? skyAnimatedWorld : skyWorldBundle).add(skyMesh)
      }
      for(const model of map.brushModels){const template=new THREE.Group();for(const batch of model.batches){const mesh=createWorldMesh(batch);if(mesh)template.add(mesh)}brushModelTemplates.set(model.index,template)}

      const environmentTextures = new Map<string, THREE.Texture>()
      const authoredEnvironmentMaterials=new Set<string>()
      for (const texture of request.environment?.textures ?? []) {
        const authored=request.environment?.authoredTextures.get(texture.logicalPath.toLowerCase()),value=authored?retainAuthoredTexture(authored,THREE.SRGBColorSpace):disposables.add(textureFromRgba(texture, THREE.SRGBColorSpace, materialStates.get(texture.material.toLowerCase())))
        if(authored)authoredEnvironmentMaterials.add(texture.material.toLowerCase())
        environmentTextures.set(texture.material.toLowerCase(), value)
      }
      const projectedMarkMaterials=disposables.add(new ProjectedMarkMaterials(identity=>{
        const state=materialStates.get(identity),texture=environmentTextures.get(identity)
        if(!state||!texture)throw new RenderingError("MissingInput",`projected mark state or texture ${identity} is unavailable`)
        if(!authoredEnvironmentMaterials.has(identity))requireMipInputs(identity,state)
        const material=new THREE.MeshBasicNodeMaterial(materialOptions({logicalPath:identity,width:1,height:1,shader:3,features:1,textureRole:0},state))
        material.colorNode=sourceFragmentColor(TSL.texture(texture,TSL.uv()),state,waterFogUniforms)
        material.toneMapped=false;material.name=identity;return material
      }))
      for (const mark of request.environment?.markRecords ?? []) {
        if (mark.status !== 0 || !mark.enabled) continue
        const identity = mark.material.toLowerCase()
        if (environmentTextures.has(identity)) continue
        const resolved = map.materials.find(material => material.logicalPath.toLowerCase() === identity)
        if (!resolved?.baseTexture) throw new RenderingError("MissingInput", `projected mark ${identity} has no authoritative base texture`)
        const texture = createBase(resolved, identity)
        if (!texture) throw new RenderingError("MissingInput", `projected mark ${identity} base texture is unavailable`)
        environmentTextures.set(identity, texture)
        authoredEnvironmentMaterials.add(identity)
      }
      combatDecalTexture=environmentTextures.get("materials/decals/decals_mod2x.vmt")??null
      const cubemapIdentities = new Set<number>()
      for (const fact of request.environment?.cubemapFacts ?? []) {
        if (cubemapIdentities.has(fact.index)) {
          throw new RenderingError("MalformedInput", "cubemap occurrence input is invalid")
        }
        cubemapIdentities.add(fact.index)
      }
      if(request.environment?.sky){
        const semantics=["right","left","back","front","up","down"] as const
        const faces=new Map<number,(typeof request.environment.sky.faces)[number]>()
        for(const face of request.environment.sky.faces){
          if(face.face<0||face.face>5||faces.has(face.face))throw new RenderingError("MalformedInput","2D sky face identity is invalid")
          faces.set(face.face,face)
        }
        if(faces.size!==6)throw new RenderingError("MissingInput","2D sky requires six semantic faces")
        if([...faces.values()].every(face=>face.encoding)){
          skyGroup=new THREE.Group()
          skyGroup.renderOrder=-1
          const geometryFaces=buildSourceSkyGeometry([0,0,0],1)
          for(let faceIndex=0;faceIndex<6;faceIndex+=1){
            const face=faces.get(faceIndex)!,geometryFace=geometryFaces.find(value=>value.face===semantics[faceIndex])!
            if(face.selectedTextures.length!==1)throw new RenderingError("UnsupportedFeature","the supplied 2D-sky shader encoding requires one selected texture")
            const selected=face.selectedTextures[0]!,authored=request.environment.authoredTextures.get(selected.logicalPath.toLowerCase())
            if(!authored||authored.sourceSha256!==selected.sha256)throw new RenderingError("IdentityMismatch","2D sky authored texture identity differs")
            const texture=face.encoding==="hdr-rgbs"?textureResidency.retain(`${authored.sourceSha256}:sky-hdr:0`,()=>textureFromAuthored(authored,THREE.NoColorSpace)):retainAuthoredTexture(authored,face.encoding==="srgb"?THREE.SRGBColorSpace:THREE.NoColorSpace)
            if(face.encoding==="hdr-rgbs"){texture.minFilter=THREE.NearestMipmapNearestFilter;texture.magFilter=THREE.NearestFilter;texture.needsUpdate=true}
            const geometry=new THREE.BufferGeometry(),positions=Float32Array.from(geometryFace.vertices.flatMap(vertex=>vertex.position)),uv=Float32Array.from(geometryFace.vertices.flatMap(vertex=>vertex.uv))
            geometry.setAttribute("position",new THREE.BufferAttribute(positions,3));geometry.setAttribute("uv",new THREE.BufferAttribute(uv,2));geometry.setIndex([...geometryFace.indices]);disposables.add(geometry)
            const material=new THREE.MeshBasicNodeMaterial({depthTest:false,depthWrite:false,side:THREE.BackSide,fog:false})
            if(face.textureTransform.length!==16||!face.textureTransform.every(Number.isFinite)||face.color.length!==3||!face.color.every(Number.isFinite))throw new RenderingError("MalformedInput","2D sky shader state is invalid")
            const sourceUv=TSL.uv(),matrix=face.textureTransform,skyUv=TSL.vec2(sourceUv.x.mul(matrix[0]!).add(sourceUv.y.mul(matrix[1]!)).add(matrix[3]!),sourceUv.x.mul(matrix[4]!).add(sourceUv.y.mul(matrix[5]!)).add(matrix[7]!)),skyColor=TSL.vec3(...face.color)
            if(face.encoding==="hdr-rgbs"){
              const baseUv=skyUv,fudge=0.01/Math.max(authored.width,authored.height),increment=TSL.vec2(0.5/authored.width-fudge,0.5/authored.height-fudge),uv00=baseUv.sub(increment),uv10=TSL.vec2(baseUv.x.add(increment.x),baseUv.y.sub(increment.y)),uv01=TSL.vec2(baseUv.x.sub(increment.x),baseUv.y.add(increment.y)),uv11=baseUv.add(increment),s00=TSL.texture(texture,uv00),s10=TSL.texture(texture,uv10),s01=TSL.texture(texture,uv01),s11=TSL.texture(texture,uv11),fraction=TSL.fract(uv00.mul(TSL.vec2(authored.width,authored.height))),top=TSL.mix(s00.rgb.mul(s00.a),s10.rgb.mul(s10.a),fraction.x),bottom=TSL.mix(s01.rgb.mul(s01.a),s11.rgb.mul(s11.a),fraction.x)
              material.colorNode=TSL.vec4(TSL.mix(top,bottom,fraction.y).mul(8).mul(skyColor).mul(exposureUniform),1)
            }else{const sample=TSL.texture(texture,skyUv);material.colorNode=TSL.vec4(sample.rgb.mul(skyColor).mul(exposureUniform),1)}
            material.toneMapped=false;disposables.add(material)
            const mesh=new THREE.Mesh(geometry,material);mesh.userData.skyFace=geometryFace.face;mesh.renderOrder=-1;skyGroup.add(mesh)
          }
          group.add(skyGroup)
        }
      }
      for (const mark of request.environment?.markRecords ?? []) {
        if (mark.status !== 0 || !mark.enabled) continue
        const texture = environmentTextures.get(mark.material.toLowerCase())
        if (!texture) {
          throw new RenderingError("MissingInput", `projected mark texture is unavailable: ${mark.material}`)
        }
        for (const fragment of mark.fragments) {
          const geometry = new THREE.BufferGeometry()
          geometry.setAttribute("position", new THREE.BufferAttribute(fragment.positions, 3))
          geometry.setAttribute("normal", new THREE.BufferAttribute(fragment.normals, 3))
          geometry.setAttribute("uv", new THREE.BufferAttribute(fragment.uv, 2))
          geometry.setIndex(new THREE.BufferAttribute(fragment.indices, 1))
          disposables.add(geometry)
          const identity=mark.material.toLowerCase()
          const material=projectedMarkMaterials.fragment(identity)
          const mesh = new THREE.Mesh(geometry, material)
          mesh.userData.materialIdentity=identity
          mesh.renderOrder = mark.renderOrder
          mesh.visible = false
          if (fragment.visibility.kind === "world") {
            mesh.matrixAutoUpdate = false
            mesh.updateMatrix()
          }
          projectedMarks.push({ mesh, face: fragment.face, sourceIndex: mark.sourceIndex, visibility: fragment.visibility })
          projectedMarkGroup.add(mesh)
        }
      }

      for (const model of map.models) {
        const priorTemplate = retained?.modelTemplates.get(model.logicalPath)
        if (priorTemplate && retained!.modelPipelineKeys.has(model.logicalPath) && model.materials.every(material => request.modelMaterials?.get(material.logicalPath.toLowerCase())?.shader !== "unlit-two-texture")) {
          const resources = retained!.modelTemplateResources.get(model.logicalPath)!
          modelTemplates.set(model.logicalPath, priorTemplate)
          if (handoffProfile) handoffProfile.counters.retainedModelTemplates += 1
          modelTemplateResources.set(model.logicalPath, resources)
          modelPipelineKeys.add(model.logicalPath)
          borrowedResources.push(...resources)
          for (const material of model.materials) {
            const identity = material.logicalPath.toLowerCase(), textures = retained!.modelLightingTextures.get(identity)
            if (textures) modelLightingTextures.set(identity, { ...textures,
              normal: textures.normal ? () => createModelTexture(identity, 7)?.texture : undefined })
          }
          continue
        }
        const template = new THREE.Group()
        const resources: (THREE.BufferGeometry | THREE.Material)[] = []
        modelTemplateResources.set(model.logicalPath, resources)
        for (let primitiveIndex=0;primitiveIndex<model.primitives.length;primitiveIndex+=1) {
          const primitive=model.primitives[primitiveIndex]!
          const geometry = new THREE.BufferGeometry()
          geometry.setAttribute("position", new THREE.BufferAttribute(primitive.positions, 3))
          geometry.setAttribute("normal", new THREE.BufferAttribute(primitive.normals, 3))
          geometry.setAttribute("uv", new THREE.BufferAttribute(primitive.uv, 2))
          geometry.setIndex(new THREE.BufferAttribute(primitive.indices, 1))
          geometry.computeBoundingSphere()
          disposables.add(geometry)
          resources.push(geometry)
          const bindGeometry = new THREE.BufferGeometry()
          bindGeometry.setAttribute("position", new THREE.BufferAttribute(primitive.bindPositions, 3))
          bindGeometry.setAttribute("normal", new THREE.BufferAttribute(primitive.bindNormals, 3))
          bindGeometry.setAttribute("tangent", new THREE.BufferAttribute(primitive.bindTangents, 4))
          bindGeometry.setAttribute("skinIndex", new THREE.BufferAttribute(primitive.boneIndices, 4))
          bindGeometry.setAttribute("skinWeight", new THREE.BufferAttribute(primitive.boneWeights, 4))
          bindGeometry.setAttribute("uv", geometry.getAttribute("uv"))
          bindGeometry.setIndex(geometry.getIndex())
          bindGeometry.boundingSphere = geometry.boundingSphere
          disposables.add(bindGeometry)
          resources.push(bindGeometry)
          SOURCE_MODEL_BIND_GEOMETRY.set(geometry, Object.freeze({ geometry: bindGeometry, palette: primitive.bonePalette }))
          const resolved = model.materials[primitive.material]!
          const materialState = materialStates.get(resolved.logicalPath.toLowerCase())
          if (materialState?.noDraw) continue
           const typedMaterial = request.modelMaterials?.get(resolved.logicalPath.toLowerCase())
           const baseTexture = typedMaterial?.shader === "eye-refract" || typedMaterial?.shader === "eyes"
             ? createModelTexture(resolved.logicalPath, 8, "model")
             : createModelTexture(resolved.logicalPath, 0)
           if ((typedMaterial?.shader === "eye-refract" || typedMaterial?.shader === "eyes") && !baseTexture) {
             throw new RenderingError("MissingInput", `authored Studio eye iris ${resolved.logicalPath} is unavailable`)
           }
           if (typedMaterial && typedMaterial.shader !== "unlit-generic" && typedMaterial.shader !== "unlit-two-texture" && typedMaterial.shader !== "modulate") {
             const warp = createModelTexture(resolved.logicalPath, 6, "model")?.texture
             const exponent = createModelTexture(resolved.logicalPath, 5, "model")?.texture
             const iris = typedMaterial.shader === "eye-refract" || typedMaterial.shader === "eyes" ? baseTexture?.texture : undefined
             const ambientOcclusion = typedMaterial.shader === "eye-refract"
               ? createModelTexture(resolved.logicalPath, 10, "model")?.texture
               : undefined
             const environment = createModelCubemap(resolved.logicalPath)
              const normal = typedMaterial.state.kind === "vertex-lit-generic" && typedMaterial.state.cloak?.enabled ? () => createModelTexture(resolved.logicalPath, 7)?.texture : undefined
              if (warp || exponent || iris || ambientOcclusion || environment || normal) {
               modelLightingTextures.set(resolved.logicalPath.toLowerCase(), Object.freeze({
                  warp,
                  normal,
                 exponent,
                 iris,
                 ambientOcclusion,
                 environment,
               }))
             }
           }
          const material = new THREE.MeshBasicNodeMaterial({
            ...materialOptions(resolved, materialState),
            side: sourceModelSide(request.modelFacing!.get(model.logicalPath.split("#")[0]!.toLowerCase())!),
          })
           let sampled=baseTexture?TSL.texture(baseTexture.texture,TSL.uv()):undefined
           if(sampled&&baseTexture?.input.sourceFormat===1)sampled=sampled.abgr
           else if(sampled&&baseTexture?.input.sourceFormat===11)sampled=sampled.gbar
           else if(sampled&&baseTexture?.input.sourceFormat===12)sampled=sampled.bgra
           else if(sampled&&baseTexture?.input.sourceFormat===16)sampled=TSL.vec4(sampled.bgr,1)
            const first = selectDiagnosticModelBase(baseTexture !== undefined) === "authored-texture"
              ? sampled!
              : TSL.vec4(TSL.color(debugColor(`diagnostic:${resolved.logicalPath}`)), 1)
             // Static base sampling is texture/interpretation state, not an
             // occurrence or primitive. Sharing it also lets equivalent VMT
             // graphs hit the node-builder cache before WGSL generation.
             const baseKey = baseTexture && typedMaterial?.shader !== "unlit-two-texture"
               ? `${baseTexture.texture.uuid}:${baseTexture.input.sourceFormat}` : undefined
             let base = baseKey ? modelBaseSamples.get(baseKey) : undefined
             if (!base) {
               base = first
               if (baseKey) modelBaseSamples.set(baseKey, base)
             }
             if (baseKey && baseTexture) SOURCE_MODEL_BASE_TEXTURE.set(base, baseTexture)
            if (typedMaterial?.shader === "unlit-two-texture") {
              const second = createModelTexture(resolved.logicalPath, 6)
              if (!second) throw new RenderingError("MissingInput", `authored second model texture ${resolved.logicalPath} is unavailable`)
              const scrollX = TSL.uniform(0, "float")
              const scrollY = TSL.uniform(0, "float")
              const frames = authoredFrames(second.input, THREE.SRGBColorSpace)
              const consumer = `panel:${model.logicalPath.toLowerCase()}:${resolved.logicalPath.toLowerCase()}`
              frames.select(0, consumer)
              const animation: ModelPanelMaterialAnimation = {
                texture: second.texture,
                frames,
                consumer,
                frameRate: typedMaterial.state.secondFrameRate,
                scrollRate: typedMaterial.state.secondScrollRate,
                scrollAngle: typedMaterial.state.secondScrollAngle,
                scrollX,
                scrollY,
                frame: 0,
              }
              const key = model.logicalPath.toLowerCase()
              const records = modelPanelMaterialAnimations.get(key) ?? []
              records.push(animation)
              modelPanelMaterialAnimations.set(key, records)
              const shifted = TSL.uv().add(TSL.vec2(scrollX, scrollY))
              let sampledSecond = TSL.texture(second.texture, shifted)
              if (second.input.sourceFormat === 1) sampledSecond = sampledSecond.abgr
              else if (second.input.sourceFormat === 11) sampledSecond = sampledSecond.gbar
              else if (second.input.sourceFormat === 12) sampledSecond = sampledSecond.bgra
              else if (second.input.sourceFormat === 16) sampledSecond = TSL.vec4(sampledSecond.bgr, 1)
              base = TSL.vec4(first.rgb.mul(sampledSecond.rgb), 1)
            }
           if (typedMaterial?.state.kind === "unlit-generic") {
             const modulation = typedMaterial.state.colorModulation
             if (!Array.isArray(modulation) || modulation.length !== 3 || !modulation.every(Number.isFinite)) throw new RenderingError("MalformedInput", "unlit model color modulation is invalid")
             if (modulation.some(value => value !== 1)) {
               base = TSL.vec4(base.rgb.mul(TSL.uniform(new THREE.Vector3(...modulation))), base.a)
             }
           }
           if (typedMaterial?.shader === "modulate") {
            const rgba = TSL.clamp(base, 0, 1)
            base = TSL.vec4(TSL.mix(TSL.vec3(0.5), rgba.rgb, rgba.a), rgba.a)
          }
          SOURCE_MODEL_BASE_COLOR.set(geometry, base)
          SOURCE_MODEL_BASE_COLOR.set(bindGeometry, base)
          material.colorNode = sourceFragmentColor(base, materialState, waterFogUniforms)
          material.toneMapped = false
          disposables.add(material)
          resources.push(material)
          const mesh = new THREE.Mesh(geometry, material)
          mesh.userData.primitiveMaterial = primitive.material
          mesh.userData.sourcePrimitive=primitiveIndex
          mesh.userData.materialIdentity=resolved.logicalPath
          template.add(mesh)
        }
        modelTemplates.set(model.logicalPath, template)
      }
      // VHV and unlit occurrences share the exact authored material/facing/pass.
      // Fade is draw data, not graph identity; runtime-lit graphs still capture
      // their own authored lighting. Opaque batching remains unchanged.
      if(request.staticProps){const props=request.staticProps,profile=this.configuration.lightingProfile==="hdr"?1:0,sharedStaticMaterials=new Map<string,THREE.MeshBasicNodeMaterial>(),fadedMaterials=new Map<THREE.MeshBasicNodeMaterial,THREE.MeshBasicNodeMaterial>()
        for(let propIndex=0;propIndex<props.count;propIndex+=1){const modelIdentity=props.models[props.presentationModel[propIndex]!]!,key=modelKey(modelIdentity,props.skin[propIndex]!),template=modelTemplates.get(key);if(!template)throw new RenderingError("MissingInput",`static-prop model ${key} is unavailable`)
          if(props.body[propIndex]!==0)throw new RenderingError("UnsupportedFeature","nonzero static-prop body selection is unavailable")
          const instance=template.clone(true),lightingKind=props.lightingKind[propIndex]!,fadeUniform=TSL.uniform(1,"float"),meshes:THREE.Mesh[]=[];instance.traverse(value=>{if(value instanceof THREE.Mesh)meshes.push(value)})
          const fadeBindings:StaticPropFadeBinding[]=[]
          let colorMeshes:StaticPropInput["vhv"][number]["meshes"]=Object.freeze([])
          if(lightingKind===0){const objectIndex=props.vhvObjects[propIndex*2+profile]!,object=props.vhv[objectIndex];if(!object||object.occurrence!==props.source[propIndex]||object.profile!==profile||object.model!==props.dictionaryModel[propIndex])throw new RenderingError("IdentityMismatch","static-prop VHV occurrence identity differs");colorMeshes=Object.freeze(object.meshes.filter(mesh=>mesh.lod===props.lod[propIndex]))}
          let colorIndex=0
          for(const mesh of meshes){if(lightingKind===0){const sourceGeometry=mesh.geometry,geometry=new THREE.BufferGeometry();for(const name of Object.keys(sourceGeometry.attributes))geometry.setAttribute(name,sourceGeometry.getAttribute(name));geometry.setIndex(sourceGeometry.getIndex());geometry.boundingBox=sourceGeometry.boundingBox;geometry.boundingSphere=sourceGeometry.boundingSphere;const color=colorMeshes[colorIndex++];const position=geometry.getAttribute("position");if(!color||color.vertexCount!==position.count||color.colors.length!==position.count*4)throw new RenderingError("IdentityMismatch","static-prop VHV mesh order differs");geometry.setAttribute("staticLighting",new THREE.Uint8BufferAttribute(color.colors,4,true));disposables.add(geometry);mesh.geometry=geometry}
            const original=mesh.material;if(Array.isArray(original)||!(original instanceof THREE.MeshBasicNodeMaterial))throw new RenderingError("UnsupportedFeature","static-prop model material family is unavailable")
            const identity=String(mesh.userData.materialIdentity),shader=request.modelMaterials?.get(identity.toLowerCase())?.shader,unlit=shader==="unlit-generic"||shader==="unlit-two-texture",fading=(props.flags[propIndex]!&1)!==0,sharingKey=lightingKind===0||unlit?`${identity.toLowerCase()}:${original.side}:${unlit?"unlit":"vertex"}:${Number(fading)}`:undefined
            if(fading)bindStaticPropFade(mesh,fadeUniform)
            let material=sharingKey===undefined?undefined:sharedStaticMaterials.get(sharingKey)
            if(!material){
              material=original.clone()
              const base=original.colorNode??TSL.vec4(1,1,1,1)
              const state=request.modelMaterials?.get(identity.toLowerCase())?.state as Readonly<{halfLambert?:boolean;phong?:unknown}>|undefined
              const halfLambert=Boolean(state?.phong)||state?.halfLambert===true
              const rgb=unlit?base.rgb:base.rgb.mul(lightingKind===0?sourceStaticVertexLightingNode():runtimeStaticLightingNode(map,props,propIndex,halfLambert,modelLightingTextures.get(identity.toLowerCase())?.warp)).mul(exposureUniform)
              const materialState=materialStates.get(identity.toLowerCase()),sourceOpacity=materialState?.alphaOwnership.opacity?base.a:TSL.float(1)
              material.colorNode=sourceFragmentColor(TSL.vec4(rgb,sourceOpacity.mul(fading?modelLightingGraphs.staticFade:fadeUniform)),materialState,waterFogUniforms,fading)
              material.toneMapped=false
              disposables.add(material)
              if(sharingKey!==undefined)sharedStaticMaterials.set(sharingKey,material)
            }
            mesh.material=material
            if(fading){
              let faded=fadedMaterials.get(material)
              if(!faded){faded=createStaticPropFadeVariant(material);fadedMaterials.set(material,faded);disposables.add(faded)}
              fadeBindings.push({mesh,authored:material,faded})
            }
            if(fading&&handoffProfile)observeStaticPropUse(mesh,handoffProfile,sceneGeneration,props.source[propIndex]!)
          }
          if(lightingKind===0&&colorIndex!==colorMeshes.length)throw new RenderingError("IdentityMismatch","static-prop VHV mesh closure differs")
          const position=props.transform.subarray(propIndex*6,propIndex*6+3),angles=props.transform.subarray(propIndex*6+3,propIndex*6+6);sourceTransform(instance,position,angles);instance.updateMatrix();instance.matrixAutoUpdate=false;instance.visible=false;instance.userData.staticPropSource=props.source[propIndex]
          const box = new THREE.Box3().setFromObject(instance)
          const sphere = box.getBoundingSphere(new THREE.Sphere())
          const leafStart = props.leafOffsets[propIndex]!
          const leafEnd = props.leafOffsets[propIndex + 1]!
          const ownership = props.ownership[propIndex] as 0 | 1
          const lightingOrigin = Number.isFinite(props.lightingOrigin[propIndex * 3])
            ? Object.freeze([props.lightingOrigin[propIndex * 3]!, props.lightingOrigin[propIndex * 3 + 1]!, props.lightingOrigin[propIndex * 3 + 2]!] as const)
            : null
          ;(ownership === 0 ? mainStaticProps : skyStaticProps).add(instance)
          staticPropInstances.push(Object.freeze({
            object: instance,
            source: props.source[propIndex]!,
            ownership,
            leaves: props.leaves.subarray(leafStart, leafEnd),
            origin: Object.freeze([position[0]!, position[1]!, position[2]!] as const),
            lightingOrigin,
            flags: props.flags[propIndex]!,
            fadeMinimum: props.fades[propIndex * 3]!,
            fadeMaximum: props.fades[propIndex * 3 + 1]!,
            forcedFadeScale: props.fades[propIndex * 3 + 2]!,
            radius: sphere.radius,
            bounds: Object.freeze([box.min.x, box.min.y, box.min.z, box.max.x, box.max.y, box.max.z] as const),
            fadeUniform,
            fadeBindings,
            batches: [],
          }))
        }
        const candidates = new Map<string, { prop: StaticPropResource; mesh: THREE.Mesh }[]>()
        for (let index = 0; index < staticPropInstances.length; index += 1) {
          const prop = staticPropInstances[index]!
          if ((prop.flags & 1) !== 0) continue
          for (const mesh of prop.object.children) {
            if (!(mesh instanceof THREE.Mesh) || Array.isArray(mesh.material) || mesh.material.transparent) continue
            const identity = `${prop.ownership}:${props.models[props.presentationModel[index]!]!}:${props.skin[index]}:${props.lod[index]}:${props.lightingKind[index]}:${mesh.userData.sourcePrimitive}:${mesh.material.uuid}`
            let group = candidates.get(identity)
            if (!group) candidates.set(identity, group = [])
            group.push({ prop, mesh })
          }
        }
        for (const group of candidates.values()) {
          for (let start = 0; start < group.length; start += MAX_STATIC_PROPS_PER_BATCH) {
            const entries = group.slice(start, start + MAX_STATIC_PROPS_PER_BATCH)
            if (entries.length < 2) continue
            const first = entries[0]!
            const batch = createStaticPropBatch(entries.map(({ prop, mesh }) => ({
              source: prop.source,
              geometry: mesh.geometry,
              matrix: prop.object.matrix,
            })), first.mesh.material as THREE.Material)
            batch.mesh.userData.materialIdentity = first.mesh.userData.materialIdentity
            batch.mesh.userData.sourcePrimitive = first.mesh.userData.sourcePrimitive
            disposables.add(batch.mesh.geometry)
            ;(first.prop.ownership === 0 ? mainStaticProps : skyStaticProps).add(batch.mesh)
            staticPropBatches.push({ ownership: first.prop.ownership, batch })
            for (const [index, { prop, mesh }] of entries.entries()) {
              prop.batches.push({ batch, index })
              prop.object.remove(mesh)
              if (mesh.geometry.hasAttribute("staticLighting")) disposables.release(mesh.geometry)
              if (prop.object.children.length === 0) prop.object.removeFromParent()
            }
          }
        }
      }
      for (const occurrence of map.modelOccurrences) {
        const model = map.models[occurrence.model]!
        const instance = modelTemplates.get(model.logicalPath)!.clone(true)
        instance.userData.entity = occurrence.entity
        const m = occurrenceMatrices.get(occurrence.entity)!.matrix
        instance.matrix.set(m[0]!, m[1]!, m[2]!, m[3]!, m[4]!, m[5]!, m[6]!, m[7]!, m[8]!, m[9]!, m[10]!, m[11]!, 0, 0, 0, 1)
        instance.matrix.decompose(instance.position, instance.quaternion, instance.scale)
        instance.matrixAutoUpdate = false
        const inputs = modelDrawInputs.get(occurrence.entity)
        if (inputs) {
          const retained: { instance: THREE.Group; lighting?: SourceModelLightingUniforms; eyes?: Map<number, SourceModelEyeUniforms> } = { instance }
          this.#applyDynamicModelLighting(retained, {
            identity: occurrence.entity,
            model: model.logicalPath,
            position: Object.freeze([m[3]!, m[7]!, m[11]!]),
            angles: Object.freeze([0, 0, 0]),
            scale: 1,
            modelLighting: inputs.lighting,
            eyeStates: inputs.eyes,
          }, {
            materials: request.modelMaterials,
            states: materialStates,
            waterFog: waterFogUniforms,
            textures: modelLightingTextures,
            cubemap: modelCubemap,
            exposure: exposureUniform,
            graphs: modelLightingGraphs,
          })
          if (retained.lighting) modelOccurrenceLighting.push(retained.lighting)
          instance.traverse((object) => {
            if (!(object instanceof THREE.Mesh) || object.userData.dynamicMaterial !== true) return
            for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
              disposables.add(material)
            }
          })
        }
        modelOccurrenceInstances.set(occurrence.entity,instance)
        mainModelOccurrences.add(instance)
      }
      this.#buildParticleMaterials(request.particleTextures ?? [], materialStates, disposables, waterFogUniforms, particleDepth, particleTextures, particleBatchMaterials, particlePipelineMeshes, exposureUniform, map.lighting.profile === "hdr")
      if (request.legacyVisualTextures?.length) {
        const textures=new Map<string,THREE.Texture>()
        const materials = request.legacyVisualTextures.map(input => {
          const state = materialStates.get(input.material.toLowerCase())
          if (!state) throw new RenderingError("MissingInput", `Legacy visual material state ${input.material} is unavailable`)
          return Array.from({length:input.frameCount},(_,frame)=>{
            const program=input.program,key=`${input.sourceSha256}:${frame}:${program.srgb}`
            let texture=textures.get(key)
            if(!texture){texture=disposables.add(textureFromAuthored(input,program.srgb?THREE.SRGBColorSpace:THREE.NoColorSpace,frame,this.textureQuality));textures.set(key,texture)}
            const material = disposables.add(new THREE.MeshBasicNodeMaterial(materialOptions({ logicalPath:input.material,width:input.width,height:input.height,shader:2,features:1,textureRole:0 },state)))
            const sampled=TSL.texture(texture,TSL.uv()), raw=TSL.attribute("legacyColor","vec4"), color=program.vertexGamma?TSL.vec4(raw.rgb.pow(2.2),raw.a):raw
            let rgb=program.vertexRgb?sampled.rgb.mul(color.rgb):sampled.rgb
            if(program.cable){
              if(!input.normal)throw new RenderingError("MissingInput","Cable normal texture is unavailable")
              const normalKey=`${input.normal.sourceSha256}:0:false`
              let normal=textures.get(normalKey)
              if(!normal){normal=disposables.add(textureFromAuthored(input.normal,THREE.NoColorSpace,0,this.textureQuality));textures.set(normalKey,normal)}
              // Cable_DX9 expands the normal, then applies squared half-Lambert
              // against tangent-space +Z: the resulting factor is blue squared.
              rgb=rgb.mul(TSL.texture(normal,TSL.uv()).b.pow(2))
            }
            const alpha=program.vertexAlpha?sampled.a.mul(color.a):sampled.a
            const hdr=this.configuration.lightingProfile==="hdr"?TSL.attribute("legacyHdr","float"):TSL.float(1)
            const exposure=program.gammaExposure?exposureUniform.pow(1/2.2):exposureUniform
            const value=TSL.vec4(rgb.mul(TSL.vec3(...program.modulation.slice(0,3))).mul(hdr).mul(exposure),alpha.mul(program.modulation[3]))
            material.colorNode=legacyFog(sourceFragmentColor(value,state),state.fog,this.#viewFogUniforms,waterFogUniforms)
            material.fog=false;material.toneMapped=false;material.forceSinglePass=true;material.name=`legacy:${input.material}:${frame}`
            if(program.cable){material.transparent=false;material.userData.sourceRope=true}
            return material
          })
        })
        legacyVisuals=Array.from({length:5},(_,index)=>{const view=disposables.add(new LegacyVisuals(this.#backend.backend,materials));view.world.visible=index===0;group.add(view.world);return view})
      }
    } catch (error) {
      const failed = {
        group,
        modelTemplates,
        modelOccurrenceInstances,
        brushModelTemplates,
        particleTextures,
        skyGroup,
        cubemapTextures,
        mainStaticProps,
        skyStaticProps,
        staticPropInstances,
        materialStates,
        disposables,
        textureResidency,
        waterMaterials,
        refractMaterials,
        worldMaterials,
        projectedMarks,
        disposed: false,
      }
      disposeScene(failed)
      if (error instanceof RenderingError) throw error
      throw new RenderingError("BoundExceeded", `runtime map GPU staging failed: ${String(error)}`)
    }

    const requirements = map.lighting.profile === "hdr" ? map.lighting.descriptor.requirements : Object.freeze([])
    const remainingRequirements=requirements.filter(requirement=>{
      if(requirement.identity==="map-environment-presentation")return !request.environment
      if(requirement.identity==="map-water-presentation")return !request.environment||request.environment.waterMaterials.size===0||request.environment.waterSurfaceFacts.length===0
      return true
    })
    for (const requirement of remainingRequirements) {
      diagnostics.push(
        diagnostic(
          requirement.disposition === "Missing" ? "MissingProfileInput" : "UnsupportedProfileInput",
          requirement.identity,
          requirement.reason,
        ),
      )
    }
    diagnostics.sort(
      (left, right) => left.code.localeCompare(right.code) || left.identity.localeCompare(right.identity),
    )
    const directionalFaces = map.lighting.profile === "hdr" ? map.lighting.descriptor.directionalFaces : 0
    const worldLights = map.lighting.profile === "hdr" ? map.lighting.descriptor.worldLights.length : 0
    const ambientSamples = map.lighting.profile === "hdr" ? map.lighting.descriptor.ambientSamples.length : 0
    const surfaceLighting = map.lighting.profile === "hdr"
      ? new Map(map.lighting.descriptor.surfaces.map((surface) => [surface.face, surface] as const))
      : new Map<number, never>()
    const displacementEvidence = map.displacements.map((displacement) => {
      const resolved = map.materials[displacement.material]!
      const state = materialStates.get(resolved.logicalPath.toLowerCase())
      const options = materialOptions(resolved, state)
      const lighting = surfaceLighting.get(displacement.face)
      const submittedTriangles = map.batches
        .filter((batch) => batch.material === displacement.material)
        .reduce((total, batch) => total + batch.faces.reduce((count, face) => count + Number(face === displacement.face), 0), 0)
      return Object.freeze({
        source: displacement.source,
        face: displacement.face,
        material: resolved.logicalPath,
        positions: displacement.positions,
        normals: displacement.normals,
        indices: displacement.indices,
        bounds: displacement.bounds,
        submittedTriangles,
        cull: options.side === THREE.DoubleSide ? "none" as const : "back" as const,
        depthTest: options.depthTest !== false,
        depthWrite: options.depthWrite !== false,
        blend: options.transparent === true,
        lighting: Object.freeze({
          kind: lighting?.kind ?? (displacement.lightOffset < 0 ? "unlit" : "flat"),
          styles: lighting?.styles ?? displacement.styles,
          sampleStart: lighting?.sampleStart ?? Math.max(0, displacement.lightOffset / 4),
          samplesPerLayer: lighting?.samplesPerLayer ?? displacement.lightmapWidth * displacement.lightmapHeight,
          layers: lighting?.layerCount ?? (displacement.lightOffset < 0 ? 0 : 1),
        }),
      })
    })
    const projectedMarksByFace = new Map<number, ProjectedMarkResource[]>()
    const brushProjectedMarks: ProjectedMarkResource[] = []
    for (const mark of projectedMarks) {
      if (mark.visibility.kind === "world") {
        let values = projectedMarksByFace.get(mark.face)
        if (!values) projectedMarksByFace.set(mark.face, values = [])
        values.push(mark)
      } else {
        brushProjectedMarks.push(mark)
      }
    }
    const runtimeStaticPropInstances = staticPropInstances.filter((_, index) => request.staticProps?.lightingKind[index] === 1)
    const createVisibility = (batches: readonly WorldBatchResource[], source?: RetainedWorldVisibility) => new RetainedWorldVisibility(
      batches.map((batch) => ({
        faces: batch.faces,
        sourceIndices: batch.sourceIndices,
        targetIndices: batch.index.array as Uint32Array,
        transparent: batch.transparent,
      })),
      source,
    )
    const worldVisibility = createVisibility(worldBatches)
    const skyWorldVisibility = createVisibility(skyWorldBatches, worldVisibility)
    const leafVisibility = new RetainedLeafVisibility(staticPropInstances)
    const result: SceneResult = Object.freeze({
      payloadSha256,
      lightingProfile: map.lighting.profile,
      deviceGeneration: this.#deviceGeneration,
      sceneGeneration,
      drawableSurfaces: map.drawableSurfaces,
      drawBatches: map.batches.length,
      lightingSampleCount: map.lightingSampleCount,
      directionalFaces,
      worldLights,
      ambientSamples,
      requirements:Object.freeze(remainingRequirements),
      diagnostics: Object.freeze(diagnostics),
      resources: Object.freeze({
        geometries: [...disposables].filter((value) => value instanceof THREE.BufferGeometry).length,
        materials: [...disposables].filter((value) => value instanceof THREE.Material).length,
        textures: [...disposables].filter((value) => value instanceof THREE.Texture).length,
      }),
      environment: request.environment,
      environmentDrawables:
        request.environment?.markRecords
          .filter((mark) => mark.status === 0 && mark.enabled)
          .reduce((total, mark) => total + mark.fragments.length, 0) ?? 0,
      staticProps:Object.freeze({total:request.staticProps?.count??0,main:request.staticProps?request.staticProps.ownership.reduce((total,value)=>total+Number(value===0),0):0,sky3d:request.staticProps?request.staticProps.ownership.reduce((total,value)=>total+Number(value===1),0):0,runtimeLit:request.staticProps?request.staticProps.lightingKind.reduce((total,value)=>total+Number(value===1),0):0,batches:staticPropBatches.length,batched:staticPropInstances.reduce((total,prop)=>total+Number(prop.batches.length!==0),0),hierarchyNodes:mainStaticProps.children.length+skyStaticProps.children.length}),
      runtimeStaticProps:Object.freeze(runtimeStaticPropInstances.map(prop=>{if(!prop.lightingOrigin)throw new RenderingError("MissingInput","runtime static prop has no lighting origin");return Object.freeze({source:prop.source,origin:prop.origin,lightingOrigin:prop.lightingOrigin,radius:prop.radius})})),
      displacements: Object.freeze(displacementEvidence),
    })
    disposables.activate()
    return {
      map,
      payload,
      payloadSha256,
      loadRequest: {
        payloadSha256,
        resourceIdentity: request.resourceIdentity,
        lightStyles: request.lightStyles?.map((value) => Object.freeze({ ...value })),
        directionalTextures: [...directionalInputs.values()],
        environment: request.environment,
        materialStates: new Map(materialStates),
        particleTextures: request.particleTextures,
        modelOccurrences: request.modelOccurrences?.map((value) => Object.freeze({ ...value, matrix: value.matrix.slice() })),
        modelFacing: new Map(request.modelFacing ?? []),
        modelMaterials: new Map(request.modelMaterials ?? []),
        authoredTextures: request.authoredTextures,
        modelDrawInputs: request.modelDrawInputs?.map((input) => Object.freeze({
          entity: input.entity,
          lighting: structuredClone(input.lighting),
          eyes: Object.freeze(input.eyes.map((eye) => structuredClone(eye))),
        })),
        brushModels:request.brushModels?.map(model=>Object.freeze({...model,surfaceRange:Object.freeze([...model.surfaceRange]) as readonly[number,number],materials:Object.freeze([...model.materials])})),
        staticProps: request.staticProps,
        diagnostic: request.diagnostic,
      },
      group,
      modelTemplates,
      modelBaseSamples,
      modelTemplateResources,
      modelPipelineKeys,
      borrowedResources,
      modelLightingTextures,
      modelLightingGraphs,
      modelPanelLightingGraphs,
      modelPanelMaterialAnimations,
      modelOccurrenceInstances,
      modelOccurrenceLighting: Object.freeze(modelOccurrenceLighting),
      brushModelTemplates,
      particleTextures,
      particleBatchMaterials,
      particleDepth,
      legacyVisuals,
      particlePipelineMeshes,
      materialStates,
      disposables,
      textureResidency,
      lightmapTextures,
      exposureUniform,
      waterFogUniforms,
      diagnostics: Object.freeze(diagnostics),
      worldBundle,
      skyWorldBundle,
      worldDraws,
      skyWorldDraws,
      mainAnimatedWorld,
      skyAnimatedWorld,
      mainTransparentWorld,
      skyTransparentWorld,
      mainModelOccurrences,
      projectedMarkGroup,
      worldBatches: Object.freeze(worldBatches),
      skyWorldBatches: Object.freeze(skyWorldBatches),
      worldVisibility,
      skyWorldVisibility,
      modelLookup: new Map(map.models.map((model) => [model.logicalPath, model])),
      projectedMarksByFace,
      brushProjectedMarks: Object.freeze(brushProjectedMarks),
      leafVisibility,
      runtimeStaticPropInstances: Object.freeze(runtimeStaticPropInstances),
      projectedMarks: Object.freeze(projectedMarks),
      combatDecals:disposables.add(new CombatDecals(projectedMarkGroup,"materials/decals/decals_mod2x.vmt",()=>{
        const identity="materials/decals/decals_mod2x.vmt",state=materialStates.get(identity)
        if(!combatDecalTexture||!state)throw new RenderingError("MissingInput","authored combat decal atlas or modulation state is unavailable")
        const material=new THREE.MeshBasicNodeMaterial(materialOptions({logicalPath:identity,width:1,height:1,shader:11,features:1,textureRole:0},state))
        material.colorNode=sourceFragmentColor(TSL.texture(combatDecalTexture,TSL.uv()),state,waterFogUniforms)
        material.toneMapped=false;material.name=identity;return material
      })),
      waterMeshes:Object.freeze(waterMeshes),
      waterMaterials,
      refractMaterials,
      worldMaterials,
      cubemapTextures,
      modelCubemap,
      skyGroup,
      mainStaticProps,
      skyStaticProps,
      staticPropInstances:Object.freeze(staticPropInstances),
      staticPropBatches:Object.freeze(staticPropBatches),
      reflectionTarget,
      refractionTarget,
      result,
      disposed: false,
    }
  }

  async renderModelPanels(panels: readonly ModelPanelPass[]): Promise<ModelPanelFrameResult> {
    const started = performance.now()
    this.#requireReady()
    const assets = this.#active ?? this.#panelAssets
    if (!assets) throw new RenderingError("InvalidState", "model-panel rendering has no admitted assets")
    if (this.#renderBusy) throw new RenderingError("InvalidState", "a render is already in progress")
    if (panels.length < 1 || panels.length > 16) throw new RenderingError("BoundExceeded", "model-panel pass count is invalid")
    this.#renderBusy = true
    const result: { identity: string; model: string; skin: number; primitives: number }[] = []
    const previousAutoClear = this.#backend.autoClear
    const previousWaterFog = assets.waterFogUniforms.enabled.value
    const previousClearColor = this.#backend.getClearColor(new THREE.Color()).clone()
    const previousClearAlpha = this.#backend.getClearAlpha()
    assets.waterFogUniforms.enabled.value = 0
    try {
      for (const panel of panels) {
        if (!panel.identity || !panel.model || !Number.isSafeInteger(panel.skin) || panel.skin < 0
          || !["opaque", "transparent", "clear-transparent"].includes(panel.background)
          || !finite([...panel.origin, ...panel.angles, panel.fov,
            panel.bounds.x, panel.bounds.y, panel.bounds.width, panel.bounds.height])
          || panel.bounds.width <= 0 || panel.bounds.height <= 0
          || (panel.presentationTimeSeconds !== undefined && (!Number.isFinite(panel.presentationTimeSeconds) || panel.presentationTimeSeconds < 0))
          || panel.fov <= 0 || panel.fov >= 180) {
          throw new RenderingError("MalformedInput", `model-panel pass is invalid: ${panel.identity}`)
        }
        const displayWidth = this.#canvas.width
        const displayHeight = this.#canvas.height
        const left = Math.round(panel.bounds.x * this.#devicePixelRatio)
        const top = Math.round(panel.bounds.y * this.#devicePixelRatio)
        const right = Math.round((panel.bounds.x + panel.bounds.width) * this.#devicePixelRatio)
        const bottom = Math.round((panel.bounds.y + panel.bounds.height) * this.#devicePixelRatio)
        if (Math.min(displayWidth, right) <= Math.max(0, left)
          || Math.min(displayHeight, bottom) <= Math.max(0, top)) continue
        const identity = modelKey(panel.model.toLowerCase(), panel.skin)
        const template = assets.modelTemplates.get(identity)
        if (!template) throw new RenderingError("MissingInput", `model-panel model is unavailable: ${identity}`)
        const instanceIdentity = panel.identity
        let retained = this.#modelPanelInstances.get(instanceIdentity)
        if (retained && retained.model !== identity) {
          retained.instance.parent?.remove(retained.instance)
          this.#retainedModelPanels.retain(`${instanceIdentity}:${retained.model}:${!!retained.meshes}`, retained)
          this.#modelPanelInstances.delete(instanceIdentity)
          retained = undefined
        }
        if (!retained) {
          retained = this.#retainedModelPanels.take(`${instanceIdentity}:${identity}:${!!panel.pose}`)
            ?? (!panel.pose ? this.#preparedModelInstances.take(`panel-bind:${identity}`)
              : panel.modelLighting ? this.#preparedModelInstances.take(`panel:${identity}`) : undefined)
            ?? { model: identity, instance: template.clone(true) }
          this.#modelPanelInstances.set(instanceIdentity, retained)
        }
        if (panel.pose) retained.meshes = this.#applyPose(retained.instance, panel.pose, retained.meshes !== undefined, retained.meshes)
        const presentationTime = panel.presentationTimeSeconds ?? 0
        for (const animation of assets.modelPanelMaterialAnimations.get(identity) ?? []) {
          if (animation.frameRate !== null && animation.frames.frameCount > 1) {
            const frame = Math.floor(presentationTime * animation.frameRate) % animation.frames.frameCount
            if (frame !== animation.frame) {
              const next = animation.frames.select(frame, animation.consumer)
              animation.texture.image = next.image
              animation.texture.mipmaps = next.mipmaps
              animation.texture.needsUpdate = true
              animation.frame = frame
            }
          }
          if (animation.scrollRate !== null && animation.scrollAngle !== null) {
            const radians = animation.scrollAngle * Math.PI / 180
            const wrap = (value: number) => ((value % 1) + 1) % 1
            animation.scrollX.value = wrap(presentationTime * Math.cos(radians) * animation.scrollRate)
            animation.scrollY.value = wrap(presentationTime * Math.sin(radians) * animation.scrollRate)
          }
        }
        this.#modelPanelScene.clear()
        this.#modelPanelScene.add(retained.instance)
        const presentation = sourceModelPanelPresentation({
          model: panel.model,
          kind: panel.kind,
          fov: panel.fov,
          origin: panel.origin,
          bounds: panel.bounds,
          displayWidth,
          displayHeight,
          devicePixelRatio: this.#devicePixelRatio,
        })
        sourceTransform(retained.instance, presentation.origin, panel.angles)
        const panelLighting = {
          materials: assets.loadRequest.modelMaterials, states: assets.materialStates,
          textures: assets.modelLightingTextures, cubemap: assets.modelCubemap, exposure: this.#modelPanelExposure, graphs: assets.modelPanelLightingGraphs,
        }
        if (panel.modelLighting) this.#applyDynamicModelLighting(retained, {
          identity: 0, model: panel.model, position: presentation.origin, angles: panel.angles, scale: 1,
          pose: panel.pose, modelLighting: panel.modelLighting, eyeStates: panel.eyeStates,
        }, panelLighting)
        for (const [index, child] of (panel.mergedModels ?? []).entries()) {
          const key = modelKey(child.model.toLowerCase(), child.skin)
          const childTemplate = assets.modelTemplates.get(key)
          if (!childTemplate) throw new RenderingError("MissingInput", `merged model-panel model is unavailable: ${key}`)
          const instanceKey = `${panel.identity}:merge:${index}`
          let merged = this.#modelPanelInstances.get(instanceKey)
          if (merged && merged.model !== key) {
            merged.instance.parent?.remove(merged.instance)
            this.#retainedModelPanels.retain(`${instanceKey}:${merged.model}:${!!merged.meshes}`, merged)
            this.#modelPanelInstances.delete(instanceKey)
            merged = undefined
          }
          if (!merged) {
            merged = this.#retainedModelPanels.take(`${instanceKey}:${key}:true`) ?? this.#preparedModelInstances.take(`panel:${key}`) ?? { model: key, instance: childTemplate.clone(true) }
            this.#modelPanelInstances.set(instanceKey, merged)
          }
          merged.meshes = this.#applyPose(merged.instance, child.pose, merged.meshes !== undefined, merged.meshes)
          sourceTransform(merged.instance, presentation.origin, panel.angles)
          if (child.modelLighting) this.#applyDynamicModelLighting(merged, {
            identity: 0, model: child.model, position: presentation.origin, angles: panel.angles, scale: 1,
            pose: child.pose, modelLighting: child.modelLighting, eyeStates: child.eyeStates,
          }, panelLighting)
          this.#modelPanelScene.add(merged.instance)
        }
        const { x, y, width, height } = presentation.rendererViewport
        this.#modelPanelCamera.clearViewOffset()
        this.#modelPanelCamera.aspect = presentation.projection.width / presentation.projection.height
        this.#modelPanelCamera.fov = presentation.verticalFovDegrees
        this.#modelPanelCamera.near = presentation.near
        this.#modelPanelCamera.far = presentation.far
        this.#modelPanelCamera.setViewOffset(presentation.projection.width, presentation.projection.height,
          presentation.projection.offsetX, presentation.projection.offsetY, presentation.viewport.width, presentation.viewport.height)
        this.#modelPanelCamera.updateProjectionMatrix()
        const particleCamera: Camera = { position: [0, 0, 0], yawDegrees: 0, pitchDegrees: 0, verticalFovDegrees: presentation.verticalFovDegrees, near: presentation.near, far: presentation.far }
        let particlePool = this.#panelParticlePools.get(panel.identity)
        if (!particlePool) { particlePool = { group: createWorldClipGroup(), meshes: [], ranges: [] }; this.#panelParticlePools.set(panel.identity, particlePool) }
        this.#stageParticleBatches(panel.particles ?? [], particleCamera, particlePool)
        this.#modelPanelScene.add(particlePool.group)
        this.#backend.setViewport(x, y, width, height)
        this.#backend.setScissor(x, y, width, height)
        this.#backend.setScissorTest(true)
        this.#backend.setClearColor(0x000000, panel.background === "clear-transparent" ? 0 : 1)
        this.#backend.autoClear = panel.background !== "transparent"
        this.#modelPanelScene.background = panel.background === "opaque" ? new THREE.Color(0x000000) : null
        withSourceModelPanelTargetViewport(this.#backend.getRenderTarget(), presentation, () => {
          if (panel.background === "transparent") this.#resetDepth("hud-depth-reset")
          this.#drawPass("hud-model", this.#modelPanelScene, this.#modelPanelCamera)
        })
        let primitives = 0
        retained.instance.traverse((object) => { if (object instanceof THREE.Mesh) primitives += 1 })
        result.push({ identity: panel.identity, model: panel.model, skin: panel.skin, primitives })
      }
      return Object.freeze({ panels: Object.freeze(result.map((item) => Object.freeze(item))), milliseconds: performance.now() - started })
    } finally {
      this.#modelPanelScene.clear()
      assets.waterFogUniforms.enabled.value = previousWaterFog
      this.#backend.setClearColor(previousClearColor, previousClearAlpha)
      this.#backend.setScissorTest(false)
      this.#backend.setViewport(0, 0, this.#viewportWidth, this.#viewportHeight)
      this.#backend.autoClear = previousAutoClear
      this.#renderBusy = false
    }
  }

  async render(frame: Frame): Promise<FrameResult> {
    this.#renderOwnerProbe?.begin(this.#sceneGeneration, this.#deviceGeneration)
    const frameStarted = performance.now()
    this.#cloakFramebuffer.copies = 0
    this.#requireReady()
    if (!this.#active) throw new RenderingError("InvalidState", "renderer has no active map")
    if (this.#renderBusy) throw new RenderingError("InvalidState", "a render is already in progress")
    this.#validateFrame(frame)
    this.#renderBusy = true
    try {
      if (frame.hudMaterials && !this.#hudMaterials?.prepared) {
        this.#hudMaterials ??= new SourceHudMaterials(this.textureQuality)
        await this.#hudMaterials.prepare(frame.hudMaterials.materials)
        this.#requireReady()
      }
      if (frame.lightStyles && this.#active.map.lighting.profile === "hdr") {
        const lightmap = buildRuntimeLightmap(this.#active.map, frame.lightStyles)
        this.#replaceLightmapData(this.#active, lightmap)
      }
      if(frame.combatDecals?.length){
        const scene=this.#active
        const maximum=frame.maximumCombatDecals??200
        if(!Number.isInteger(maximum)||maximum<0||maximum>4096)throw new RenderingError("MalformedInput","combat decal limit is invalid")
        scene.combatDecals.update(frame.combatDecals,maximum)
      }
      if (frame.visibility) {
        if (
          frame.visibility.worldIdentity !== this.#active.result.environment?.identity ||
          !HASH.test(frame.visibility.cacheIdentity)
        )
          throw new RenderingError("IdentityMismatch", "visibility result identity differs from the active environment")
        const visibilityChanged = this.#worldVisibilityIdentity !== frame.visibility.cacheIdentity
        this.#setWorldVisibility(frame.visibility.surfaces, frame.visibility.cacheIdentity)
        if(visibilityChanged||frame.combatDecals?.length){
          for(const decal of this.#active.combatDecals.records.values())decal.mesh.visible=this.#active.worldVisibility.has(decal.face)
        }
        if (this.#active.skyGroup) this.#active.skyGroup.visible = frame.visibility.sky === 1
        if (!frame.collisionWorldIdentity || frame.collisionWorldIdentity !== this.#active.result.environment?.collisionWorldIdentity) {
          throw new RenderingError("IdentityMismatch", "mark collision-world identity differs")
        }
        if (visibilityChanged) {
          for (const face of this.#visibleWorldMarkFaces) {
            for (const mark of this.#active.projectedMarksByFace.get(face) ?? []) {
              if (mark.mesh.visible) this.#visibleProjectedMarkCount -= 1
              mark.mesh.visible = false
            }
          }
          this.#visibleWorldMarkFaces.clear()
          for (let index = 0; index < frame.visibility.surfaces.length; index += 1) {
            const face = frame.visibility.surfaces[index]!
            const marks = this.#active.projectedMarksByFace.get(face)
            if (!marks) continue
            this.#visibleWorldMarkFaces.add(face)
            for (const mark of marks) {
              if (!mark.mesh.visible) this.#visibleProjectedMarkCount += 1
              mark.mesh.visible = true
            }
          }
        }
        for (const mark of this.#active.brushProjectedMarks) {
          const visibility = mark.visibility
          if (visibility.kind !== "brush-model") continue
          const receiver = this.#findBrushModel(frame.brushModels?.models, visibility.entity, visibility.model)
          const visible = receiver?.draw === true
          if (mark.mesh.visible !== visible) this.#visibleProjectedMarkCount += visible ? 1 : -1
          mark.mesh.visible = visible
          if (receiver) sourceTransform(mark.mesh, receiver.worldPosition, receiver.worldAngles)
        }
        if (frame.visibility.worldMaterials.length !== this.#active.worldMaterials.size) {
          throw new RenderingError("IdentityMismatch", "evaluated world material state does not match the active scene")
        }
        for (const evaluated of frame.visibility.worldMaterials) {
          const resource = this.#active.worldMaterials.get(evaluated.identity.toLowerCase())
          if (!resource || resource.mapMaterial !== evaluated.mapMaterial) {
            throw new RenderingError("IdentityMismatch", `evaluated world material ${evaluated.identity} is unavailable`)
          }
          const bump = evaluated.textures.find((texture) => texture.role === 7)
          if (!bump || bump.frame === null || bump.frame < 0 || bump.frame >= resource.normalFrames.frameCount) {
            throw new RenderingError("MalformedInput", `evaluated world material ${evaluated.identity} selected an invalid bump frame`)
          }
          const texture = resource.normalFrames.select(bump.frame, resource.normalConsumer)
          if (bump.transform) {
            const matrix = bump.transform
            texture.matrixAutoUpdate = false
            texture.matrix.set(
              matrix[0]!, matrix[1]!, matrix[3]!,
              matrix[4]!, matrix[5]!, matrix[7]!,
              matrix[12]!, matrix[13]!, matrix[15]!,
            )
          }
          resource.normalNode.value = texture
        }
        const water = frame.visibility.water
        const visibleWater = water.visibleWater
        if (visibleWater?.evaluated) {
          const resource = this.#active.waterMaterials.get(visibleWater.material.toLowerCase())
          if (!resource) throw new RenderingError("MissingInput", `current Water material ${visibleWater.material} is unavailable`)
          const frameIndex = ((visibleWater.evaluated.normalFrame % resource.normalFrames.frameCount)
            + resource.normalFrames.frameCount) % resource.normalFrames.frameCount
          const texture = resource.normalFrames.select(frameIndex, resource.normalConsumer)
          const matrix = visibleWater.evaluated.normalTransform
          texture.matrixAutoUpdate = false
          texture.matrix.set(
            matrix[0]!, matrix[1]!, matrix[3]!,
            matrix[4]!, matrix[5]!, matrix[7]!,
            matrix[12]!, matrix[13]!, matrix[15]!,
          )
          resource.normalNode.value = texture
          if (resource.cheapNormalNode) resource.cheapNormalNode.value = texture
          const selectedMaterial = water.render.cheap && !resource.state.forceCheap.value
            ? resource.cheapMaterial
            : resource.material
          if (!selectedMaterial) {
            throw new RenderingError("MissingInput", `current Water material ${visibleWater.material} has no selected cheap cubemap shader`)
          }
          for (const waterMesh of this.#active.waterMeshes) {
            waterMesh.mesh.material = selectedMaterial
            waterMesh.mesh.visible = water.render.drawSurface
              && waterMesh.materialIdentity === visibleWater.material.toLowerCase()
          }
        } else {
          for (const waterMesh of this.#active.waterMeshes) waterMesh.mesh.visible = false
        }
      }
      const sampledHistogram = this.#exposureSampler?.take()
      if (sampledHistogram) this.#exposure.submit(sampledHistogram)
      if (frame.exposureHistogram) this.#exposure.submit(frame.exposureHistogram)
      const exposure =
        this.configuration.lightingProfile === "hdr"
          ? this.#exposure.advance(frame.deltaSeconds ?? 0)
          : this.#exposure.snapshot()
      this.#active.exposureUniform.value = this.configuration.lightingProfile === "hdr" ? exposure.current : 1
      this.#setCamera(frame.camera)
      if(frame.visibility)this.#setStaticPropVisibility(frame.visibility.leaves,0,frame.camera,frame.visibility.water.visibleWater===null)
      const dynamicItemsStarted = performance.now()
      const viewModelDepthRange = this.#stageDynamicItems(frame)
      const dynamicItemsMilliseconds = performance.now() - dynamicItemsStarted
      let viewModelPass: FrameResult["viewModelPass"],sky3dPass:FrameResult["sky3dPass"]
      let beforeHudCapture: FrameCapture | undefined
      let waterPasses: FrameResult["waterPasses"] = Object.freeze([])
      let waterPassTimings: FrameResult["waterPassTimings"] = Object.freeze([])
      let waterStateRestored = true
      let worldMilliseconds=0,viewModelMilliseconds=0
      if (!this.#suspended) {
        this.#framePresentation?.begin()
        if(this.#active?.legacyVisuals&&frame.legacyVisuals){for(const [index,view] of Object.entries(frame.legacyVisuals))this.#active.legacyVisuals[Number(index)]?.update(view)}
        const worldStarted=performance.now()
        sky3dPass=this.#renderSky3dPass(frame)
        const waterResult=this.#renderWaterPasses(frame,sky3dPass!==undefined)
        this.#renderLegacyVisuals(frame.legacyVisuals?.[0], 0)
        worldMilliseconds=performance.now()-worldStarted
        waterPasses = waterResult.passes
        waterPassTimings = waterResult.timings
        waterStateRestored = waterResult.restored
        if (this.#viewModels.children.length > 0) {
          if (!viewModelDepthRange) throw new RenderingError("InvalidState", "viewmodel depth range is unavailable")
          this.#backend.autoClear = false
          const background = this.#scene.background
          const matrixWorldAutoUpdate = this.#scene.matrixWorldAutoUpdate
          this.#scene.background = null
          this.#scene.matrixWorldAutoUpdate = false
          const viewModelStarted=performance.now()
          try {
            const phase = executeViewModelDepthPhase({
              depthRange: viewModelDepthRange,
              clearWorldDepth: () => this.#resetDepth("viewmodel-depth-reset"),
              setDepthRange: ([minimum, maximum]) => this.#backend.setViewport(0, 0, this.#viewportWidth, this.#viewportHeight, minimum, maximum),
              draw: () => this.#drawPass("viewmodel", this.#scene, this.#viewCamera),
            })
            viewModelPass = Object.freeze({
              depthRange: phase.depthRange,
              worldDepthCleared: phase.worldDepthCleared,
              viewportRestored: phase.depthRangeRestored,
            })
          } finally {
            viewModelMilliseconds=performance.now()-viewModelStarted
            this.#scene.background = background
            this.#scene.matrixWorldAutoUpdate = matrixWorldAutoUpdate
            this.#backend.autoClear = true
          }
        }
        const overlay = frame.visibility?.water.visibleWater?.overlay
        if (overlay) this.#renderUnderwaterOverlay(overlay)
        if (frame.visibility?.screenOverlay) this.#renderUnderwaterOverlay(frame.visibility.screenOverlay)
        if (this.#exposureSampler && this.#framePresentation?.active) this.#pass("frame-output", () => this.#framePresentation!.finish())
        this.#exposureSampler?.sample(
          Math.floor(this.#viewportWidth * this.#devicePixelRatio),
          Math.floor(this.#viewportHeight * this.#devicePixelRatio),
        )
        if (frame.capture?.beforeHud) {
          if (this.#framePresentation?.active) this.#pass("frame-output", () => this.#framePresentation!.finish())
          beforeHudCapture = await this.#capture(frame.capture)
        }
        if (frame.hudMaterials) this.#pass("hud-materials", () => this.#hudMaterials!.render(
          this.#backend, frame.hudMaterials!, this.#viewportWidth, this.#viewportHeight, this.#devicePixelRatio,
        ))
      }
      if (frame.capture && this.#framePresentation?.active) this.#pass("frame-output", () => this.#framePresentation!.finish())
      const capture = frame.capture ? await this.#capture(frame.capture) : undefined
      return Object.freeze({
        deviceGeneration: this.#deviceGeneration,
        sceneGeneration: this.#sceneGeneration,
        submission: null,
        exposure,
        visibleProjectedMarks: this.#visibleProjectedMarkCount,
        waterPasses,
        waterPassTimings,
        waterStateRestored,
        sky3dPass,
        visibleMainStaticPropSources: this.#visibleStaticSources[0],
        runtimeStaticPropScreen: this.#runtimeStaticPropScreen(),
        timings:Object.freeze({particleItems:frame.particles?.length??0,particleBatches:this.#particleBatchCount,cloakFramebufferCopies:this.#cloakFramebuffer.copies,dynamicItemsMilliseconds,worldMilliseconds,viewModelMilliseconds,totalMilliseconds:performance.now()-frameStarted}),
        viewModelPass,
        pacing:this.#pacing.records(),
        capture,
        beforeHudCapture,
      })
    } finally {
      if (this.#renderBusy && this.#pendingDepthReset) this.#pendingDepthReset = false
      this.#renderBusy = false
    }
  }

  #replaceLightmapData(scene: SceneResources, lightmap: RuntimeLightmap): void {
    try { replaceWorldLightmapData(scene.lightmapTextures, lightmap) }
    catch (error) { throw new RenderingError("MalformedInput", error instanceof Error ? error.message : String(error)) }
  }

  #renderUnderwaterOverlay(overlay: NonNullable<NonNullable<WaterFramePlan["visibleWater"]>["overlay"]> & { refractTint?: readonly [number, number, number] }): void {
    const scene = this.#active
    const resource = scene?.refractMaterials.get(overlay.identity.toLowerCase())
    if (!scene || !resource || overlay.normalFrame < 0 || overlay.normalFrame >= resource.normalFrames.frameCount) {
      throw new RenderingError("MissingInput", `authored underwater Refract overlay ${overlay.identity} is unavailable`)
    }
    const normal = resource.normalFrames.select(overlay.normalFrame, resource.normalConsumer)
    const matrix = overlay.normalTransform
    normal.matrixAutoUpdate = false
    normal.matrix.set(
      matrix[0]!, matrix[1]!, matrix[3]!,
      matrix[4]!, matrix[5]!, matrix[7]!,
      matrix[12]!, matrix[13]!, matrix[15]!,
    )
    resource.normalNode.value = normal
    resource.setTint(overlay.refractTint ?? resource.state.refractTint)
    const autoClear = this.#backend.autoClear
    try {
      this.#backend.autoClear = false
      this.#underwaterOverlay.material = resource.material
      this.#underwaterOverlay.render(this.#backend)
    } finally {
      this.#backend.autoClear = autoClear
    }
  }

  #setWorldVisibility(surfaces: Uint32Array, identity?: string, ownership: 0 | 1 = 0): void {
    const scene = this.#active
    if (!scene) throw new RenderingError("InvalidState", "renderer has no active world visibility resources")
    const priorIdentity = ownership === 0 ? this.#worldVisibilityIdentity : this.#skyWorldVisibilityIdentity
    if (identity !== undefined && identity === priorIdentity) return
    const prior = ownership === 0 ? this.#worldVisibilitySurfaces : this.#skyWorldVisibilitySurfaces
    if (surfaces === prior || (prior?.length === surfaces.length && surfaces.every((value, index) => value === prior[index]))) {
      if (ownership === 0) this.#worldVisibilityIdentity = identity
      else this.#skyWorldVisibilityIdentity = identity
      return
    }

    const index = ownership === 0 ? scene.worldVisibility : scene.skyWorldVisibility
    const batches = ownership === 0 ? scene.worldBatches : scene.skyWorldBatches
    try {
      index.apply(surfaces)
    } catch (error) {
      if (error instanceof RetainedVisibilityError) throw new RenderingError("MalformedInput", error.message)
      throw error
    }
    const draws = ownership === 0 ? scene.worldDraws : scene.skyWorldDraws
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
      if (!index.changed(batchIndex)) continue
      const batch = batches[batchIndex]!
      const count = index.count(batchIndex)
      if (count > 0) {
        batch.index.clearUpdateRanges()
        batch.index.addUpdateRange(0, count)
        this.#instrumentation?.indexUpload(count * batch.index.array.BYTES_PER_ELEMENT)
        if (batch.bundled) {
          const backend = this.#backend.backend as unknown as ConstructorParameters<typeof PersistentWorldDraws>[1]
          if (backend.get(batch.index).buffer !== undefined) backend.updateAttribute(batch.index)
          else batch.index.needsUpdate = true
        } else batch.index.needsUpdate = true
      }
      if (batch.bundled) {
        draws.update(batchIndex, count)
        batch.mesh.visible = true
      } else {
        batch.mesh.geometry.setDrawRange(0, count)
        batch.mesh.visible = count > 0 && !(ownership === 1 && batch.mesh.userData.skyWater === true)
      }
    }
    const indirectBytes = draws.flush()
    this.#instrumentation?.indirectUpload(indirectBytes)
    if (ownership === 0) {
      this.#worldVisibilitySurfaces = surfaces
      this.#worldVisibilityIdentity = identity
    } else {
      this.#skyWorldVisibilitySurfaces = surfaces
      this.#skyWorldVisibilityIdentity = identity
    }
  }

  #setStaticPropVisibility(leaves: readonly number[], ownership: 0 | 1, camera: Camera, frustumCull = true): void {
    const scene = this.#active
    if (!scene) return
    const cameraIdentity = [
      ...camera.position, camera.yawDegrees, camera.pitchDegrees,
      camera.verticalFovDegrees, camera.near, camera.far, this.#viewportWidth, this.#viewportHeight,
    ]
    const previous = this.#staticVisibilityInputs[ownership]
    if (previous?.scene === scene && previous.frustumCull === frustumCull
      && previous.camera.every((value, index) => value === cameraIdentity[index])
      && previous.leaves.length === leaves.length
      && previous.leaves.every((value, index) => value === leaves[index])) return
    if (frustumCull) {
      this.#camera.updateMatrixWorld()
      this.#staticPropFrustum.setFromProjectionMatrix(this.#staticPropProjection.multiplyMatrices(
        this.#camera.projectionMatrix,
        this.#camera.matrixWorldInverse,
      ), this.#camera.coordinateSystem, this.#camera.reversedDepth)
    }
    const count = scene.leafVisibility.select(leaves, ownership)
    const prior = this.#visibleStaticIndices[ownership]
    const next = this.#nextVisibleStaticIndices[ownership]
    next.clear()
    let membershipChanged = false

    for (let candidate = 0; candidate < count; candidate += 1) {
      const index = scene.leafVisibility.at(candidate)
      const prop = scene.staticPropInstances[index]!
      let alpha = 1
      if ((prop.flags & 1) !== 0) {
        if ((prop.flags & 0x20) !== 0) {
          const up = this.#fadeUp.set(0, 1, 0).applyQuaternion(this.#camera.quaternion).multiplyScalar(prop.radius)
          const first = this.#fadeFirst.set(...prop.origin).add(up).project(this.#camera)
          const second = this.#fadeSecond.set(...prop.origin).sub(up).project(this.#camera)
          const pixelWidth = this.#viewportHeight * Math.abs(second.y - first.y)
          alpha = screenFadeOpacity(pixelWidth, prop.fadeMaximum, prop.fadeMinimum)
        } else {
          const dx = prop.origin[0] - camera.position[0]
          const dy = prop.origin[1] - camera.position[1]
          const dz = prop.origin[2] - camera.position[2]
          alpha = distanceFadeOpacity(dx * dx + dy * dy + dz * dz, prop.fadeMinimum, prop.fadeMaximum)
        }
      }
      alpha = quantizeStaticPropOpacity(alpha)
      prop.fadeUniform.value = alpha
      selectStaticPropFadePass(prop.fadeBindings,alpha)
      if (alpha > 0 && (!frustumCull || this.#staticPropFrustum.intersectsSphere(
        this.#staticPropSphere.set(
          this.#staticPropSphere.center.set(
            (prop.bounds[0] + prop.bounds[3]) * 0.5,
            (prop.bounds[1] + prop.bounds[4]) * 0.5,
            (prop.bounds[2] + prop.bounds[5]) * 0.5,
          ),
          prop.radius,
        ),
      ))) {
        prop.object.visible = true
        for (const binding of prop.batches) binding.batch.setVisible(binding.index, true)
        next.add(index)
        if (!prior.has(index)) membershipChanged = true
      } else {
        prop.object.visible = false
        for (const binding of prop.batches) binding.batch.setVisible(binding.index, false)
      }
    }

    for (const index of prior) {
      if (next.has(index)) continue
      const prop = scene.staticPropInstances[index]!
      prop.object.visible = false
      for (const binding of prop.batches) binding.batch.setVisible(binding.index, false)
      membershipChanged = true
    }
    this.#visibleStaticIndices[ownership] = next
    for (const entry of scene.staticPropBatches) {
      if (entry.ownership === ownership) entry.batch.update()
    }
    this.#nextVisibleStaticIndices[ownership] = prior
    if (membershipChanged || next.size !== this.#visibleStaticSources[ownership].length) {
      const sources: number[] = []
      for (const index of next) sources.push(scene.staticPropInstances[index]!.source)
      this.#visibleStaticSources[ownership] = Object.freeze(sources)
    }
    this.#staticVisibilityInputs[ownership] = { scene, leaves, camera: cameraIdentity, frustumCull }
  }

  #runtimeStaticPropScreen(): FrameResult["runtimeStaticPropScreen"] {
    if (!this.#active || this.#active.runtimeStaticPropInstances.length === 0) return Object.freeze([])
    const output: FrameResult["runtimeStaticPropScreen"][number][] = []
    for (const prop of this.#active.runtimeStaticPropInstances) {
      if (prop.ownership !== 0 || !prop.object.visible) continue
      const bounds = prop.bounds
      let minimumX = Number.POSITIVE_INFINITY
      let maximumX = Number.NEGATIVE_INFINITY
      let minimumY = Number.POSITIVE_INFINITY
      let maximumY = Number.NEGATIVE_INFINITY
      for (let corner = 0; corner < 8; corner += 1) {
        const point = this.#projectionPoint.set(
          bounds[(corner & 4) === 0 ? 0 : 3]!,
          bounds[(corner & 2) === 0 ? 1 : 4]!,
          bounds[(corner & 1) === 0 ? 2 : 5]!,
        ).project(this.#camera)
        minimumX = Math.min(minimumX, point.x)
        maximumX = Math.max(maximumX, point.x)
        minimumY = Math.min(minimumY, point.y)
        maximumY = Math.max(maximumY, point.y)
      }
      output.push(Object.freeze({
        source: prop.source,
        x: (minimumX + 1) * 0.5 * this.#viewportWidth,
        y: (1 - maximumY) * 0.5 * this.#viewportHeight,
        width: (maximumX - minimumX) * 0.5 * this.#viewportWidth,
        height: (maximumY - minimumY) * 0.5 * this.#viewportHeight,
      }))
    }
    return Object.freeze(output)
  }

  #findBrushModel(
    models: NonNullable<Frame["brushModels"]>["models"] | undefined,
    sourceIdentity: bigint,
    modelIdentity: number,
  ): NonNullable<Frame["brushModels"]>["models"][number] | undefined {
    if (!models || sourceIdentity < 0n || sourceIdentity > BigInt(Number.MAX_SAFE_INTEGER)) return undefined
    const source = Number(sourceIdentity)
    let low = 0
    let high = models.length - 1
    while (low <= high) {
      const middle = (low + high) >>> 1
      const value = models[middle]!
      if (value.sourceIndex < source) low = middle + 1
      else if (value.sourceIndex > source) high = middle - 1
      else return value.model === modelIdentity ? value : undefined
    }
    return undefined
  }

  #validateFrame(frame: Frame): void {
    const invalid=invalidFrameEnvelope(frame,MAX_EFFECTS)
    if (invalid) {
      throw new RenderingError("MalformedInput", `render frame is invalid: ${JSON.stringify({ ...invalid,
        clientFrame:frame.clientFrame,clientFrameSeconds:frame.clientFrameSeconds,
        brushTick:frame.brushModels?.tick.toString(),entityRevision:frame.brushModels?.entityRevision.toString(),
        sceneGeneration:this.#sceneGeneration,deviceGeneration:this.#deviceGeneration,
        counts:{effects:frame.effects.length,shadows:frame.shadows?.length??0,models:frame.models?.length??0,particles:frame.particles?.length??0,brushes:frame.brushModels?.models.length??0},
      })}`)
    }
    try {
      validateDynamicLights(frame.effects, MAX_EFFECTS)
      validateShadows(frame.shadows ?? [], MAX_EFFECTS)
    } catch (error) {
      throw new RenderingError("MalformedInput", `dynamic-light or shadow input is invalid: ${String(error)}`)
    }
    if ((frame.effects.length > 0 || (frame.shadows?.length ?? 0) > 0) && !this.#active!.loadRequest.diagnostic) {
      throw new RenderingError("UnsupportedFeature", "the active renderer backend has no accepted dynamic-light or shadow pass")
    }
    for (const item of frame.models ?? []) {
      const transform = item.angles ?? item.orientation ?? []
      if (
        !Number.isSafeInteger(item.identity) ||
        item.identity < 1 ||
        !finite([...item.position, ...transform, item.scale]) ||
        (item.angles === undefined) === (item.orientation === undefined) ||
        (item.orientation !== undefined &&
          (item.orientation.length !== 4 || Math.abs(item.orientation.reduce((s, v) => s + v * v, 0) - 1) > 1e-4)) ||
        !Number.isSafeInteger(item.skin ?? 0) ||
        (item.skin ?? 0) < 0 ||
        item.scale <= 0
      )
        throw new RenderingError("MalformedInput", "render model item is invalid")
      if (!this.#active!.modelTemplates.has(modelKey(item.model, item.skin ?? 0, item.body ?? 0))) {
        throw new RenderingError("MissingInput", `runtime model ${item.model} skin ${item.skin ?? 0} is unavailable`)
      }
      if (item.viewModel && (!item.viewModelProjection || item.viewModelProjection.kind !== "viewmodel"))
        throw new RenderingError("MalformedInput", "viewmodel projection is missing")
      if (!item.modelLighting && !this.#active!.loadRequest.diagnostic) {
        throw new RenderingError("MissingInput", `explicit model lighting ${item.identity} is unavailable`)
      }
      if (item.modelLighting) {
        try {
          validateModelLightingInput(item.modelLighting)
          validateModelEyeStates(item.eyeStates ?? [])
        } catch (error) {
          throw new RenderingError("MalformedInput", `model draw input ${item.identity} is invalid: ${String(error)}`)
        }
      }
      const runtimeModel = this.#active!.modelLookup.get(modelKey(item.model, item.skin ?? 0, item.body ?? 0))
      if (!runtimeModel) throw new RenderingError("IdentityMismatch", "runtime model draw identity differs")
      const skipUnavailableDiagnosticInputs = this.#active!.loadRequest.diagnostic && !item.modelLighting && !item.eyeStates
        && !item.currentFramebufferAvailable && !item.gameProxyValuesAvailable
      for (const [primitiveIndex, primitive] of runtimeModel.primitives.entries()) {
        if (skipUnavailableDiagnosticInputs) break
        if (item.pose?.primitives[primitiveIndex]?.visible === false) continue
        const materialIdentity = runtimeModel.materials[primitive.material]!.logicalPath.toLowerCase()
        const material = this.#active!.loadRequest.modelMaterials?.get(materialIdentity)
        if (!material) continue
        if (this.#active!.loadRequest.diagnostic && material.requiredInputs.some((requirement) =>
          (requirement === "ambient-cube" || requirement === "local-lights" || requirement === "camera-position")
            ? item.modelLighting === undefined
            : requirement === "studio-eye-parameters"
              ? !(item.eyeStates ?? []).some((eye) => eye.primitive === (item.pose?.primitives[primitiveIndex]?.primitive ?? primitiveIndex))
              : requirement === "local-environment"
                ? item.modelLighting?.localEnvironment === null || item.modelLighting?.localEnvironment === undefined
                : requirement === "current-framebuffer"
                   ? item.currentFramebufferAvailable !== true && material.shader !== "refract"
                  : requirement === "game-proxy-values"
                    ? item.gameProxyValuesAvailable !== true
                    : false,
        )) continue
        try {
          prepareModelDrawInputs({
            primitive: item.pose?.primitives[primitiveIndex]?.primitive ?? primitiveIndex,
            required: material.requiredInputs as readonly ModelDrawRequirement[],
            lighting: item.modelLighting,
            eyes: item.eyeStates,
            currentFramebuffer: item.currentFramebufferAvailable === true || material.shader === "refract",
            authoredTexturePlanes: true,
            gameProxyValues: item.gameProxyValuesAvailable === true,
          })
        } catch (error) {
          if (!this.#active!.loadRequest.diagnostic) {
            throw new RenderingError("MissingInput", `model draw ${item.identity} inputs are incomplete: ${String(error)}`)
          }
        }
      }
      if (item.viewModelProjection) {
        const projection = item.viewModelProjection
        try {
          sourceHorizontal4By3FovToVertical(projection.horizontalFov4By3)
          sourceViewportDepthRange(projection.depthRange)
        } catch {
          throw new RenderingError("MalformedInput", "viewmodel projection is invalid")
        }
        if (
          !Number.isFinite(projection.near) ||
          projection.near <= 0 ||
          !projection.drawsAfterWorld ||
          !projection.opaqueBeforeTranslucent ||
          typeof projection.optionalViewSpaceYReflection !== "boolean"
        ) {
          throw new RenderingError("MalformedInput", "viewmodel projection is invalid")
        }
      }
    }
    for (const item of frame.particles ?? []) {
      const position = item.position
      const previous = item.previousPosition
      if (
        !Number.isSafeInteger(item.identity)
        || item.identity < 1
        || !Number.isFinite(position[0])
        || !Number.isFinite(position[1])
        || !Number.isFinite(position[2])
        || !Number.isFinite(previous[0])
        || !Number.isFinite(previous[1])
        || !Number.isFinite(previous[2])
        || !Number.isFinite(item.radius)
        || !Number.isFinite(item.rollRadians)
        || !Number.isFinite(item.opacity)
        || !Number.isFinite(item.trailLength)
        || !Number.isFinite(item.ageSeconds)
        || !Number.isFinite(item.trailMinLength)
        || !Number.isFinite(item.trailMaxLength)
        || !Number.isFinite(item.trailFadeInSeconds)
        || item.radius < 0
        || item.opacity < 0
        || item.opacity > 1
        || !Number.isInteger(item.orientationType) || item.orientationType < 0 || item.orientationType > 2
        || !this.#active!.particleTextures.has(item.material.toLowerCase())
        || !item.primarySheet
      ) throw new RenderingError("MalformedInput", "particle draw item is invalid")
    }
    if(this.#active!.result.environment&&!frame.visibility)throw new RenderingError("MissingInput","an exact visibility result is required")
    if(frame.visibility){if(!Array.isArray(frame.visibility.leaves)||!Array.isArray(frame.visibility.areas)||!Array.isArray(frame.visibility.worldMaterials)||frame.visibility.sky<0||frame.visibility.sky>2||(frame.visibility.eyeLeaf!==null&&(!Number.isSafeInteger(frame.visibility.eyeLeaf)||frame.visibility.eyeLeaf<0)))throw new RenderingError("MalformedInput","visibility view state is invalid");const water=frame.visibility.water;if(water.passes.length<1||water.passes.length>4||water.passes.filter(pass=>pass.kind==="main").length!==1)throw new RenderingError("MalformedInput","Water view plan is invalid");let prior=-1;for(const pass of water.passes){const order=pass.kind==="reflection"?0:pass.kind==="refraction"?1:pass.kind==="main"?2:3;if(order<prior||!finite([...pass.origin,...pass.angles,pass.clip?.height??0])||pass.surfaces.length>100_000)throw new RenderingError("MalformedInput","Water pass is invalid");prior=order}
      const selected = water.visibleWater
      if (selected && (!Number.isFinite(selected.surfaceZ) || (!selected.evaluated && (!water.render.cheap || water.render.reflect || water.render.refract || selected.overlay)))) throw new RenderingError("MalformedInput", "ordinary water surface has an invalid view plan")
      if(selected?.evaluated&&(!finite([selected.evaluated.cheapStart,selected.evaluated.cheapEnd,...selected.evaluated.normalTransform])||selected.evaluated.normalTransform.length!==16))throw new RenderingError("MalformedInput","current Water state is invalid")
    }
    if(frame.brushModels){let prior=-1;for(const model of frame.brushModels.models){if(model.sourceIndex<=prior||model.model<1||model.model>=(this.#active!.loadRequest.brushModels?.length??0)||!finite([...model.worldPosition,...model.worldAngles])||model.renderMode<0||model.renderMode>10)throw new RenderingError("MalformedInput","brush-model publication record is invalid");prior=model.sourceIndex}}
  }

  #setCamera(input: Camera): void {
    this.#camera.position.set(...input.position)
    for (const lighting of this.#active?.modelOccurrenceLighting ?? []) {
      ;(lighting.cameraPosition.value as THREE.Vector3).set(...input.position)
    }
    if (
      this.#camera.fov !== input.verticalFovDegrees
      || this.#camera.near !== input.near
      || this.#camera.far !== input.far
    ) {
      this.#camera.fov = input.verticalFovDegrees
      this.#camera.near = input.near
      this.#camera.far = input.far
      this.#camera.updateProjectionMatrix()
    }
    const yaw = THREE.MathUtils.degToRad(input.yawDegrees)
    const pitch = THREE.MathUtils.degToRad(input.pitchDegrees)
    this.#cameraDirection.set(
      Math.cos(pitch) * Math.cos(yaw),
      Math.cos(pitch) * Math.sin(yaw),
      -Math.sin(pitch),
    )
    this.#camera.lookAt(this.#cameraTarget.copy(this.#camera.position).add(this.#cameraDirection))
    this.#viewCamera.position.copy(this.#camera.position)
    this.#viewCamera.quaternion.copy(this.#camera.quaternion)
    if (this.#active?.skyGroup) {
      this.#active.skyGroup.position.copy(this.#camera.position)
      this.#active.skyGroup.scale.setScalar(input.far)
    }
  }

  #setWaterCamera(pass: WaterFramePass, frame: Camera): void {
    this.#camera.position.set(...pass.origin)
    if (
      this.#camera.fov !== frame.verticalFovDegrees
      || this.#camera.near !== frame.near
      || this.#camera.far !== frame.far
    ) {
      this.#camera.fov = frame.verticalFovDegrees
      this.#camera.near = frame.near
      this.#camera.far = frame.far
      this.#camera.updateProjectionMatrix()
    }
    const yaw = THREE.MathUtils.degToRad(pass.angles[1])
    const pitch = THREE.MathUtils.degToRad(pass.angles[0])
    this.#cameraDirection.set(Math.cos(pitch) * Math.cos(yaw), Math.cos(pitch) * Math.sin(yaw), -Math.sin(pitch))
    this.#camera.lookAt(this.#cameraTarget.copy(this.#camera.position).add(this.#cameraDirection))
    if (this.#active?.skyGroup) {
      this.#active.skyGroup.position.copy(this.#camera.position)
      this.#active.skyGroup.scale.setScalar(frame.far)
    }
  }

  #setClip(clip: WaterFramePass["clip"]): () => void {
    const previousNormal = this.#waterClipPlane.normal.z
    const previousConstant = this.#waterClipPlane.constant
    if (clip) {
      const direction = clip.keep === "above" ? 1 : -1
      this.#waterClipPlane.normal.set(0, 0, direction)
      this.#waterClipPlane.constant = -direction * clip.height
    } else {
      this.#waterClipPlane.normal.set(0,0,0)
      this.#waterClipPlane.constant=0
    }
    return () => {
      this.#waterClipPlane.normal.set(0, 0, previousNormal)
      this.#waterClipPlane.constant = previousConstant
    }
  }

  #setSceneFog(fog: THREE.Fog | null): void {
    this.#scene.fog = fog
    if (!fog) {
      this.#viewFogUniforms.enabled.value = 0
      return
    }
    this.#viewFogUniforms.enabled.value = 1
    this.#viewFogUniforms.start.value = fog.near
    this.#viewFogUniforms.end.value = fog.far
    this.#viewFogUniforms.maximumDensity.value = 1
    ;(this.#viewFogUniforms.color.value as THREE.Vector3).set(fog.color.r, fog.color.g, fog.color.b)
  }

  #fog(input:FogInput|undefined):THREE.Fog|null{
    if(!input?.enabled)return null
    if(input.radial||input.maximumDensity!==1||input.end<input.start)throw new RenderingError("UnsupportedEnvironment","selected fog contract is unavailable")
    let fog=this.#fogCache.get(input)
    if(!fog){fog=new THREE.Fog(new THREE.Color().setRGB(input.primary[0]/255,input.primary[1]/255,input.primary[2]/255,THREE.SRGBColorSpace),input.start,input.end);this.#fogCache.set(input,fog)}
    return fog
  }

  #renderSky3dPass(frame: Frame): FrameResult["sky3dPass"] {
    const scene = this.#active
    const sky = frame.sky3d
    if (!sky || !scene || frame.visibility?.sky !== 2 || sky.visibility.worldIdentity !== scene.result.environment?.identity) {
      return undefined
    }
    for (let index = 0; index < sky.visibility.surfaces.length; index += 1) {
      if (scene.worldVisibility.has(sky.visibility.surfaces[index]!)) {
        throw new RenderingError("IdentityMismatch", "main and 3D-sky world surfaces overlap")
      }
    }

    const background = this.#scene.background
    const fog = this.#scene.fog
    const autoClear = this.#backend.autoClear
    const effects = this.#effects.visible
    const particles = this.#particles.visible
    const mainVisible = scene.mainStaticProps.visible
    const skyVisible = scene.skyStaticProps.visible
    const mainWorldVisible = scene.worldBundle.visible
    const skyWorldVisible = scene.skyWorldBundle.visible
    const mainAnimatedVisible = scene.mainAnimatedWorld.visible
    const skyAnimatedVisible = scene.skyAnimatedWorld.visible
    const mainTransparentVisible = scene.mainTransparentWorld.visible
    const skyTransparentVisible = scene.skyTransparentWorld.visible
    const modelOccurrencesVisible = scene.mainModelOccurrences.visible
    const marksVisible = scene.projectedMarkGroup.visible
    const waterVisibility = scene.waterMeshes.map((water) => water.mesh.visible)
    let rendered = false

    try {
      this.#setWorldVisibility(sky.visibility.surfaces, sky.visibility.cacheIdentity, 1)
      this.#setCamera(sky.camera)
      this.#setStaticPropVisibility(sky.visibility.leaves, 1, sky.camera)
      scene.worldBundle.visible = false
      scene.mainAnimatedWorld.visible = false
      scene.mainTransparentWorld.visible = false
      scene.skyWorldBundle.visible = true
      scene.skyAnimatedWorld.visible = true
      scene.skyTransparentWorld.visible = true
      scene.mainStaticProps.visible = false
      scene.skyStaticProps.visible = true
      scene.mainModelOccurrences.visible = false
      scene.projectedMarkGroup.visible = false
      if(scene.legacyVisuals){for(let i=0;i<5;i++)scene.legacyVisuals[i]!.world.visible=i===1}
      for (const water of scene.waterMeshes) water.mesh.visible = false
      this.#effects.visible = false
      this.#particles.visible = false
      this.#skyParticles.visible = true
      if (scene.skyGroup) scene.skyGroup.visible = true
      this.#setSceneFog(this.#fog(sky.fog))
      this.#backend.autoClear = true
      this.#drawPass("sky3d", this.#scene, this.#camera)
      this.#renderLegacyNoDepth(frame.legacyVisuals?.[1],1)
      this.#renderLegacyVisuals(frame.legacyVisuals?.[1],1)
      rendered = true
      this.#resetDepth("sky-depth-reset")
    } finally {
      this.#setCamera(frame.camera)
      scene.worldBundle.visible = mainWorldVisible
      scene.mainAnimatedWorld.visible = mainAnimatedVisible
      scene.mainTransparentWorld.visible = mainTransparentVisible
      scene.skyWorldBundle.visible = skyWorldVisible
      scene.skyAnimatedWorld.visible = skyAnimatedVisible
      scene.skyTransparentWorld.visible = skyTransparentVisible
      scene.mainStaticProps.visible = mainVisible
      scene.skyStaticProps.visible = skyVisible
      scene.mainModelOccurrences.visible = modelOccurrencesVisible
      scene.projectedMarkGroup.visible = marksVisible
      if(scene.legacyVisuals){for(let i=0;i<5;i++)scene.legacyVisuals[i]!.world.visible=i===0}
      for (let index = 0; index < scene.waterMeshes.length; index += 1) {
        scene.waterMeshes[index]!.mesh.visible = waterVisibility[index] ?? false
      }
      this.#effects.visible = effects
      this.#particles.visible = particles
      this.#skyParticles.visible = false
      if (scene.skyGroup) scene.skyGroup.visible = false
      this.#setSceneFog(this.#fog(frame.fog) ?? (fog as THREE.Fog | null))
      this.#scene.background = background
      this.#backend.autoClear = autoClear
    }
    if (!rendered) return undefined
    const visibleSkyPropSources = this.#visibleStaticSources[1]
    return Object.freeze({
      phases: Object.freeze(["sky3d", "depth-reset", "main", "restore"] as const),
      skySurfaces: sky.visibility.surfaces.length,
      skyProps: visibleSkyPropSources.length,
      mainProps: this.#visibleStaticSources[0].length,
      visibleSkyPropSources,
      fog: Object.freeze({ start: sky.fog.start, end: sky.fog.end, primary: sky.fog.primary }),
      stateRestored: this.#scene.background === background && this.#effects.visible === effects && this.#particles.visible === particles,
    })
  }

  #renderLegacyVisuals(frame: LegacyVisualFrame | undefined, view:LegacyViewKind): void {
    const owner=this.#active?.legacyVisuals?.[view]
    if (!owner || !frame) return
    owner.capture(this.#camera)
    if (!frame.quads.length) return
    const autoClear=this.#backend.autoClear
    // CGlowOverlay is drawn after the world view disables fog.
    owner.group.fog=null
    this.#backend.autoClear=false
    try {
      if(frame.quads.some(quad=>quad.layer===2))this.#drawPass(view===1?"sky-legacy-overlays":"legacy-overlays",owner.group,this.#camera)
    }
    finally { this.#backend.autoClear=autoClear }
  }

  #renderLegacyNoDepth(frame:LegacyVisualFrame|undefined,view:LegacyViewKind):void {
    const owner=this.#active?.legacyVisuals?.[view]
    if(!owner||!frame?.quads.some(quad=>quad.layer===1))return
    const clear=this.#backend.autoClear;this.#backend.autoClear=false
    owner.noDepthClip.enabled=this.#waterClipping.enabled;owner.noDepthClip.clippingPlanes=this.#waterClipping.clippingPlanes
    try{this.#drawPass(view===1?"sky-legacy-no-depth":"legacy-no-depth",owner.noDepth,this.#camera)}finally{this.#backend.autoClear=clear}
  }

  #renderWaterPasses(
    frame: Frame,
    preserveColor = false,
  ): Readonly<{
    passes: FrameResult["waterPasses"]
    timings: FrameResult["waterPassTimings"]
    restored: boolean
  }> {
    const scene = this.#active
    if (!scene) throw new RenderingError("InvalidState", "renderer has no active Water resources")
    const plan = frame.visibility?.water
    if (!plan) {
      this.#backend.autoClear = !preserveColor
      this.#drawPass("main", this.#scene, this.#camera)
      this.#renderLegacyNoDepth(frame.legacyVisuals?.[0],0)
      this.#backend.autoClear = true
      return Object.freeze({ passes: Object.freeze(["main"] as const), timings: Object.freeze([]), restored: true })
    }

    const soleMain = plan.passes.length === 1 && plan.passes[0]?.kind === "main" ? plan.passes[0] : undefined
    if (
      !plan.visibleWater && soleMain && !soleMain.clip && !soleMain.renderWaterSurface
      && soleMain.drawEntities && soleMain.drawSky2d === (frame.visibility?.sky === 1)
    ) {
      const background = this.#scene.background
      this.#backend.autoClear = !preserveColor
      this.#scene.background = soleMain.drawSky2d ? background : null
      try {
        this.#drawPass("main", this.#scene, this.#camera)
        this.#renderLegacyNoDepth(frame.legacyVisuals?.[0],0)
      } finally {
        this.#scene.background = background
      }
      return Object.freeze({ passes: Object.freeze(["main"] as const), timings: Object.freeze([]), restored: true })
    }

    if (plan.visibleWater && plan.passes.some((pass) => pass.kind !== "main" && pass.drawSky2d) && !scene.skyGroup) {
      throw new RenderingError("MissingInput", "Water auxiliary view requests the unresolved 2D sky pass")
    }
    const waterResource = plan.visibleWater
      ? scene.waterMaterials.get(plan.visibleWater.material.toLowerCase())
      : undefined
    const waterState = waterResource?.state
    if (plan.visibleWater?.evaluated && !waterState) {
      throw new RenderingError("MissingInput", `Water material ${plan.visibleWater.material} is unavailable`)
    }

    const markVisibility = scene.projectedMarks.map((mark) => mark.mesh.visible)
    const waterVisibility = scene.waterMeshes.map((water) => water.mesh.visible)
    const background = this.#scene.background
    const previousFog = this.#scene.fog
    const previousClearColor = this.#backend.getClearColor(new THREE.Color()).clone()
    const previousClearAlpha = this.#backend.getClearAlpha()
    const effectsVisible = this.#effects.visible
    const particlesVisible = this.#particles.visible
    const legacyVisible=scene.legacyVisuals?.map(view=>view.world.visible)
    const skyVisible = scene.skyGroup?.visible ?? false
    const completed: ("reflection" | "refraction" | "main" | "intersection")[] = []
    const timings: WaterPassTiming[] = []
    const uniforms = scene.waterFogUniforms

    try {
      for (const pass of plan.passes) {
        const legacyView:LegacyViewKind=pass.kind==="reflection"?2:pass.kind==="refraction"?3:pass.kind==="intersection"?4:0
        const passStarted = performance.now()
        this.#setWorldVisibility(pass.surfaces)
        const visibilityMilliseconds = performance.now() - passStarted
        for (const mark of scene.projectedMarks) {
          if (mark.visibility.kind === "world") mark.mesh.visible = scene.worldVisibility.has(mark.face)
        }
        for (const decal of scene.combatDecals.records.values()) decal.mesh.visible = scene.worldVisibility.has(decal.face)
        for (let index = 0; index < scene.waterMeshes.length; index += 1) {
          scene.waterMeshes[index]!.mesh.visible = pass.renderWaterSurface && waterVisibility[index] === true
        }
        if (scene.skyGroup) scene.skyGroup.visible = pass.drawSky2d
        this.#effects.visible = pass.drawEntities
        this.#particles.visible = pass.drawEntities
        if(scene.legacyVisuals)for(let index=0;index<5;index++)scene.legacyVisuals[index]!.world.visible=pass.drawEntities&&index===legacyView
        this.#scene.background = pass.drawSky2d ? background : null
        this.#setWaterCamera(pass, frame.camera)

        uniforms.enabled.value = 0
        if (pass.fog.kind === "water" && waterResource?.fog) {
          const fog = waterResource.state.fog
          const color = waterResource.fog.color
          this.#backend.setClearColor(color, 1)
          if (pass.fog.heightFog && pass.kind === "refraction") {
            const range = fog.end.value - fog.start.value
            uniforms.enabled.value = 1
            uniforms.waterHeight.value = plan.visibleWater!.surfaceZ
            uniforms.eyeHeight.value = pass.origin[2]
            uniforms.inverseFogRange.value = range === 0 ? 1 : 1 / range
            const exposure = Number(scene.exposureUniform.value)
            ;(uniforms.fogColor.value as THREE.Vector3).set(
              color.r * exposure,
              color.g * exposure,
              color.b * exposure,
            )
            this.#setSceneFog(null)
          } else {
            this.#setSceneFog(waterResource.fog)
          }
        } else {
          this.#backend.setClearColor(previousClearColor, previousClearAlpha)
          this.#setSceneFog(this.#fog(frame.fog))
        }

        const sceneMilliseconds = performance.now() - passStarted - visibilityMilliseconds
        const clipStarted = performance.now()
        const restoreClip = this.#setClip(pass.clip)
        const clippingMilliseconds = performance.now() - clipStarted
        let renderMilliseconds = 0
        try {
          this.#backend.setRenderTarget(
            pass.kind === "reflection"
              ? scene.reflectionTarget ?? (() => { throw new RenderingError("MissingInput", "Water reflection target was not authored") })()
              : pass.kind === "refraction"
                ? scene.refractionTarget ?? (() => { throw new RenderingError("MissingInput", "Water refraction target was not authored") })()
                : this.#framePresentation?.target ?? null,
          )
          this.#backend.autoClear = pass.kind === "main" && preserveColor ? false : pass.kind !== "intersection"
          const renderStarted = performance.now()
          this.#drawPass(pass.kind, this.#scene, this.#camera)
          if(pass.drawEntities){this.#renderLegacyNoDepth(frame.legacyVisuals?.[legacyView],legacyView);if(legacyView!==0)this.#renderLegacyVisuals(frame.legacyVisuals?.[legacyView],legacyView)}
          renderMilliseconds = performance.now() - renderStarted
          completed.push(pass.kind)
        } finally {
          restoreClip()
        }
        timings.push(Object.freeze({
          kind: pass.kind,
          visibilityMilliseconds,
          sceneMilliseconds,
          clippingMilliseconds,
          renderMilliseconds,
          totalMilliseconds: performance.now() - passStarted,
        }))
      }
    } finally {
      uniforms.enabled.value = 0
      this.#backend.setRenderTarget(this.#framePresentation?.target ?? null)
      this.#backend.autoClear = true
      this.#backend.setClearColor(previousClearColor, previousClearAlpha)
      this.#setSceneFog(previousFog as THREE.Fog | null)
      this.#scene.background = background
      this.#effects.visible = effectsVisible
      this.#particles.visible = particlesVisible
      if(scene.legacyVisuals)scene.legacyVisuals.forEach((view,index)=>{view.world.visible=legacyVisible?.[index]??false})
      if (scene.skyGroup) scene.skyGroup.visible = skyVisible
      this.#setWorldVisibility(frame.visibility!.surfaces)
      for (const decal of scene.combatDecals.records.values()) decal.mesh.visible = scene.worldVisibility.has(decal.face)
      for (let index = 0; index < scene.projectedMarks.length; index += 1) {
        scene.projectedMarks[index]!.mesh.visible = markVisibility[index]!
      }
      for (let index = 0; index < scene.waterMeshes.length; index += 1) {
        scene.waterMeshes[index]!.mesh.visible = waterVisibility[index]!
      }
      this.#setCamera(frame.camera)
    }

    if (!completed.includes("main")) throw new RenderingError("MalformedInput", "Water view plan omitted the main pass")
    return Object.freeze({
      passes: Object.freeze(completed),
      timings: Object.freeze(timings),
      restored: this.#backend.autoClear
        && this.#scene.background === background
        && this.#scene.fog === previousFog
        && this.#effects.visible === effectsVisible
        && this.#particles.visible === particlesVisible
        && uniforms.enabled.value === 0,
    })
  }

  #clearDynamic(group: THREE.Group): void {
    for (const child of [...group.children]) this.#disposeDynamicInstance(child)
  }

  #buildParticleMaterials(inputs: NonNullable<MapLoadRequest["particleTextures"]>, states: ReadonlyMap<string, MaterialStateInput>, disposables: OwnedResourceGeneration,
    waterFog: SourceWaterFogUniforms, depth: SourceParticleDepth, textures: Map<string, THREE.Texture>, materials: Map<string, THREE.MeshBasicNodeMaterial>, pipelines: THREE.Group, exposure: any, hdr: boolean): void {
    const depthNode = depth.sample()
    const graphs = new ParticleMaterialGraphs()
    for (const texture of inputs) {
      const state = states.get(texture.material.toLowerCase())
      if (!state) throw new RenderingError("MissingInput", `Particle material state ${texture.material} is unavailable`)
      const key = particlePipelineKey(particlePipelineVariant(texture.material, state))
      if (materials.has(key)) continue
      const additive = texture.additiveSprite
      const candidate = textureFromAuthored(texture, additive && !additive.srgb ? THREE.NoColorSpace : THREE.SRGBColorSpace, 0, this.textureQuality)
      const alias = texture.frameCount === 1 ? particleTextureAlias(candidate, textures.values()) : undefined
      const value = alias ?? disposables.add(candidate)
      textures.set(texture.material.toLowerCase(), value)
      const material = new THREE.MeshBasicNodeMaterial(materialOptions({ logicalPath: texture.material, width: texture.width, height: texture.height, shader: 7, features: 1, textureRole: 0 }, state))
      disposables.add(material); applyParticleDepthState(material, state)
      const graph = graphs.get(material, { texture: value, state, spriteCard: texture.spriteCard, additive,
        waterFog, depth: depthNode, exposure, hdr, fog: this.#viewFogUniforms })
      if (graph.position) { material.positionNode = graph.position; material.forceSinglePass = true }
      material.userData.sourceParticleDepth = texture.spriteCard?.depthBlend === true
      material.colorNode = graph.color
      if (additive) { material.fog = false; material.forceSinglePass = true }
      material.toneMapped = false; material.name = `particle:${texture.material.toLowerCase()}`
      material.blending = THREE.CustomBlending; material.transparent = true
      materials.set(key, material)
      const geometry = this.#createParticleBatchGeometry(1); disposables.add(geometry)
      for (const side of particlePreparationSides(material)) {
        const prepared = side === material.side ? material : material.clone()
        if (prepared !== material) { prepared.side = side; bindParticleTexture(prepared, value); disposables.add(prepared) }
        const mesh = new THREE.Mesh(geometry, prepared); mesh.frustumCulled = false; pipelines.add(mesh)
      }
    }
  }

  #createParticleBatchGeometry(capacity:number):THREE.BufferGeometry{
    if (this.#uploadEvidence) this.#uploadEvidence.particleGeometries += 1
    const geometry=new THREE.BufferGeometry(),dynamic=(array:Float32Array,size:number)=>new THREE.BufferAttribute(array,size).setUsage(THREE.DynamicDrawUsage),indices=capacity*4>0xffff?new Uint32Array(capacity*6):new Uint16Array(capacity*6)
    writeParticleQuadIndices(indices)
    geometry.setAttribute("position",dynamic(new Float32Array(capacity*12),3));geometry.setAttribute("particleCenterOrientation",dynamic(new Float32Array(capacity*16),4));geometry.setAttribute("uv",dynamic(new Float32Array(capacity*8),2));geometry.setAttribute("particleUvNext",dynamic(new Float32Array(capacity*8),2));geometry.setAttribute("particleSheetBlend",dynamic(new Float32Array(capacity*4),1));geometry.setAttribute("particleColor",dynamic(new Float32Array(capacity*16),4));geometry.setIndex(new THREE.BufferAttribute(indices,1));return geometry
  }

  #updateParticleBatchGeometry(
    geometry: THREE.BufferGeometry,
    items: readonly ParticleItem[],
    start: number,
    end: number,
    camera: Camera,
  ): void {
    const first = items[start]!
    if (first.primitive === "rope") {
      if (end !== start + 1 || !first.mesh) throw new RenderingError("MalformedInput", "rope batch requires its native mesh")
      const mesh = first.mesh, vertices = mesh.positions.length / 3
      for (const name of ["position", "particleCenterOrientation", "uv", "particleUvNext", "particleSheetBlend", "particleColor"] as const) {
        const attribute = geometry.getAttribute(name) as THREE.BufferAttribute
        const array = attribute.array as Float32Array
        if (name === "position") array.set(mesh.positions)
        else if (name === "particleCenterOrientation") for (let vertex = 0; vertex < vertices; vertex++) {
          array[vertex * 4] = mesh.positions[vertex * 3]!
          array[vertex * 4 + 1] = mesh.positions[vertex * 3 + 1]!
          array[vertex * 4 + 2] = mesh.positions[vertex * 3 + 2]!
          array[vertex * 4 + 3] = 0
        }
        else if (name === "uv" || name === "particleUvNext") array.set(mesh.uv)
        else if (name === "particleSheetBlend") array.fill(0, 0, vertices)
        else for (let component = 0; component < vertices * 4; component++) array[component] = mesh.colors[component]! / 255
        const count = vertices * attribute.itemSize
        attribute.clearUpdateRanges(); attribute.addUpdateRange(0, count); attribute.needsUpdate = true
        if (this.#uploadEvidence) { this.#uploadEvidence.particleUploadBytes += count * 4; this.#uploadEvidence.particleFullUploadBytes += count * 4; this.#uploadEvidence.particleUploadWrites++ }
      }
      const indices = geometry.getIndex()!
      indices.array.set(mesh.indices); indices.clearUpdateRanges(); indices.addUpdateRange(0, mesh.indices.length); indices.needsUpdate = true
      if (this.#uploadEvidence) { const bytes = mesh.indices.length * indices.array.BYTES_PER_ELEMENT; this.#uploadEvidence.particleUploadBytes += bytes; this.#uploadEvidence.particleFullUploadBytes += bytes; this.#uploadEvidence.particleUploadWrites++ }
      geometry.userData.ropeIndices = true
      geometry.setDrawRange(0, mesh.indices.length)
      return
    }
    if (geometry.userData.ropeIndices) {
      const indices = geometry.getIndex()!, array = indices.array
      writeParticleQuadIndices(array as Uint16Array | Uint32Array)
      indices.clearUpdateRanges(); indices.addUpdateRange(0, array.length); indices.needsUpdate = true
      if (this.#uploadEvidence) { this.#uploadEvidence.particleUploadBytes += array.byteLength; this.#uploadEvidence.particleFullUploadBytes += array.byteLength; this.#uploadEvidence.particleUploadWrites++ }
      geometry.userData.ropeIndices = false
    }
    const positions = (geometry.getAttribute("position") as THREE.BufferAttribute).array as Float32Array
    const centers = (geometry.getAttribute("particleCenterOrientation") as THREE.BufferAttribute).array as Float32Array
    const uv = (geometry.getAttribute("uv") as THREE.BufferAttribute).array as Float32Array
    const uvNext = (geometry.getAttribute("particleUvNext") as THREE.BufferAttribute).array as Float32Array
    const sheetBlend = (geometry.getAttribute("particleSheetBlend") as THREE.BufferAttribute).array as Float32Array
    const colors = (geometry.getAttribute("particleColor") as THREE.BufferAttribute).array as Float32Array
    const writeParticleQuad = createParticleQuadWriter(camera)
    const updates = this.#particleAttributeUpdates
    resetParticleAttributeUpdates(updates)
    const arrays = { uv, uvNext, sheetBlend, colors }
    for (let index = 0; index < end - start; index += 1) {
      const item = items[start + index]!
      if (item.primitive === "rope") throw new RenderingError("MalformedInput", "rope geometry cannot share a sprite batch")
      writeParticleCenters(centers, index * 16, item.position, item.orientationType)
      const orientationTint = writeParticleQuad(item, positions, index * 12)
      writeParticleAppearance(item, arrays, index, updates, orientationTint)
    }
    const vertices = (end - start) * 4
    const centerAttribute = geometry.getAttribute("particleCenterOrientation") as THREE.BufferAttribute
    centerAttribute.clearUpdateRanges(); centerAttribute.addUpdateRange(0, vertices * 4); centerAttribute.needsUpdate = true
    if (this.#uploadEvidence) { this.#uploadEvidence.particleUploadBytes += vertices * 16; this.#uploadEvidence.particleFullUploadBytes += vertices * 16; this.#uploadEvidence.particleUploadWrites++ }
    for (const [index, name] of (["position", "uv", "particleUvNext", "particleSheetBlend", "particleColor"] as const).entries()) {
      const update = index > 0 ? updates[index - 1]! : undefined
      if (update && update.end === 0) continue
      const attribute = geometry.getAttribute(name) as THREE.BufferAttribute
      const offset = update?.start ?? 0
      const count = update ? update.end - update.start : vertices * attribute.itemSize
      attribute.clearUpdateRanges()
      attribute.addUpdateRange(offset, count)
      attribute.needsUpdate = true
      if (this.#uploadEvidence) {
        this.#uploadEvidence.particleUploadBytes += count * Float32Array.BYTES_PER_ELEMENT
        this.#uploadEvidence.particleUploadWrites += 1
        this.#uploadEvidence.particleFullUploadBytes += vertices * attribute.itemSize * Float32Array.BYTES_PER_ELEMENT
      }
    }
    geometry.setDrawRange(0, (end - start) * 6)
  }

  #clearParticleBatches():void{
    this.#framePresentation?.abandon()
    this.#particleVisibility.reset()
    for(const retained of this.#particleBatchMeshes){retained.mesh.removeFromParent();retained.mesh.geometry.dispose()}
    this.#particleBatchMeshes=[];this.#particleBatchCount=0
    for (const pool of this.#panelParticlePools.values()) { pool.group.clear(); for (const entry of pool.meshes) entry.mesh.geometry.dispose() }
    this.#panelParticlePools.clear()
  }

  captureParticleBatchEvidence() {
    return this.#particleBatchMeshes.map(({ key, mesh }) => ({ key, visible: mesh.visible,
      sky: mesh.parent === this.#skyParticles, count: mesh.geometry.drawRange.count,
      positions: Array.from(mesh.geometry.getAttribute("position").array.slice(0, 12)),
      material: (mesh.material as THREE.Material).name,
      depthTest:(mesh.material as THREE.Material).depthTest,depthWrite:(mesh.material as THREE.Material).depthWrite,depthFunc:(mesh.material as THREE.Material).depthFunc }))
  }

  captureMaterialDepthEvidence() {
    const materials=new Map<number,MaterialDepthEvidence>()
    this.#scene.traverseVisible(object=>{
      if(!(object instanceof THREE.Mesh))return
      for(const material of Array.isArray(object.material)?object.material:[object.material])materials.set(material.id,{id:material.id,name:material.name,identity:object.userData.materialIdentity,depthTest:material.depthTest,depthWrite:material.depthWrite,depthFunc:material.depthFunc,transparent:material.transparent,renderOrder:object.renderOrder})
    })
    return [...materials.values()]
  }

  #stageParticleBatches(
    items: readonly ParticleItem[],
    camera: Camera,
    pool?: { group: THREE.Group; meshes: { key: string; capacity: number; mesh: THREE.Mesh }[]; ranges: MutableParticleBatchRange[] },
    skyCamera?: Camera,
  ): void {
    const assets = this.#active ?? this.#panelAssets
    if (!pool) this.#particleVisibility.stage(items)
    const meshes = pool?.meshes ?? this.#particleBatchMeshes, ranges = pool?.ranges ?? this.#particleBatchRanges, group = pool?.group ?? this.#particles
    const count = fillParticleBatchRanges(items, ranges)
    if (!pool) this.#particleBatchCount = count
    for (let index = 0; index < count; index += 1) {
      const batch = ranges[index]!
      const first = items[batch.start]!
      const key = particlePipelineKey(first)
      const required = first.primitive === "rope" ? Math.max(Math.ceil((first.mesh?.positions.length ?? 0) / 12), (first.mesh?.indices.length ?? 0) / 6) : batch.end - batch.start
      let retained = meshes[index]
      if (retained && (retained.key !== key || retained.capacity < required)) {
        const reusable = meshes.findIndex((candidate, candidateIndex) =>
          candidateIndex > index && candidate.key === key && candidate.capacity >= required,
        )
        if (reusable >= 0) {
          meshes[index] = meshes[reusable]!
          meshes[reusable] = retained
          retained = meshes[index]
        }
      }
      if (!retained || retained.key !== key || retained.capacity < required) {
        if (retained) {
          retained.mesh.removeFromParent()
          retained.mesh.geometry.dispose()
        }
        let capacity = 1
        while (capacity < required) capacity *= 2
        const material = assets?.particleBatchMaterials.get(key)
        if (!material) throw new RenderingError("IdentityMismatch", `Particle output blend differs from authored material: ${first.material}`)
        const geometry = this.#createParticleBatchGeometry(capacity)
        const mesh = new THREE.Mesh(geometry, material)
        mesh.frustumCulled = false
        const profile = browserFrameProfiler()
        mesh.onBeforeRender = (renderer, _scene, camera) => {
          if (Array.isArray((renderer as any)._compilationPromises)) return
          if (material.userData.sourceParticleDepth || (assets!.particleDepth.evidenceRequested && mesh.parent!==this.#skyParticles && camera === this.#camera
            && (renderer as THREE.WebGPURenderer).getRenderTarget() === (this.#framePresentation?.target ?? null))) {
            assets!.particleDepth.capture(renderer as THREE.WebGPURenderer, camera, mesh.parent!==this.#skyParticles && camera === this.#camera
              && (renderer as THREE.WebGPURenderer).getRenderTarget() === (this.#framePresentation?.target ?? null))
          }
          if (profile && !material.userData.firstParticleUse) {
            material.userData.firstParticleUse = true
            ;(profile.firstParticleUses ??= []).push({ at: performance.now(), id: material.id, identity: material.name, pass: profile.currentPass?.identity ?? null })
          }
        }
        retained = { key, capacity, mesh }
        meshes[index] = retained
      }
      const owner = first.sky ? this.#skyParticles : group
      if (retained.mesh.parent !== owner) owner.add(retained.mesh)
      const batchCamera = first.sky ? skyCamera : camera
      if (!batchCamera) { retained.mesh.visible = false; continue }
      this.#updateParticleBatchGeometry(retained.mesh.geometry, items, batch.start, batch.end, batchCamera)
      retained.mesh.renderOrder = batch.start+(retained.mesh.material.transparent?0:0x200000)
      retained.mesh.visible = true
    }
    for (let index = count; index < meshes.length; index += 1) {
      meshes[index]!.mesh.visible = false
    }
    if (this.#uploadEvidence) this.#uploadEvidence.retainedParticleBatches = this.#particleBatchMeshes.length + [...this.#panelParticlePools.values()].reduce((sum, pool) => sum + pool.meshes.length, 0)
  }

  #applyPose(
    instance: THREE.Group,
    pose: NonNullable<ModelItem["pose"]>,
    _retainGeometry: boolean,
    retainedMeshes?: THREE.Mesh[],
    renderBounds?: ModelItem["renderBounds"],
  ): THREE.Mesh[] {
    const meshes = retainedMeshes ?? []
    if (!retainedMeshes) instance.traverse((object) => { if (object instanceof THREE.Mesh) meshes.push(object) })
    if (meshes.length !== pose.primitives.length) {
      throw new RenderingError("IdentityMismatch", "posed model primitive count differs")
    }
    // Authored camera/logic models can contain no drawable geometry or bones.
    // They have no skinning work; do not manufacture a bone or reject the map.
    if (meshes.length === 0 && pose.boneMatrices.length === 0) return meshes
    if (pose.boneMatrices.length % 12 !== 0 || pose.boneMatrices.length === 0 || pose.boneMatrices.length > 256 * 12) {
      throw new RenderingError("MalformedInput", `authored model bone matrices are invalid: ${pose.model}:${pose.identity} meshes=${meshes.length} matrices=${pose.boneMatrices.length}`)
    }
    let skeleton = instance.userData.sourceSkeleton as THREE.Skeleton | undefined
    const created = !skeleton
    if (!skeleton) {
      skeleton = createSourceModelSkeleton(pose.boneMatrices)
      instance.userData.sourceSkeleton = skeleton
    }
    let matrixBytes: number
    try { matrixBytes = created ? skeleton.bones.length * 16 * Float32Array.BYTES_PER_ELEMENT : updateSourceModelSkeleton(skeleton, pose.boneMatrices) }
    catch { throw new RenderingError("IdentityMismatch", "authored model bone count differs") }
    if (this.#uploadEvidence) {
      if (matrixBytes === 0) this.#uploadEvidence.unchangedPoseAttributes += 1
      else this.#uploadEvidence.poseAttributes += 1
      this.#uploadEvidence.poseUploadBytes += matrixBytes
    }
    if (matrixBytes !== 0) this.#instrumentation?.poseUpload(matrixBytes)
    for (let primitive = 0; primitive < meshes.length; primitive += 1) {
      let object = meshes[primitive]!
      const posed = pose.primitives[primitive]!
      object.visible = posed.visible !== false
      if (
        posed.material !== object.userData.primitiveMaterial
        || posed.vertexCount !== object.geometry.getAttribute("position").count
      ) throw new RenderingError("IdentityMismatch", "posed model primitive differs from its template")
      if (!(object instanceof THREE.SkinnedMesh)) {
        const authored = SOURCE_MODEL_BIND_GEOMETRY.get(object.geometry)
        if (!authored || authored.palette.some((bone) => bone >= pose.boneMatrices.length / 12)) {
          throw new RenderingError("IdentityMismatch", "authored model bone palette differs from its skeleton")
        }
        const skinned = bindSourceModelMesh(authored.geometry, object.material, skeleton)
        skinned.visible = posed.visible !== false
        skinned.userData = { ...object.userData }
        transferModelBindings(object, skinned)
        const parent = object.parent
        if (!parent) throw new RenderingError("MissingInput", "posed model mesh parent is unavailable")
        const index = parent.children.indexOf(object)
        parent.remove(object)
        parent.add(skinned)
        parent.children.splice(parent.children.indexOf(skinned), 1)
        parent.children.splice(index, 0, skinned)
        object = skinned
        meshes[primitive] = object
      }
      if (renderBounds && object instanceof THREE.SkinnedMesh) applyMapModelRenderBounds(object, renderBounds)
      object.userData.posedPrimitive = posed.primitive
      const flex = pose.flex?.find((value) => value.primitive === posed.primitive)
      if (flex && flex.indices.length > 0) {
        if (object.userData.dynamicGeometry !== true) {
          const source = object.geometry
          const geometry = new THREE.BufferGeometry()
          for (const name of Object.keys(source.attributes)) {
            const attribute = source.getAttribute(name)
            geometry.setAttribute(name, name === "position" || name === "normal" ? attribute.clone().setUsage(THREE.DynamicDrawUsage) : attribute)
          }
          geometry.setIndex(source.getIndex())
          SOURCE_MODEL_BASE_COLOR.set(geometry, SOURCE_MODEL_BASE_COLOR.get(source))
          object.geometry = geometry
          object.userData.dynamicGeometry = true
        }
        const position = object.geometry.getAttribute("position") as THREE.BufferAttribute
        const normal = object.geometry.getAttribute("normal") as THREE.BufferAttribute
        for (let i = 0; i < flex.indices.length; i += 1) {
          const vertex = flex.indices[i]!
          if (vertex >= position.count) throw new RenderingError("IdentityMismatch", "model flex vertex is outside its primitive")
          position.setXYZ(vertex, flex.positions[i * 3]!, flex.positions[i * 3 + 1]!, flex.positions[i * 3 + 2]!)
          normal.setXYZ(vertex, flex.normals[i * 3]!, flex.normals[i * 3 + 1]!, flex.normals[i * 3 + 2]!)
        }
        const first = flex.indices[0]! * 3, count = (flex.indices[flex.indices.length - 1]! + 1) * 3 - first
        position.addUpdateRange(first, count)
        normal.addUpdateRange(first, count)
        position.needsUpdate = true
        normal.needsUpdate = true
        this.#instrumentation?.poseUpload(count * 2 * Float32Array.BYTES_PER_ELEMENT)
        if (this.#uploadEvidence) {
          this.#uploadEvidence.poseAttributes += 2
          this.#uploadEvidence.poseUploadBytes += count * 2 * Float32Array.BYTES_PER_ELEMENT
        }
      }
      if (object.userData.dynamicMaterial !== true) {
        object.material = Array.isArray(object.material) ? object.material.map((material) => this.#cloneDynamicMaterial(material)) : this.#cloneDynamicMaterial(object.material)
        object.userData.dynamicMaterial = true
      }
      if (Array.isArray(object.material)) {
        for (const material of object.material) {
          material.transparent = posed.translucent
          material.depthWrite = !posed.translucent
        }
      } else {
        object.material.transparent = posed.translucent
        object.material.depthWrite = !posed.translucent
      }
    }
    return meshes
  }

  #stageViewModel(item: ModelItem, frame: Frame): readonly [number, number] {
    const key = modelKey(item.model, item.skin ?? 0, item.body ?? 0)
    let retained = this.#viewModelInstances.get(item.identity)
    if (!retained || retained.model !== key) {
      if (retained) {
        retained.root.parent?.remove(retained.root)
        this.#retainedModels.retain(`view:${item.identity}:${retained.model}`, retained)
      }
      const cached = this.#retainedModels.take(`view:${item.identity}:${key}`)
      if (cached && !("root" in cached)) throw new RenderingError("IdentityMismatch", "retained model namespace differs")
      retained = cached as RetainedViewModel | undefined
      if (retained) {
        if (item.pose) this.#applyPose(retained.instance, item.pose, true, retained.meshes)
      } else {
        const prepared = this.#preparedModelInstances.take(`view:${key}`)
        const instance = prepared?.instance ?? this.#active!.modelTemplates.get(key)!.clone(true)
        if (!item.pose) throw new RenderingError("MalformedInput", "viewmodel pose is missing")
        const meshes = this.#applyPose(instance, item.pose, false, prepared?.meshes)
        const root = new THREE.Group()
        root.userData.identity = item.identity
        root.setRotationFromMatrix(new THREE.Matrix4().set(0, -1, 0, 0, 0, 0, 1, 0, -1, 0, 0, 0, 0, 0, 0, 1))
        root.add(instance)
        root.traverse((object) => object.layers.set(1))
        retained = { ...prepared, model: key, root, instance, meshes, seen: 0 }
      }
      this.#viewModels.add(retained.root)
      this.#viewModelInstances.set(item.identity, retained)
    } else if (item.pose) {
      this.#applyPose(retained.instance, item.pose, true, retained.meshes)
    }
    this.#applyDynamicModelLighting(retained, item)
    this.#applyDynamicModelCloak(retained, item)
    const frameState = item.pose?.viewmodel
    if (!frameState) throw new RenderingError("MalformedInput", "complete viewmodel frame state is missing")
    const encoded = this.#active!.loadRequest.modelFacing?.get(item.model.toLowerCase())
    if (!encoded) throw new RenderingError("MissingInput", `StudioModel facing ${item.model} is unavailable`)
    const expectedFront = frameState.reflected
      ? encoded.frontFace === "clockwise" ? "counter-clockwise" : "clockwise"
      : encoded.frontFace
    if (frameState.cullFace !== encoded.cullFace || frameState.frontFace !== expectedFront) {
      throw new RenderingError("IdentityMismatch", "viewmodel effective facing differs from encoded facing and reflection")
    }
    sourceTransform(retained.instance, item.position, item.angles!)
    retained.instance.scale.set(item.scale, frameState.reflected ? -item.scale : item.scale, item.scale)
    retained.instance.visible = frameState.drawDisposition === "draw"
    const projection = item.viewModelProjection!
    if (projection.optionalViewSpaceYReflection !== frameState.reflected) {
      throw new RenderingError("IdentityMismatch", "viewmodel reflection state differs")
    }
    const verticalFov = sourceHorizontal4By3FovToVertical(projection.horizontalFov4By3)
    if (this.#viewCamera.fov !== verticalFov || this.#viewCamera.near !== projection.near || this.#viewCamera.far !== frame.camera.far) {
      this.#viewCamera.fov = verticalFov
      this.#viewCamera.near = projection.near
      this.#viewCamera.far = frame.camera.far
      this.#viewCamera.updateProjectionMatrix()
    }
    return sourceViewportDepthRange(projection.depthRange)
  }

  #cloneDynamicMaterial(material: THREE.Material): THREE.Material {
    this.#instrumentation?.dynamicModel("materialCreated")
    return material.clone()
  }

  #disposeDynamicInstance(instance: THREE.Object3D): void {
    this.#instrumentation?.dynamicModel("disposed")
    this.#renderObjectLifetime?.release(instance)
    disposeDynamicModel(instance, this.#instrumentation ? () => this.#instrumentation!.dynamicModel("materialDisposed") : undefined)
  }

  #applyDynamicModelCloak(retained: { instance: THREE.Group; meshes?: THREE.Mesh[] }, item: ModelItem): void {
    const meshes = retained.meshes
    if (!meshes) return
    let cloak = retained.instance.userData.sourceModelCloak as SourceModelCloak | undefined
    if (!cloak && !item.pose?.cloak && !(retained.instance.userData.sourceStaticCloak ??= meshes.some(mesh => {
      const material = this.#active!.loadRequest.modelMaterials?.get(String(mesh.userData.materialIdentity).toLowerCase())
      return material && (material.cloakProxy & 3) === 0 && "cloak" in material.state && material.state.cloak?.enabled && material.state.cloak.factor !== 0
    }))) return
    if (!cloak) {
      cloak = new SourceModelCloak(meshes, this.#cloakFramebuffer,
        identity => this.#active!.loadRequest.modelMaterials?.get(identity),
        identity => this.#active!.modelLightingTextures.get(identity)?.normal?.())
      retained.instance.userData.sourceModelCloak = cloak
      this.#backend.setTransparentSort((a, b) => sourceCloakTransparentSort(a, b, this.#backend.info.calls))
    }
    cloak?.update(item.pose?.cloak, item.viewModel === true, retained.instance, item.viewModel ? this.#viewCamera : this.#camera, item.identity)
    retained.instance.visible = item.viewModel === true || (item.pose?.cloak?.worldFactor ?? 0) < 1
  }

  #applyDynamicModelLighting(
    retained: DynamicModelLighting,
    item: ModelItem,
    scene?: ModelLightingScene,
  ): void {
    const input = item.modelLighting
    if (!input) return
    const resources = scene ?? {
      materials: this.#active!.loadRequest.modelMaterials,
      states: this.#active!.materialStates,
      waterFog: this.#active!.waterFogUniforms,
      textures: this.#active!.modelLightingTextures,
      cubemap: this.#active!.modelCubemap,
      exposure: this.#active!.exposureUniform,
      graphs: this.#active!.modelLightingGraphs,
    }
    let uniforms = retained.lighting
    const localEnvironment = input.localEnvironment ? resources.cubemap(input.localEnvironment) : undefined
    const environmentShape = modelEnvironmentShape(localEnvironment)
    const meshes = retained.meshes ?? []
    if (!retained.meshes) retained.instance.traverse((object) => { if (object instanceof THREE.Mesh && !object.userData.sourceCloakOverlay) meshes.push(object) })
    retained.meshes = meshes
    if (retained.cubemapIdentity !== input.localEnvironment) {
      if (localEnvironment) for (const mesh of meshes) bindModelTexture(mesh, "sourceEnvironment", localEnvironment)
      retained.cubemapIdentity = input.localEnvironment
    }
    if (!uniforms || retained.environmentShape !== environmentShape) {
      uniforms ??= createSourceModelLightingUniforms()
      retained.lighting = uniforms
      retained.environmentShape = environmentShape
      for (const mesh of meshes) {
        bindModelLighting(mesh, uniforms)
        const identity = String(mesh.userData.materialIdentity).toLowerCase()
        const authored = resources.materials?.get(identity)
        if (!authored || authored.shader === "unlit-generic" || authored.shader === "unlit-two-texture" || authored.shader === "modulate" || authored.shader === "refract") continue
        if (mesh.userData.dynamicMaterial !== true) {
          mesh.material = Array.isArray(mesh.material)
            ? mesh.material.map((material) => this.#cloneDynamicMaterial(material))
            : this.#cloneDynamicMaterial(mesh.material)
          mesh.userData.dynamicMaterial = true
        }
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
        for (const material of materials) {
          if (!(material instanceof THREE.MeshBasicNodeMaterial)) {
            throw new RenderingError("UnsupportedFeature", `model lighting shader ${identity} is unavailable`)
          }
          const state = authored.state as Readonly<{
            halfLambert?: boolean
            phong?: SourceModelPhongState | null
            dilation?: number
            ambientOcclusionColor?: readonly [number, number, number]
            glossiness?: number
          }>
          if (typeof state.halfLambert !== "boolean") {
            throw new RenderingError("MissingInput", `model half-Lambert state ${identity} is unavailable`)
          }
          const textures = resources.textures.get(identity)
          const environmentState = authored.environmentMap as Readonly<{
            tint: readonly [number, number, number]
          }> | null
          let environment: Readonly<{ texture: THREE.CubeTexture; tint: readonly [number, number, number]; scale: number }> | undefined
          if (environmentState && (textures?.environment || input.localEnvironment)) {
            const texture = textures?.environment ?? localEnvironment
            if (!texture) throw new RenderingError("MissingInput", `model cubemap ${input.localEnvironment} is unavailable`)
            environment = Object.freeze({
              texture,
              tint: Object.freeze(environmentState.tint.map(sourceShaderGammaToLinear)) as readonly [number, number, number],
              scale: authored.shader === "eye-refract" ? 1 : this.configuration.lightingProfile === "hdr" ? 16 : 1,
            })
          }
          const base = SOURCE_MODEL_BASE_COLOR.get(mesh.geometry)
          if (!base) throw new RenderingError("MissingInput", `authored model base sample ${identity} is unavailable`)
          const baseTexture = SOURCE_MODEL_BASE_TEXTURE.get(base)
          if (authored.shader === "eye-refract" || authored.shader === "eyes") {
            const primitive = Number(mesh.userData.posedPrimitive ?? mesh.userData.sourcePrimitive)
            const eye = item.eyeStates?.find((value) => value.primitive === primitive)
            if (!eye || !textures?.iris || typeof state.dilation !== "number") {
              throw new RenderingError("MissingInput", `authored Studio eye state ${identity}:${primitive} is unavailable`)
            }
            const eyeUniforms = retained.eyes?.get(primitive) ?? createSourceModelEyeUniforms()
            const eyes = retained.eyes ?? new Map<number, SourceModelEyeUniforms>()
            eyes.set(primitive, eyeUniforms)
            retained.eyes = eyes
            updateSourceModelEyeUniforms(eyeUniforms, eye)
            bindModelLighting(mesh, uniforms, eyeUniforms)
          }
          material.colorNode = modelMaterialGraph(mesh, resources.graphs, { shader: authored.shader, state: state as typeof state & { halfLambert: boolean },
            fragment: resources.states.get(identity), base, baseTexture: baseTexture && { texture: baseTexture.texture, sourceFormat: baseTexture.input.sourceFormat },
            textures, environment, exposure: resources.exposure, waterFog: resources.waterFog }, () => this.#instrumentation?.dynamicModel("graphCreated"))
          material.needsUpdate = true
        }
      }
    }
    updateSourceModelLightingUniforms(uniforms, input)
    if (retained.eyes) {
      for (const eye of item.eyeStates ?? []) {
        const uniforms = retained.eyes.get(eye.primitive)
        if (uniforms) updateSourceModelEyeUniforms(uniforms, eye)
      }
    }
  }

  #placeDynamicInstance(instance: THREE.Group, order: number): void {
    if (instance.parent !== this.#effects) this.#effects.add(instance)
    const children = this.#effects.children
    if (children[order] === instance) return
    const previous = children.indexOf(instance)
    if (previous < 0) throw new RenderingError("InvalidState", "dynamic occurrence escaped its owning scene")
    children.splice(previous, 1)
    children.splice(order, 0, instance)
  }

  #stageDynamicItems(frame: Frame): readonly [number, number] | undefined {
    if (frame.modelVisibility) {
      for (const [identity, visible] of frame.modelVisibility) {
        const instance = this.#active!.modelOccurrenceInstances.get(identity)
        if (!instance) throw new RenderingError("MissingInput", `model occurrence ${identity} is unavailable`)
        if (!this.#dynamicModelInstances.has(identity)) instance.visible = visible
      }
    }
    const prior = this.#stagedDynamic
    if (prior && prior.particles === frame.particles && prior.models === frame.models && prior.brushModels === frame.brushModels && prior.studioModels === frame.studioModels) {
      this.#stageParticleBatches(frame.particles ?? [], frame.camera, undefined, frame.sky3d?.camera)
      if (this.#viewCamera.far !== frame.camera.far) {
        this.#viewCamera.far = frame.camera.far
        this.#viewCamera.updateProjectionMatrix()
      }
      return prior.viewModelDepthRange
    }

    const revision = ++this.#dynamicRevision
    let effectOrder = 0
    let viewModelDepthRange: readonly [number, number] | undefined
    try {
      const particleItems = frame.particles ?? []
      for (const item of particleItems) {
        if (item.secondarySheet) {
          throw new RenderingError("UnsupportedFeature", "dual-sequence Particle rendering requires exact selected material support")
        }
      }

      for (const item of frame.brushModels?.models ?? []) {
        if (!item.draw) continue
        const template = this.#active!.brushModelTemplates.get(item.model)
        if (!template) continue
        const appearance = `${item.renderMode}:${item.color[0]}:${item.color[1]}:${item.color[2]}:${item.color[3]}`
        let retained = this.#dynamicBrushInstances.get(item.sourceIndex)
        if (!retained || retained.model !== item.model || retained.appearance !== appearance) {
          if (retained) this.#disposeDynamicInstance(retained.instance)
          const instance = template.clone(true)
          const modulation = new THREE.Color(item.color[0] / 255, item.color[1] / 255, item.color[2] / 255)
          instance.traverse((object) => {
            if (!(object instanceof THREE.Mesh)) return
            const original = object.material
            const prepare = (value: THREE.Material) => {
              const material = value.clone()
              if ("color" in material && material.color instanceof THREE.Color) material.color.multiply(modulation)
              material.opacity *= item.color[3] / 255
              material.transparent = material.transparent || item.renderMode !== 0 || item.color[3] < 255
              return material
            }
            object.material = Array.isArray(original) ? original.map(prepare) : prepare(original)
            object.userData.dynamicMaterial = true
          })
          retained = { model: item.model, appearance, instance, seen: revision }
          this.#dynamicBrushInstances.set(item.sourceIndex, retained)
        }
        retained.seen = revision
        sourceTransform(retained.instance, item.worldPosition, item.worldAngles)
        retained.instance.userData.identity = item.sourceIndex
        retained.instance.userData.renderMode = item.renderMode
        retained.instance.userData.renderFx = item.renderFx
        retained.instance.userData.effects = item.effects
        retained.instance.userData.mover = item.mover
        this.#placeDynamicInstance(retained.instance, effectOrder++)
      }

      for (const item of frame.models ?? []) {
        if (item.viewModel) {
          const nextDepthRange = this.#stageViewModel(item, frame)
          if (
            viewModelDepthRange
            && (viewModelDepthRange[0] !== nextDepthRange[0] || viewModelDepthRange[1] !== nextDepthRange[1])
          ) throw new RenderingError("IdentityMismatch", "viewmodel depth ranges differ in one pass")
          viewModelDepthRange = nextDepthRange
          this.#viewModelInstances.get(item.identity)!.seen = revision
          continue
        }

        const model = modelKey(item.model, item.skin ?? 0, item.body ?? 0)
        const key = `${model}:${Number(!!item.pose)}:${Number(!!item.modelLighting)}`
        let retained = this.#dynamicModelInstances.get(item.identity)
        if (!retained || retained.model !== key) {
          if (retained) {
            retained.instance.removeFromParent()
            this.#retainedModels.retain(`world:${item.identity}:${retained.model}`, retained)
          }
          const cached = this.#retainedModels.take(`world:${item.identity}:${key}`)
          this.#instrumentation?.dynamicModel(cached ? "reused" : "created")
          const prepared = !cached && item.pose && item.modelLighting ? this.#preparedModelInstances.take(`world:${model}`)
            : !cached && !item.pose && !item.modelLighting ? this.#preparedModelInstances.take(`world-bind:${model}`) : undefined
          retained = cached ?? (prepared ? { ...prepared, model: key, seen: revision } : { model: key, instance: this.#active!.modelTemplates.get(model)!.clone(true), seen: revision })
          this.#dynamicModelInstances.set(item.identity, retained)
        }
        retained.seen = revision
        const staticInstance = this.#active!.modelOccurrenceInstances.get(item.identity)
        if (staticInstance) staticInstance.visible = false
        if (item.pose) {
          retained.meshes = this.#applyPose(retained.instance, item.pose, retained.meshes !== undefined, retained.meshes, item.renderBounds)
        }
        this.#applyDynamicModelLighting(retained, item)
        this.#applyDynamicModelCloak(retained, item)
        const coldProfile = browserFrameProfiler()
        if (coldProfile?.captureModelPrograms && !retained.instance.userData.coldModelObserved) {
          retained.instance.userData.coldModelObserved = true
          let observed = false
          const observedMeshes = retained.meshes ?? []
          if (!retained.meshes) retained.instance.traverse(object => { if (object instanceof THREE.Mesh) observedMeshes.push(object) })
          for (const mesh of observedMeshes) {
            const original = mesh.onBeforeRender
            mesh.onBeforeRender = function (...args) {
              original.apply(this, args)
              mesh.onBeforeRender = original
              if (observed) return
              observed = true
              const records = coldProfile.firstModelUses ??= []
              if (records.length < 256) records.push({ at: performance.now(), model: item.model, skin: item.skin ?? 0, identity: item.identity, pass: coldProfile.currentPass?.identity ?? null,
                materials: (retained.meshes ?? []).flatMap(mesh => (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).map(material => ({ id: material.id, identity: String(mesh.userData.materialIdentity) }))) })
            }
          }
        }
        if (item.angles) sourceTransform(retained.instance, item.position, item.angles)
        else {
          retained.instance.position.set(...item.position)
          retained.instance.quaternion.set(...item.orientation!)
        }
        retained.instance.scale.setScalar(item.scale)
        retained.instance.userData.identity = item.identity
        this.#placeDynamicInstance(retained.instance, effectOrder++)
      }
      this.#stageParticleBatches(particleItems, frame.camera, undefined, frame.sky3d?.camera)
    } catch (error) {
      if (error instanceof RenderingError) throw error
      throw new RenderingError("BoundExceeded", `render item staging failed: ${String(error)}`)
    }

    for (const [identity, retained] of this.#dynamicBrushInstances) {
      if (retained.seen === revision) continue
      this.#disposeDynamicInstance(retained.instance)
      this.#dynamicBrushInstances.delete(identity)
    }
    for (const [identity, retained] of this.#dynamicModelInstances) {
      if (retained.seen === revision) continue
      retained.instance.removeFromParent()
      this.#retainedModels.retain(`world:${identity}:${retained.model}`, retained)
      this.#instrumentation?.dynamicModel("parked")
      this.#dynamicModelInstances.delete(identity)
      const staticInstance = this.#active!.modelOccurrenceInstances.get(identity)
      if (staticInstance) staticInstance.visible = frame.modelVisibility?.get(identity) ?? true
    }
    for (const [identity, retained] of this.#viewModelInstances) {
      if (retained.seen === revision) continue
      retained.root.parent?.remove(retained.root)
      this.#retainedModels.retain(`view:${identity}:${retained.model}`, retained)
      this.#viewModelInstances.delete(identity)
    }
    // A map occurrence is not a static prop. Its Entity transform includes
    // parent motion, including invisible brush pushers. Update the existing
    // occurrence rather than leaving a second copy at the authored origin.
    for (const item of frame.studioModels ?? []) {
      const dynamic = this.#dynamicModelInstances.get(item.sourceIndex)?.instance
      const occurrence = this.#active!.modelOccurrenceInstances.get(item.sourceIndex)
      const instance = dynamic ?? occurrence
      if (!instance) continue
      if (dynamic && occurrence) dynamic.scale.copy(occurrence.scale)
      applyMapModelTransform(instance, item)
      if (dynamic && occurrence) occurrence.visible = false
    }
    if (frame.studioModels !== prior?.studioModels) {
      const live = new Set(frame.studioModels?.map(model => model.sourceIndex))
      for (const removed of prior?.studioModels ?? []) {
        if (live.has(removed.sourceIndex)) continue
        const instance = this.#active!.modelOccurrenceInstances.get(removed.sourceIndex)
        if (instance) instance.visible = false
      }
    }
    this.#stagedDynamic = Object.freeze({
      particles: frame.particles,
      models: frame.models,
      brushModels: frame.brushModels,
      studioModels: frame.studioModels,
      viewModelDepthRange,
    })
    return viewModelDepthRange
  }

  async #capture(request: FrameCaptureRequest): Promise<FrameCapture> {
    try {
      let blob: Blob
      if ("convertToBlob" in this.#canvas) {
        blob = await this.#canvas.convertToBlob({ type: request.format })
      } else {
        const canvas = this.#canvas as HTMLCanvasElement
        blob = await new Promise<Blob>((resolve, reject) =>
          canvas.toBlob(
            (value: Blob | null) => (value ? resolve(value) : reject(new Error("canvas returned no capture"))),
            request.format,
          ),
        )
      }
      const bytes = new Uint8Array(await blob.arrayBuffer())
      return Object.freeze({ format: request.format, sha256: await digest(bytes), bytes })
    } catch (error) {
      throw new RenderingError("CaptureFailed", `canvas capture failed: ${String(error)}`)
    }
  }

  resize(cssWidth: number, cssHeight: number, devicePixelRatio: number): ResizeResult {
    this.#requireReady()
    const width = Math.floor(cssWidth * devicePixelRatio)
    const height = Math.floor(cssHeight * devicePixelRatio)
    const deviceLimit = this.#backend.backend.device?.limits.maxTextureDimension2D ?? MAX_DIMENSION
    const limit = Math.min(MAX_DIMENSION, deviceLimit)
    if (
      !finite([cssWidth, cssHeight, devicePixelRatio]) ||
      cssWidth < 0 ||
      cssHeight < 0 ||
      devicePixelRatio <= 0 ||
      !Number.isSafeInteger(width) ||
      !Number.isSafeInteger(height) ||
      width > limit ||
      height > limit
    )
      throw new RenderingError("BoundExceeded", "renderer dimensions are invalid")
    if (cssWidth === this.#cssWidth && cssHeight === this.#cssHeight && devicePixelRatio === this.#devicePixelRatio) {
      return Object.freeze({ width, height, suspended: this.#suspended, deviceGeneration: this.#deviceGeneration, attachmentGeneration: this.#attachmentGeneration })
    }
    const prior = Object.freeze({
      cssWidth: this.#cssWidth,
      cssHeight: this.#cssHeight,
      devicePixelRatio: this.#devicePixelRatio,
      viewportWidth: this.#viewportWidth,
      viewportHeight: this.#viewportHeight,
      suspended: this.#suspended,
      aspect: this.#camera.aspect,
      physicalWidth: Math.floor(this.#cssWidth * this.#devicePixelRatio),
      physicalHeight: Math.floor(this.#cssHeight * this.#devicePixelRatio),
    })
    this.#framePresentation?.abandon()
    try {
      if (this.#active && width > 0 && height > 0) resizeSampledRenderTargets([this.#active.reflectionTarget, this.#active.refractionTarget], width, height, target => this.#backend.initRenderTarget(target))
      this.#backend.setPixelRatio(devicePixelRatio)
      this.#backend.setSize(cssWidth, cssHeight, false)
      if (this.#active) {
        this.#active.worldBundle.needsUpdate = true
        this.#active.skyWorldBundle.needsUpdate = true
      }
      this.#viewportWidth = cssWidth
      this.#viewportHeight = cssHeight
      this.#cssWidth = cssWidth
      this.#cssHeight = cssHeight
      this.#devicePixelRatio = devicePixelRatio
      this.#camera.aspect = cssHeight === 0 ? 1 : cssWidth / cssHeight
      this.#viewCamera.aspect = this.#camera.aspect
      this.#camera.updateProjectionMatrix()
      this.#viewCamera.updateProjectionMatrix()
      this.#suspended = width === 0 || height === 0
      this.#pacing.suspend(this.#suspended)
      this.#attachmentGeneration += 1
    } catch (error) {
      if (this.#active && prior.physicalWidth > 0 && prior.physicalHeight > 0) resizeSampledRenderTargets([this.#active.reflectionTarget, this.#active.refractionTarget], prior.physicalWidth, prior.physicalHeight, target => this.#backend.initRenderTarget(target))
      this.#backend.setPixelRatio(prior.devicePixelRatio)
      this.#backend.setSize(prior.cssWidth,prior.cssHeight,false)
      this.#viewportWidth=prior.viewportWidth;this.#viewportHeight=prior.viewportHeight;this.#cssWidth=prior.cssWidth;this.#cssHeight=prior.cssHeight;this.#devicePixelRatio=prior.devicePixelRatio;this.#suspended=prior.suspended;this.#camera.aspect=prior.aspect;this.#viewCamera.aspect=prior.aspect;this.#camera.updateProjectionMatrix();this.#viewCamera.updateProjectionMatrix()
      throw new RenderingError("GpuValidationFailed",`renderer attachment replacement failed: ${String(error)}`)
    }
    return Object.freeze({ width, height, suspended: this.#suspended, deviceGeneration: this.#deviceGeneration, attachmentGeneration:this.#attachmentGeneration })
  }

  startFramePacing(callback: FramePacingCallback): void {
    this.#requireReady()
    if (this.#pacingHandle !== undefined || this.#pacingCallback) {
      throw new RenderingError("InvalidState", "frame pacing is already active")
    }
    if (typeof globalThis.requestAnimationFrame !== "function") {
      throw new RenderingError("UnsupportedEnvironment", "animation-frame scheduling is unavailable")
    }
    this.#pacingCallback = callback
    this.#pacing.start()
    const tick = (timestamp: number) => {
      this.#pacingHandle = undefined
      if (!this.#pacingCallback || this.#lifecycle === "Disposed" || this.#lifecycle === "Failed") return
      void this.#pacing.offer(timestamp,async()=>{
        const frame=await this.#pacingCallback?.(timestamp)
        if(frame)await this.render(frame)
        const submitMilliseconds=performance.now()
        await this.#backend.backend.device?.queue.onSubmittedWorkDone()
        return Object.freeze({submitMilliseconds,presentMilliseconds:performance.now()})
      }).catch(()=>{this.#pacingCallback=undefined;this.#pacing.stop()})
      this.#pacingHandle = requestAnimationFrame(tick)
    }
    this.#pacingHandle = requestAnimationFrame(tick)
  }

  stopFramePacing(): void {
    this.#pacingCallback = undefined
    this.#pacing.stop()
    if (this.#pacingHandle !== undefined && typeof globalThis.cancelAnimationFrame === "function") {
      cancelAnimationFrame(this.#pacingHandle)
    }
    this.#pacingHandle = undefined
  }

  async #recover(): Promise<void> {
    if (this.#lifecycle === "Disposed" || this.#lifecycle === "Recovering") return
    this.#lifecycle = "Recovering"
    this.#loadOrdinal += 1
    this.stopFramePacing()
    // Pending framebuffer output belongs to this generation. Drain its queue
    // without presenting it before any target/material can be retired.
    this.#framePresentation?.abandon()
    const active = this.#active
    this.#active = undefined
    this.#clearDynamic(this.#effects)
    this.#dynamicModelInstances.clear()
    this.#dynamicBrushInstances.clear()
    this.#clearParticleBatches()
    this.#clearDynamic(this.#viewModels)
    this.#viewModelInstances.clear()
    this.#retainedModels.clear()
    for (const retained of this.#modelPanelInstances.values()) this.#disposeDynamicInstance(retained.instance)
    this.#modelPanelInstances.clear()
    this.#retainedModelPanels.clear()
    this.#preparedModelInstances.clear()
    this.#stagedDynamic = undefined
    this.#worldVisibilitySurfaces = undefined
    this.#worldVisibilityIdentity = undefined
    this.#skyWorldVisibilitySurfaces = undefined
    this.#skyWorldVisibilityIdentity = undefined
    this.#visibleStaticIndices[0].clear()
    this.#visibleStaticIndices[1].clear()
    this.#nextVisibleStaticIndices[0].clear()
    this.#nextVisibleStaticIndices[1].clear()
    this.#visibleStaticSources = [Object.freeze([]), Object.freeze([])]
    this.#visibleWorldMarkFaces.clear()
    this.#visibleProjectedMarkCount = 0
    this.#world.clear()
    const oldBackend = this.#backend
    try {
      if (active) disposeScene(active)
      try {
        oldBackend.backend.context?.unconfigure()
      } catch {
        /* device already lost */
      }
      this.#restoreOrderedBundles?.()
      this.#restoreOrderedBundles = undefined
      this.#uploadBatch?.dispose()
      this.#uploadBatch = undefined
      this.#restoreNodeBuilderInstrumentation?.()
      this.#restoreNodeBuilderInstrumentation = undefined
      this.#renderOwnerProbe?.dispose()
      this.#renderOwnerProbe = undefined
      this.#renderObjectLifetime?.restore()
      this.#renderObjectLifetime = undefined
      this.#restoreGeometryAttributeLifetime?.()
      this.#restoreGeometryAttributeLifetime = undefined
      this.#restoreTextureBindingLifetime?.()
      this.#restoreTextureBindingLifetime = undefined
      this.#restoreBufferNames?.()
      this.#restoreBufferNames = undefined
      this.#exposureSampler?.dispose()
      this.#hudMaterials?.dispose()
      this.#hudMaterials = undefined
      this.#exposureSampler = undefined
      disposeWebGpuBackend(oldBackend)
      this.#backend = await this.#createBackend()
      this.#particleVisibility.attach(this.#backend)
      this.#deviceGeneration += 1
      if (active) {
        const directional = validateDirectionalInputs(active.loadRequest.directionalTextures ?? [])
        const map = await parseRuntimeMapVerified(active.payload)
        const rebuiltMap =
          map.lighting.profile === "hdr" && active.loadRequest.lightStyles
            ? Object.freeze({ ...map, lightmap: buildRuntimeLightmap(map, active.loadRequest.lightStyles) })
            : map
        const rebuilt = this.#buildScene(
          rebuiltMap,
          active.payload,
          active.payloadSha256,
          directional,
          active.loadRequest as MapLoadRequest,
          this.#sceneGeneration + 1,
        )
        this.#world.add(rebuilt.group)
        this.#world.updateMatrixWorld(true)
        await this.#prepareReachablePipelines(rebuilt, undefined, this.#loadOrdinal)
        await this.#prepareWaterPipelines(
          rebuilt,
          active.loadRequest.environment,
          undefined,
          this.#loadOrdinal,
        )
        this.#active = rebuilt
        this.#scene.background = active.loadRequest.diagnostic ? new THREE.Color(0x111820) : null
        this.#sceneGeneration += 1
      }
      this.#lifecycle = "Ready"
    } catch {
      if (active) disposeScene(active)
      this.#lifecycle = "Failed"
    }
  }

  async #retire(scene: SceneResources): Promise<void> {
    try {
      await this.#backend.backend.device?.queue.onSubmittedWorkDone()
    } catch {
      /* loss invalidated the generation */
    }
    this.#renderObjectLifetime?.release(scene.group)
    scene.modelLightingGraphs.releaseDrawReferences()
    scene.modelPanelLightingGraphs.releaseDrawReferences()
    disposeScene(scene)
  }

  #checkAbort(signal: AbortSignal | undefined, ordinal: number): void {
    if (signal?.aborted || ordinal !== this.#loadOrdinal) {
      throw new RenderingError("InvalidState", "scene load was cancelled or replaced")
    }
  }

  #requireReady(): void {
    if (this.#lifecycle !== "Ready") {
      throw new RenderingError(
        this.#lifecycle === "Recovering" ? "DeviceLost" : "InvalidState",
        `renderer is ${this.#lifecycle}`,
      )
    }
  }

  async dispose(): Promise<void> {
    if (this.#panelAssets) {
      this.#panelAssets.disposables.dispose()
      this.#panelAssets.textureResidency.clear()
      this.#panelAssets = undefined
    }
    this.#cloakFramebuffer.dispose()
    if (this.#lifecycle === "Disposed") return
    this.#lifecycle = "Disposed"
    this.#loadOrdinal += 1
    this.stopFramePacing()
    this.#framePresentation?.abandon()
    this.#particleVisibility.dispose()
    this.#clearDynamic(this.#effects)
    this.#dynamicModelInstances.clear()
    this.#dynamicBrushInstances.clear()
    this.#clearParticleBatches()
    this.#clearDynamic(this.#viewModels)
    this.#viewModelInstances.clear()
    this.#retainedModels.clear()
    for (const retained of this.#modelPanelInstances.values()) this.#disposeDynamicInstance(retained.instance)
    this.#modelPanelInstances.clear()
    this.#retainedModelPanels.clear()
    this.#preparedModelInstances.clear()
    this.#stagedDynamic = undefined
    this.#worldVisibilitySurfaces = undefined
    this.#worldVisibilityIdentity = undefined
    this.#skyWorldVisibilitySurfaces = undefined
    this.#skyWorldVisibilityIdentity = undefined
    this.#visibleStaticIndices[0].clear()
    this.#visibleStaticIndices[1].clear()
    this.#nextVisibleStaticIndices[0].clear()
    this.#nextVisibleStaticIndices[1].clear()
    this.#visibleStaticSources = [Object.freeze([]), Object.freeze([])]
    this.#visibleWorldMarkFaces.clear()
    this.#visibleProjectedMarkCount = 0
    const active = this.#active
    this.#active = undefined
    this.#world.clear()
    if (active) await this.#retire(active)
    try {
      await this.#backend.backend.device?.queue.onSubmittedWorkDone()
    } catch {
      /* device already unavailable */
    }
    this.#restoreOrderedBundles?.()
    this.#restoreOrderedBundles = undefined
    this.#uploadBatch?.dispose()
    this.#uploadBatch = undefined
    this.#framePresentation?.dispose()
    this.#framePresentation = undefined
    this.#restoreNodeBuilderInstrumentation?.()
    this.#restoreNodeBuilderInstrumentation = undefined
    this.#renderOwnerProbe?.dispose()
    this.#renderOwnerProbe = undefined
    this.#renderObjectLifetime?.restore()
    this.#renderObjectLifetime = undefined
    this.#restoreGeometryAttributeLifetime?.()
    this.#restoreGeometryAttributeLifetime = undefined
    this.#restoreTextureBindingLifetime?.()
    this.#restoreTextureBindingLifetime = undefined
    this.#restoreBufferNames?.()
    this.#restoreBufferNames = undefined
    this.#exposureSampler?.dispose()
    this.#hudMaterials?.dispose()
    this.#hudMaterials = undefined
    this.#exposureSampler = undefined
    disposeWebGpuBackend(this.#backend)
    try {
      this.#backend.backend.context?.unconfigure()
    } catch {
      /* already unconfigured */
    }
  }
}

export async function createRenderer(request: RendererCreateRequest): Promise<Renderer> {
  const owner = await new RendererOwner(request).initialize()
  return owner
}
